import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, doc, setDoc, getDoc, getDocs, deleteDoc, arrayUnion } from 'firebase/firestore';
import { onAuthStateChanged, deleteUser } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import html2pdf from 'html2pdf.js';
import ReportEngine from '../components/ReportEngine';
import { gerarInsights } from '../components/parecerEngine';
import { db, auth, app } from '../services/firebase'; // Verifique se este caminho está correto!
import { METODO_PARECER, calculateCoreMetrics, calculateProjection, calculateHealthStatus, calculateExtendedMetrics, calculateStochasticProjection, getSectorMultiple } from '../components/financeEngine';
import { gerarParecerIA } from '../services/parecerIAService';
import { nomeArquivoRelatorio } from '../services/nomeArquivoPdf';
import { agruparPorMatriz, irmasDoMesmoGrupo, consolidarGrupo, rotuloUnidade, formatarCNPJ, extrairRaizCNPJ, ehMatriz } from '../components/filiaisEngine';
import { cancelarAssinaturaAsaas } from '../services/asaasService';
import BlocoCancelarAssinatura from '../components/BlocoCancelarAssinatura';
import AvisoCustoNaoRecorrente from '../components/AvisoCustoNaoRecorrente';
import { COMPANY_INFO } from '../constants/constants';
import CheckoutAsaasCard from '../components/CheckoutAsaasCard';

import {
  LayoutDashboard, Users, FileText, Settings, Search, Plus, Bell,
  TrendingUp, AlertTriangle, ShieldCheck, ChevronRight, Activity,
  Landmark, LogOut, BarChart3, PieChart, Palette, Type, Shield,
  Download, Eye, User, CreditCard, Lock, X, ArrowLeft, Coins,
  Target, Briefcase, FileSignature, Wand, Trash2, Sliders, Monitor, CheckCircle2, Zap, Sun, Moon, Info, WifiOff, ChevronDown, Sparkles, Crown, Building2, RefreshCw, Bot, Check
} from 'lucide-react';

// Horizontes de projeção liberados por plano do Consultor — MESMA
// progressão usada no Gestor (Tactical=3,6 / Plus=+12 / Total=+24,36),
// só que aplicada aos planos do Consultor: Advisor PRO=3,6 / Boutique
// Elite=+12 / Enterprise Ultra=+24,36. Decisão do CTO: antes o Advisor
// PRO não tinha NENHUMA projeção, o que não fazia sentido pro plano de
// entrada custar mais caro que o Tactical do Gestor e entregar menos.
const HORIZONTES_POR_PLANO_CONSULTOR = {
  advisor: [3, 6],
  boutique: [3, 6, 12],
  enterprise: [3, 6, 12, 24, 36],
};
function getHorizontesLiberados(plano) {
  return HORIZONTES_POR_PLANO_CONSULTOR[String(plano || '').toLowerCase()] || [];
}

// 🔮 Interpreta a Matriz de Sensibilidade (9 cenários de Múltiplo x WACC)
// numa leitura em texto simples — calculado na hora, sem chamada de IA
// nem custo extra. Idêntica à usada no app, de propósito (mesmo texto
// nos dois lugares pro mesmo cliente).
function interpretarSensibilidade({ apexMultiple, apexWacc, baseEbitda, debt, valorAtual, valorMax, valorMin }) {
  if (!valorMax || valorMax === 0) {
    return 'Nenhuma combinação razoável de múltiplo e WACC destrava valor nesse momento — a dívida consome todo o Enterprise Value em qualquer cenário. Reestruturação de capital é pré-requisito antes de qualquer negociação.';
  }
  const faixa = valorMax - valorMin;
  const faixaPct = valorAtual !== 0 ? faixa / valorAtual : 0;
  const zeraEmAlgumCenario = valorMin === 0;

  let leitura = '';
  if (zeraEmAlgumCenario) {
    leitura = `Nos cenários mais conservadores da matriz, o valuation zera — a estrutura de capital está no limite. `;
  } else if (faixaPct > 0.6) {
    leitura = `A variação entre o melhor e o pior cenário é grande (mais de 60%) — o valuation dessa empresa é altamente sensível a pequenas mudanças de múltiplo ou WACC. `;
  } else if (faixaPct > 0.3) {
    leitura = `Existe uma variação moderada entre os cenários — o valuation responde de forma razoável a mudanças de múltiplo e WACC, sem ser instável. `;
  } else {
    leitura = `Os 9 cenários ficam relativamente próximos — sinal de um valuation estável, pouco dependente de qual múltiplo ou WACC exato se usa na negociação. `;
  }

  const efeitoMultiplo = Math.max(0, (baseEbitda * (apexMultiple + 1) * 12) - Math.max(0, (baseEbitda * (apexMultiple - 1) * 12)));
  const efeitoWacc = Math.abs((baseEbitda * apexMultiple * 12 * (1 - ((apexWacc - 2) - 12.5) / 100)) - (baseEbitda * apexMultiple * 12 * (1 - ((apexWacc + 2) - 12.5) / 100)));
  const variavelDominante = efeitoMultiplo >= efeitoWacc ? 'o múltiplo de EBITDA negociado' : 'a taxa de WACC (custo de capital)';

  return `${leitura}Nessa empresa, ${variavelDominante} é o fator que mais move o valuation — é o ponto que mais vale a pena travar bem numa negociação de M&A.`;
}
// (negrito, títulos, etc). Copiada aqui de propósito.
function formatarParecerIA(texto) {
  if (!texto) return null;
  const linhas = texto.split('\n').filter(l => l.trim() !== '');
  const formatarNegrito = (linha) => {
    const partes = linha.split(/(\*\*[^*]+\*\*)/g);
    return partes.map((parte, i) =>
      parte.startsWith('**') && parte.endsWith('**')
        ? <strong key={i} className="text-lime-300 font-black">{parte.slice(2, -2)}</strong>
        : parte
    );
  };
  return linhas.map((linha, i) => {
    const l = linha.trim();
    if (l === '---') return <div key={i} className="h-px bg-white/10 my-6" />;
    if (l.startsWith('## ')) {
      return <h4 key={i} className="text-[11px] sm:text-xs font-black text-lime-400 uppercase italic tracking-wide mt-7 mb-2 leading-snug text-left">{l.replace(/^##\s*/, '')}</h4>;
    }
    if (l.startsWith('# ')) {
      return <h3 key={i} className="text-base sm:text-lg font-black text-white uppercase italic tracking-wide mb-3 leading-snug text-left">{l.replace(/^#\s*/, '')}</h3>;
    }
    return <p key={i} className="text-[13px] sm:text-sm text-slate-300 font-medium leading-relaxed tracking-tight text-justify mb-4">{formatarNegrito(l)}</p>;
  });
}

// ===================================================================
// 🏛️ ESTILOS GLOBAIS, AURAS E ANIMAÇÕES
// ===================================================================
const DashboardStyles = () => (
  <style dangerouslySetInnerHTML={{
    __html: `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;700;900&display=swap');
    
    .font-dashboard {
      font-family: 'Inter', sans-serif !important;
    }
    
    .custom-scroll::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    .custom-scroll::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.02);
      border-radius: 10px;
    }
    .custom-scroll::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.1);
      border-radius: 10px;
    }
    .custom-scroll::-webkit-scrollbar-thumb:hover {
      background: #bef264;
    }
    
    .no-scrollbar::-webkit-scrollbar {
      display: none;
    }

    .spark-glow {
      filter: drop-shadow(0px 0px 4px currentColor);
    }

    /* ============================================================ */
    /* MODO CLARO — sobrescreve as cores mais usadas do tema escuro   */
    /* quando a classe .theme-light está ativa no container raiz.     */
    /* Uso seletor de atributo (contém a classe) em vez de escapar    */
    /* caracteres especiais tipo [ ] # / — mais confiável.            */
    /* ============================================================ */
    .theme-light { background-color: #f8fafc !important; color: #1e293b !important; }
    .theme-light [class*="bg-[#010208]"],
    .theme-light [class*="bg-[#020617]"],
    .theme-light [class*="bg-[#1a1a1a]"],
    .theme-light [class*="from-[#"],
    .theme-light [class*="to-[#"],
    .theme-light [class*="via-[#"] { background-color: #ffffff !important; background-image: none !important; }
    .theme-light [class*="from-slate-900"],
    .theme-light [class*="to-slate-900"] { background-color: #f1f5f9 !important; background-image: none !important; }
    .theme-light [class*="bg-slate-900/"],
    .theme-light [class*="bg-black/"] { background-color: #f1f5f9 !important; }
    .theme-light [class*="bg-white/5"],
    .theme-light [class*="bg-white/10"],
    .theme-light [class*="bg-white/20"] { background-color: #e2e8f0 !important; }
    .theme-light [class*="text-white"] { color: #0f172a !important; }
    .theme-light [class*="text-slate-200"],
    .theme-light [class*="text-slate-300"] { color: #334155 !important; }
    .theme-light [class*="text-slate-400"] { color: #4b5a70 !important; }
    .theme-light [class*="text-slate-500"],
    .theme-light [class*="text-slate-600"],
    .theme-light [class*="text-slate-700"] { color: #5f6e84 !important; }
    .theme-light [class*="border-white/"] { border-color: #e2e8f0 !important; }
    .theme-light input,
    .theme-light select,
    .theme-light textarea { color: #0f172a !important; background-color: #ffffff; }
    .theme-light ::placeholder { color: #94a3b8 !important; }
    .theme-light .custom-scroll::-webkit-scrollbar-track { background: rgba(0,0,0,0.03); }
    .theme-light .custom-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); }
    .theme-light .dark-glow { display: none !important; }
    .theme-light [class*="bg-slate-800"] { background-color: #cbd5e1 !important; }

    /* ============== CORREÇÃO DE CONTRASTE: TEXTOS ROXOS NO TEMA CLARO ============== */
    /* text-purple-300/400/500 foram calibrados pra fundo escuro — no tema
       claro (fundo quase branco) ficam ilegíveis. Escurece só no modo claro,
       sem mexer no visual do tema escuro. Cobre os blocos de "Projeção por
       IA Estocástica", badges de plano e a Matriz de Sensibilidade. */
    .theme-light [class*="text-purple-300"],
    .theme-light [class*="text-purple-400"] { color: #7c3aed !important; }
    .theme-light [class*="text-purple-500"] { color: #6d28d9 !important; }
    .theme-light [class*="border-purple-500/"] { border-color: #c4b5fd !important; }
    .theme-light [class*="bg-purple-500/"],
    .theme-light [class*="bg-purple-900/"] { background-color: #ede9fe !important; }
    .theme-light [class*="from-purple-900/"] { background-color: #f5f3ff !important; background-image: none !important; }

    /* ============== CORREÇÃO DE CONTRASTE: VERDE-LIMÃO NO TEMA CLARO ============== */
    /* text-lime-400/300 foram calibrados pra brilhar num fundo escuro —
       no tema claro (fundo quase branco), esse mesmo verde-limão fica
       claro demais e difícil de ler. Escurece pra um verde mais forte só
       no modo claro, sem mexer no tema escuro. Cobre títulos, valores em
       destaque, botões e badges que usam essa cor em toda a tela. */
    .theme-light [class*="text-lime-300"],
    .theme-light [class*="text-lime-400"] { color: #4d7c0f !important; }
    .theme-light [class*="text-lime-500"] { color: #3f6212 !important; }
    .theme-light [class*="text-red-300"],
    .theme-light [class*="text-red-400"] { color: #dc2626 !important; }
    .theme-light [class*="text-green-300"],
    .theme-light [class*="text-green-400"],
    .theme-light [class*="text-emerald-300"],
    .theme-light [class*="text-emerald-400"] { color: #15803d !important; }
    .theme-light [class*="text-amber-300"],
    .theme-light [class*="text-amber-400"] { color: #b45309 !important; }
    .theme-light [class*="border-lime-400/"],
    .theme-light [class*="border-lime-500/"] { border-color: #84cc16 !important; }
    .theme-light [class*="bg-lime-400/"] { background-color: #ecfccb !important; }
    /* Botões SÓLIDOS de verde-limão (ex: "Salvar", "Assinar") continuam com
       fundo verde (senão o botão inteiro sumiria) — só o TEXTO deles precisa
       ficar escuro em vez de preto-sobre-verde-claro, senão o contraste some
       igual. O padrão já usa texto preto (text-black) nesses botões, que já
       funciona nos dois temas — por isso não precisa de regra extra aqui. */

    /* ============== CONTRASTE ABSOLUTO OLED ============== */
    /* Liga de verdade agora (antes era só um desenho fixo na tela, sem
       nenhum efeito). Troca o cinza-azulado escuro por preto puro
       #000000 — em tela OLED de verdade, pixel preto puro = pixel
       desligado, economiza bateria e aumenta contraste em jornadas
       longas. Só disponível junto com o tema escuro. */
    .oled-absoluto,
    .oled-absoluto [class*="bg-[#010208]"],
    .oled-absoluto [class*="bg-[#020617]"] { background-color: #000000 !important; }
    .oled-absoluto [class*="from-[#020617]"],
    .oled-absoluto [class*="to-[#020617]"],
    .oled-absoluto [class*="from-[#010208]"],
    .oled-absoluto [class*="to-[#010208]"] { background-color: #000000 !important; background-image: none !important; }
    .oled-absoluto [class*="border-white/5"] { border-color: rgba(255,255,255,0.08) !important; }

    /* ============== EXCEÇÃO: ÍCONES DECORATIVOS DE FUNDO ============== */
    /* Os ícones grandes e sutis no canto dos cards (Briefcase, Users,
       AlertTriangle, ShieldCheck) foram calibrados pra ficar discretos
       num fundo escuro — no tema claro, a mesma opacidade baixa contra um
       fundo quase branco fica praticamente invisível. Aumenta a opacidade
       só no tema claro pra manter o mesmo efeito "presente, mas discreto"
       nos dois temas. */
    .theme-light .phoenix-watermark-icon { opacity: 0.13 !important; }
    .theme-light .group:hover .phoenix-watermark-icon { opacity: 0.22 !important; }

    /* Exportar como PDF: usa o "Salvar como PDF" do navegador (Ctrl+P),
       escondendo o menu lateral e o cabeçalho pra sair só o relatório. */
    @media print {
      .no-print { display: none !important; }
      body, .theme-light, .min-h-screen { background: #ffffff !important; }
      * { color: #0f172a !important; box-shadow: none !important; }
    }
  `}} />
);

const gerarIdTarefa = () => `${Date.now()}${Math.random().toString(36).slice(2, 8)}`;

// 🔥 Formata número enquanto o usuário digita (pontuação de milhar em tempo
// real) — usado nos campos de Receita/Custos/Dívida do "Novo Cliente".
// Mantém vírgula pros centavos, igual ao padrão brasileiro do resto do app.
const formatarEnquantoDigita = (valor) => {
  const somenteNumeros = valor.replace(/\D/g, '');
  if (!somenteNumeros) return '';
  const numero = Number(somenteNumeros) / 100;
  return numero.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Converte "1.234.567,89" (formato BR) em número — usado pra montar o
// snapshot do histórico na hora de salvar (sem depender do parser que já
// existe dentro do listener do Firestore).
const parseValorBR = (valor) => {
  if (!valor) return 0;
  if (typeof valor === 'number') return valor;
  return Number(valor.toString().replace(/\./g, '').replace(',', '.')) || 0;
};

const formatCurrency = (value) => {
  return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// COMPONENTE SPARKLINE B2B (RESPONSIVO COM NEON)
// ==========================================
// GRÁFICO DE EVOLUÇÃO REAL — usa o histórico de verdade salvo a cada edição
// do cliente (não é mais decorativo/aleatório como as Sparklines antigas).
// ==========================================
const GraficoEvolucao = ({ historico, campo, cor, label, formatador }) => {
  if (!historico || historico.length < 2) {
    return (
      <div className="h-48 flex flex-col items-center justify-center text-center border border-dashed border-white/10 rounded-2xl">
        <TrendingUp size={24} className="text-slate-700 mb-2" />
        <p className="text-[11px] text-slate-600 font-bold uppercase tracking-widest">Ainda não há histórico suficiente</p>
        <p className="text-[11px] text-slate-700 mt-1">Aparece aqui conforme o cliente é editado ao longo do tempo</p>
      </div>
    );
  }
  const valores = historico.map(h => h[campo] || 0);
  const max = Math.max(...valores);
  const min = Math.min(...valores);
  const range = (max - min) || 1;
  const largura = 600, altura = 180, padY = 20;

  const pontos = valores.map((v, i) => {
    const x = (i / (valores.length - 1)) * largura;
    const y = altura - padY - ((v - min) / range) * (altura - padY * 2);
    return { x, y, v, data: historico[i].date };
  });
  const linhaSVG = pontos.map(p => `${p.x},${p.y}`).join(' ');
  const areaSVG = `0,${altura} ${linhaSVG} ${largura},${altura}`;

  return (
    <div>
      <svg viewBox={`0 0 ${largura} ${altura}`} className="w-full h-48 overflow-visible">
        <defs>
          <linearGradient id={`grad-${campo}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={cor} stopOpacity="0.25" />
            <stop offset="100%" stopColor={cor} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={areaSVG} fill={`url(#grad-${campo})`} />
        <polyline points={linhaSVG} fill="none" stroke={cor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {pontos.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="4" fill="#020617" stroke={cor} strokeWidth="2" />
            <title>{`${p.data}: ${formatador ? formatador(p.v) : p.v}`}</title>
          </g>
        ))}
      </svg>
      <div className="flex justify-between mt-2 text-[11px] text-slate-600 font-bold uppercase tracking-widest">
        <span>{historico[0].date}</span>
        <span>{historico[historico.length - 1].date}</span>
      </div>
    </div>
  );
};

const Sparkline = ({ data, color }) => {
  const dataMax = Math.max(...data);
  const dataMin = Math.min(...data);

  // Margem de segurança de 20%
  const padding = (dataMax - dataMin) * 0.2;
  const max = dataMax + (padding === 0 ? (dataMax * 0.1 || 1) : padding);
  const min = dataMin - (padding === 0 ? (Math.abs(dataMin * 0.1) || 0) : padding);
  const range = max - min === 0 ? 1 : max - min;

  // X vai de 0 a 100 (para representar 100% da largura)
  // Y vai de 5 a 25 (numa viewBox de 30 de altura) para sobrar espaço para o Neon não cortar
  const points = data.map((val, index) => {
    const x = (index / (data.length - 1)) * 100;
    const y = 25 - ((val - min) / range) * 20;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg
      viewBox="0 0 100 30"
      preserveAspectRatio="none"
      className="w-full h-20 overflow-visible mt-2"
    >
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="3"
        vectorEffect="non-scaling-stroke"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
        style={{ filter: `drop-shadow(0px 5px 8px ${color}90)` }}
      />
    </svg>
  );

  const fillPoints = `0,40 ${points} 140,40`;

  return (
    <div className="w-full h-16 my-4 flex items-center justify-center text-lime-400" style={{ color: color }}>
      <svg className="w-full h-full overflow-visible spark-glow" viewBox="0 0 140 40" preserveAspectRatio="none">
        <polygon fill="currentColor" fillOpacity="0.15" points={fillPoints} />
        <polyline fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" points={points} />
        <circle cx="140" cy={points.split(' ').pop().split(',')[1]} r="3" fill="currentColor" />
      </svg>
    </div>
  );
};

// Mesmos planos e preços já usados no app (vitrine em MobileInterface.jsx) — nunca
// deixar esses valores divergirem entre os dois lugares. Nome diferente
// de propósito (PLANOS_CONSULTOR_CHECKOUT) — já existe um objeto chamado
// PLANOS_CONSULTOR dentro do componente, com outro formato (usado nos
// cards de "Plano Ativo"/"Upgrade Disponível").
const PLANOS_CONSULTOR_CHECKOUT = [
  { id: 'advisor', name: 'Advisor PRO', price: '499,00', type: 'ASSINATURA MENSAL' },
  { id: 'boutique', name: 'Boutique Elite', price: '1.997,00', type: 'ASSINATURA MENSAL' },
  { id: 'enterprise', name: 'Enterprise Ultra', price: '3.997,00', type: 'ASSINATURA MENSAL' },
];

// 🔥 Converte o parecer REAL da IA (texto corrido, com "## títulos") no
// mesmo formato {t, d} que gerarInsights sempre produziu — assim ele
// entra no PDF usando exatamente o mesmo layout já pronto e testado, sem
// precisar redesenhar nenhuma página. Cai pra gerarInsights (estático)
// só quando ainda não existe parecer de IA gerado pra esse cliente.
// Idêntico ao do Dashboard Gestor, de propósito.
function parecerIAParaBlocosPdf(texto) {
  if (!texto) return null;
  const linhas = texto.split('\n').map(l => l.trim()).filter(l => l && l !== '---');
  const blocos = [];
  let atual = null;
  linhas.forEach((l) => {
    if (l.startsWith('## ')) {
      if (atual && atual.d) blocos.push(atual);
      atual = { t: l.replace(/^##\s*/, '').replace(/\*\*/g, ''), d: '' };
    } else if (l.startsWith('# ')) {
      // ignorado — o layout do PDF já tem seus próprios títulos de página.
    } else if (atual) {
      const textoLimpo = l.replace(/\*\*/g, '');
      atual.d = atual.d ? `${atual.d} ${textoLimpo}` : textoLimpo;
    }
  });
  if (atual && atual.d) blocos.push(atual);
  return blocos.length > 0 ? blocos : null;
}

export default function DashboardInterface({ onSwitchMode, onLogout, userEmail }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedClient, setSelectedClient] = useState(null);
  const [atualizandoParecerIA, setAtualizandoParecerIA] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sectorFilter, setSectorFilter] = useState('Todos');
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [viewMode, setViewMode] = useState('tabela'); // 'tabela' | 'kanban'
  const [apexOverrides, setApexOverrides] = useState({}); // { [clientId]: { multiple, wacc } }

  // ==========================================
  // 💳 CHECKOUT EMBUTIDO — mesmo componente usado no Dashboard Gestor.
  // planoSugeridoCheckout guarda qual plano vem pré-selecionado ao abrir
  // (depende de onde o clique veio).
  // ==========================================
  const [checkoutAberto, setCheckoutAberto] = useState(false);
  const [planoSugeridoCheckout, setPlanoSugeridoCheckout] = useState(null);
  const [mostrarCheckoutConfig, setMostrarCheckoutConfig] = useState(false);

  const abrirCheckout = (planoSugeridoId) => {
    setPlanoSugeridoCheckout(planoSugeridoId || null);
    setCheckoutAberto(true);
  };
  const [colunaArrastandoSobre, setColunaArrastandoSobre] = useState(null);

  const moverPipeline = async (clientId, novoStatus) => {
    // Atualiza local na hora (sensação instantânea), Firestore confirma depois
    setClients(prev => prev.map(c => c.id === clientId ? { ...c, pipelineStatus: novoStatus } : c));
    try {
      await setDoc(doc(db, 'cenarios', clientId), { pipelineStatus: novoStatus }, { merge: true });
    } catch (e) {
      console.error('[PHOENIX] Erro ao mover estágio do pipeline:', e);
    }
  };

  const COLUNAS_PIPELINE = [
    { key: 'prospeccao', label: 'Prospecção', cor: 'slate' },
    { key: 'analise', label: 'Em Análise', cor: 'blue' },
    { key: 'fechado', label: 'Fechado', cor: 'lime' },
    { key: 'perdido', label: 'Perdido', cor: 'red' }
  ];

  // ==========================================
  // ANOTAÇÕES / HISTÓRICO POR CLIENTE
  // Escuta em tempo real as notas salvas no documento do cliente selecionado
  // (coleção 'cenarios', campo 'notasConsultor').
  // ==========================================
  const [notas, setNotas] = useState([]);
  const [novaNota, setNovaNota] = useState('');
  const [salvandoNota, setSalvandoNota] = useState(false);
  const [notaEditandoIndex, setNotaEditandoIndex] = useState(null);
  const [textoEdicao, setTextoEdicao] = useState('');
  const [notaParaExcluir, setNotaParaExcluir] = useState(null); // índice aguardando confirmação

  // ==========================================
  // TAREFAS / LEMBRETES POR CLIENTE
  // Mesmo princípio das anotações: escuta em tempo real o documento do
  // cliente selecionado (coleção 'cenarios', campo 'tarefasConsultor').
  // ==========================================
  const [tarefas, setTarefas] = useState([]);
  const [novaTarefaTexto, setNovaTarefaTexto] = useState('');
  const [novaTarefaPrazo, setNovaTarefaPrazo] = useState('');
  const [salvandoTarefa, setSalvandoTarefa] = useState(false);
  const [tarefaParaExcluir, setTarefaParaExcluir] = useState(null);

  const [editandoCnpj, setEditandoCnpj] = useState(false);
  const [cnpjEditado, setCnpjEditado] = useState('');
  const salvarCnpj = async () => {
    if (!selectedClient?.id) return;
    const valor = cnpjEditado.trim();
    try {
      await setDoc(doc(db, 'cenarios', selectedClient.id), { cnpj: valor }, { merge: true });
      setSelectedClient(prev => prev ? { ...prev, cnpj: valor } : prev);
      setClients(prev => prev.map(c => c.id === selectedClient.id ? { ...c, cnpj: valor } : c));
    } catch (e) {
      console.error('[PHOENIX] Erro ao salvar CNPJ:', e);
    } finally {
      setEditandoCnpj(false);
    }
  };
  const [tarefaEditandoId, setTarefaEditandoId] = useState(null);
  const [textoEdicaoTarefa, setTextoEdicaoTarefa] = useState('');
  const [prazoEdicaoTarefa, setPrazoEdicaoTarefa] = useState('');

  useEffect(() => {
    if (!selectedClient?.id) {
      setTarefas([]);
      return;
    }
    const unsubscribe = onSnapshot(doc(db, 'cenarios', selectedClient.id), (docSnap) => {
      setTarefas(docSnap.exists() ? (docSnap.data().tarefasConsultor || []) : []);
    });
    return () => unsubscribe();
  }, [selectedClient?.id]);

  const salvarTarefas = async (listaAtualizada) => {
    if (!selectedClient?.id) return;
    try {
      await setDoc(doc(db, 'cenarios', selectedClient.id), { tarefasConsultor: listaAtualizada }, { merge: true });
    } catch (e) {
      console.error('[PHOENIX] Erro ao salvar tarefas:', e);
    }
  };

  const adicionarTarefa = async () => {
    const texto = novaTarefaTexto.trim();
    if (!texto || !selectedClient?.id) return;
    setSalvandoTarefa(true);
    const tarefa = {
      id: gerarIdTarefa(),
      texto,
      prazo: novaTarefaPrazo || null,
      concluida: false,
      criadoEm: new Date().toISOString()
    };
    await salvarTarefas([tarefa, ...tarefas]);
    setNovaTarefaTexto('');
    setNovaTarefaPrazo('');
    setSalvandoTarefa(false);
  };

  const alternarTarefaConcluida = (id) => {
    salvarTarefas(tarefas.map(t => t.id === id ? { ...t, concluida: !t.concluida } : t));
  };

  const editarTarefa = (id, novoTexto, novoPrazo) => {
    if (!novoTexto.trim()) return;
    salvarTarefas(tarefas.map(t => t.id === id ? { ...t, texto: novoTexto.trim(), prazo: novoPrazo || null } : t));
  };

  const excluirTarefa = (id) => {
    salvarTarefas(tarefas.filter(t => t.id !== id));
  };

  useEffect(() => {
    if (!selectedClient?.id) {
      setNotas([]);
      return;
    }
    const unsubscribe = onSnapshot(doc(db, 'cenarios', selectedClient.id), (docSnap) => {
      if (docSnap.exists()) {
        setNotas(docSnap.data().notasConsultor || []);
      } else {
        setNotas([]);
      }
    });
    return () => unsubscribe();
  }, [selectedClient?.id]);

  const adicionarNota = async () => {
    const texto = novaNota.trim();
    if (!texto || !selectedClient?.id) return;
    setSalvandoNota(true);
    try {
      const nota = {
        texto,
        data: new Date().toISOString(),
        autor: auth.currentUser?.email || 'Consultor'
      };
      await setDoc(doc(db, 'cenarios', selectedClient.id), {
        notasConsultor: [nota, ...notas]
      }, { merge: true });
      setNovaNota('');
    } catch (e) {
      console.error('[PHOENIX] Erro ao salvar nota:', e);
    } finally {
      setSalvandoNota(false);
    }
  };

  const excluirNota = async (indexParaExcluir) => {
    if (!selectedClient?.id) return;
    const listaAtualizada = notas.filter((_, i) => i !== indexParaExcluir);
    try {
      await setDoc(doc(db, 'cenarios', selectedClient.id), {
        notasConsultor: listaAtualizada
      }, { merge: true });
    } catch (e) {
      console.error('[PHOENIX] Erro ao excluir nota:', e);
    }
  };

  const editarNota = async (indexParaEditar, novoTexto) => {
    if (!selectedClient?.id || !novoTexto.trim()) return;
    const listaAtualizada = notas.map((n, i) =>
      i === indexParaEditar ? { ...n, texto: novoTexto.trim(), editadoEm: new Date().toISOString() } : n
    );
    try {
      await setDoc(doc(db, 'cenarios', selectedClient.id), {
        notasConsultor: listaAtualizada
      }, { merge: true });
    } catch (e) {
      console.error('[PHOENIX] Erro ao editar nota:', e);
    }
  };
  const [clearedAlerts, setClearedAlerts] = useState([]); // Guarda IDs dos alertas que o consultor já limpou/leu

  // ==========================================
  // LIMITE DE ALERTA CONFIGURÁVEL (antes era 75% fixo no código)
  // ==========================================
  const [limiteAlertaRisco, setLimiteAlertaRisco] = useState(75);

  // ==========================================
  // TEMA CLARO / ESCURO
  // Consultores de M&A vivem no Excel — muitos preferem tela clara pra
  // trabalho longo com números. Salva a preferência no navegador (rápido) e
  // no Firestore (sobrevive a troca de aparelho).
  // ==========================================
  const [tema, setTema] = useState(() => localStorage.getItem('phoenix_dashboard_theme') || 'dark');
  // 🔥 Contraste Absoluto OLED — antes era só um desenho estático (sempre
  // "ligado" na tela, sem onClick nem estado nenhum por trás). Agora liga
  // de verdade: troca o fundo #010208/#020617 (cinza-azulado escuro) por
  // preto puro #000000, que em tela OLED de verdade economiza bateria de
  // verdade (pixel preto puro = pixel desligado) e aumenta o contraste
  // pra jornadas longas de auditoria. Só faz sentido no tema escuro.
  const [contrasteOled, setContrasteOled] = useState(() => localStorage.getItem('phoenix_oled_contraste') === '1');
  const [confirmandoExclusaoConta, setConfirmandoExclusaoConta] = useState(false);
  const [excluindoConta, setExcluindoConta] = useState(false);
  useEffect(() => {
    localStorage.setItem('phoenix_oled_contraste', contrasteOled ? '1' : '0');
  }, [contrasteOled]);
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  useEffect(() => {
    const ficouOnline = () => setOnline(true);
    const ficouOffline = () => setOnline(false);
    window.addEventListener('online', ficouOnline);
    window.addEventListener('offline', ficouOffline);
    return () => {
      window.removeEventListener('online', ficouOnline);
      window.removeEventListener('offline', ficouOffline);
    };
  }, []);
  // Escala de fonte (acessibilidade) — padrão um pouco menor que o
  // tamanho original, ajustável entre 80% e 115% pelos botões A-/A+.
  const [escalaFonte, setEscalaFonte] = useState(() => {
    const salvo = localStorage.getItem('phoenix_dashboard_font_scale');
    return salvo ? parseFloat(salvo) : 0.9;
  });
  const ajustarFonte = (delta) => {
    setEscalaFonte(prev => {
      const novo = Math.min(1.15, Math.max(0.8, +(prev + delta).toFixed(2)));
      localStorage.setItem('phoenix_dashboard_font_scale', novo);
      return novo;
    });
  };
  useEffect(() => {
    localStorage.setItem('phoenix_dashboard_theme', tema);
    if (auth.currentUser) {
      setDoc(doc(db, 'usuarios', auth.currentUser.uid), { dashboardTheme: tema }, { merge: true }).catch(() => { });
    }
  }, [tema]);

  // (planoConsultorReal é declarado mais abaixo, logo após 'clients' existir)

  const PLANOS_CONSULTOR = {
    advisor: { nome: 'Advisor', preco: 'R$ 499,00/mês', cnpjs: 'até 5 CNPJs', proximo: 'boutique' },
    boutique: { nome: 'Boutique Elite', preco: 'R$ 1.997,00/mês', cnpjs: 'até 15 CNPJs', proximo: 'enterprise' },
    enterprise: { nome: 'Enterprise Ultra', preco: 'R$ 3.997,00/mês', cnpjs: 'CNPJs ilimitados', proximo: null }
  };
  const [salvandoLimite, setSalvandoLimite] = useState(false);

  // ==========================================
  // 🔥 CARREGAMENTO ÚNICO E CONFIÁVEL DE TUDO QUE DEPENDE DO USUÁRIO LOGADO
  // Antes cada dado (plano, tema, limite de alerta, marca) tinha seu próprio
  // useEffect checando "auth.currentUser" no exato instante em que a página
  // carregava — e o Firebase Auth é ASSÍNCRONO, então em boa parte das vezes
  // esse valor ainda estava vazio nesse milissegundo, e o dado nunca era
  // buscado (nem tentava de novo depois). Isso explicava o plano "não
  // sincronizado" em Configurações. Agora espera a confirmação real de login
  // via onAuthStateChanged antes de buscar qualquer coisa.
  // ==========================================
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) return;
      getDoc(doc(db, 'usuarios', user.uid)).then(snap => {
        if (!snap.exists()) return;
        const dados = snap.data();
        if (dados.dashboardTheme) setTema(dados.dashboardTheme);
        if (typeof dados.alertaLimiteRisco === 'number') setLimiteAlertaRisco(dados.alertaLimiteRisco);
        if (dados.brand) setBrand(prev => ({ ...prev, ...dados.brand }));
      }).catch(e => console.error('[PHOENIX] Erro ao carregar dados do consultor:', e));
    });
    return () => unsubscribe();
  }, []);

  const salvarLimiteAlerta = async (novoLimite) => {
    setLimiteAlertaRisco(novoLimite);
    if (!auth.currentUser) return;
    setSalvandoLimite(true);
    try {
      await setDoc(doc(db, 'usuarios', auth.currentUser.uid), { alertaLimiteRisco: novoLimite }, { merge: true });
    } catch (e) {
      console.error('[PHOENIX] Erro ao salvar limite de alerta:', e);
    } finally {
      setSalvandoLimite(false);
    }
  };

  // Uma única fonte de verdade pro cálculo de risco crítico — usada no sino,
  // na lista de alertas e no botão "Limpar". Antes o 75% estava repetido e
  // fixo em 3 lugares diferentes do código.
  const calcularAlertasAtivos = () => clients.filter(c => {
    const ebitda = c.revenue - c.costs;
    const limite = { 'Varejo': 3.5, 'Indústria': 4.2, 'Tech/SaaS': 2.1, 'Serviços': 2.5 }[c.sector] || 3.5;
    // Dívida / EBITDA anualizado (o EBITDA do cliente é mensal).
    const risco = ebitda > 0 ? ((c.debt / (ebitda * 12)) / limite) * 100 : 100;
    const isCritical = risco >= limiteAlertaRisco || ebitda <= 0;
    return isCritical && !clearedAlerts.includes(c.id);
  });

  // 🔥 Tarefas atrasadas de TODA a carteira, achatadas numa lista única com
  // o nome da empresa junto — pra alimentar o contador do sino.
  const calcularTarefasAtrasadas = () => {
    const hoje = new Date().toISOString().slice(0, 10);
    const resultado = [];
    clients.forEach(c => {
      (c.tarefas || []).forEach(t => {
        if (t.prazo && t.prazo < hoje && !t.concluida) {
          resultado.push({ ...t, clienteId: c.id, clienteNome: c.name });
        }
      });
    });
    return resultado;
  };

  const [showNotifications, setShowNotifications] = useState(false);
  const notificationsRef = useRef(null);

  // 🔥 Fecha o dropdown de notificações ao clicar em qualquer lugar fora dele
  useEffect(() => {
    if (!showNotifications) return;
    const handleClickFora = (e) => {
      if (notificationsRef.current && !notificationsRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickFora);
    return () => document.removeEventListener('mousedown', handleClickFora);
  }, [showNotifications]);
  const [logoutState, setLogoutState] = useState({ isLoggingOut: false, step: 0 });
  const [mostrandoSobre, setMostrandoSobre] = useState(false);

  const [simRevenue, setSimRevenue] = useState(0);
  const [simCosts, setSimCosts] = useState(0);

  const [brand, setBrand] = useState({
    name: 'Sua Consultoria M&A',
    color: '#bef264',
    disclaimer: 'NOTIFICAÇÃO DE CONFORMIDADE: Parecer Estratégico desenvolvido por IA de alta performance. As conclusões baseiam-se na integridade dos dados informados. Documento de uso interno, confidencial e não constitui auditoria estatutária oficial.',
    logo: null
  });

  // 🔥 Persistência real do White-Label — antes o botão "Salvar" não fazia
  // nada, era decorativo. Agora carrega/salva no Firestore de verdade
  // (o carregamento acontece no onAuthStateChanged centralizado, mais abaixo).
  const [salvandoBrand, setSalvandoBrand] = useState(false);
  // (clienteParaAmostra é declarado mais abaixo, logo após 'clients' existir)

  // ==========================================
  // EXPORTAÇÃO REAL DE PDF — usa o MESMO motor (ReportEngine.jsx) que o app
  // mobile usa pra gerar o Relatório Master e o Super Relatório 360º com o
  // layout formatado de verdade. Antes o botão tirava um "print" da tela do
  // dashboard (com menu, botões etc.) — agora gera o documento oficial,
  // igualzinho ao que sai no app, direto no computador.
  // ==========================================
  const [exportando, setExportando] = useState(false);
  const [confirmandoDownload, setConfirmandoDownload] = useState(false);
  const basicPdfRef = useRef(null);   // Relatório Master (simples)
  // 🔥 NOVO (2026-10-02): Boutique/Enterprise também podem baixar o Master resumido (como no app).
  const [tipoRelatorio, setTipoRelatorio] = useState('completo');
  const superPdfRef = useRef(null);   // Super Relatório 360º (COM white-label — só usado na aba White-Label)
  const imRef = useRef(null);         // Information Memorandum (NEUTRO, logo Phoenix — usado no Boardroom)
  const imBasicRefNaoUsado = useRef(null); // slot obrigatório do ReportEngine, não usado nesse fluxo

  // 📝 BLOCO DE NOTAS AVULSO — mesma lógica do Gestor: Tarefas e Anotações
  // não entram no relatório oficial (fotografia do momento), então saem
  // como documento de trabalho separado, atualizável a qualquer momento.
  const [exportandoNotas, setExportandoNotas] = useState(false);
  const blocoNotasRef = useRef(null);

  const exportarBlocoDeNotas = () => {
    if (!selectedClient || !blocoNotasRef.current) return;
    setExportandoNotas(true);
    const opt = {
      margin: 0,
      filename: `Phoenix_Bloco_Notas_${selectedClient.name.replace(/\s+/g, '_')}.pdf`,
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 2, useCORS: true, logging: false, letterRendering: true, scrollX: 0, scrollY: 0 },
      jsPDF: { unit: 'px', format: [800, 1131], orientation: 'portrait' }
    };
    html2pdf().set(opt).from(blocoNotasRef.current).save().then(() => setExportandoNotas(false)).catch((e) => {
      console.error('[PHOENIX] Erro ao exportar bloco de notas:', e);
      setExportandoNotas(false);
    });
  };

  const exportarPDF = (ref, nomeArquivo) => {
    if (!ref.current) return;
    setExportando(true);
    const opt = {
      margin: 0,
      filename: nomeArquivo,
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 2, useCORS: true, logging: false, letterRendering: true, scrollX: 0, scrollY: 0 },
      jsPDF: { unit: 'px', format: [800, 1131], orientation: 'portrait' }
    };
    html2pdf().set(opt).from(ref.current).save().then(() => setExportando(false)).catch((e) => {
      console.error('[PHOENIX] Erro ao exportar PDF:', e);
      setExportando(false);
    });
  };

  // Monta o objeto de dados no formato que o ReportEngine espera, a partir
  // da empresa selecionada + métricas já calculadas no Dashboard.
  const montarDadosParaPDF = (client, m) => {
    if (!client || !m) return null;
    return {
      companyName: client.name,
      segment: (client.niche && client.niche !== '-') ? `${client.sector} (${client.niche})` : client.sector,
      ebitda: m.ebitda,
      margin: m.currentMargin,
      alavancagem: typeof m.alavancagem === 'number' ? m.alavancagem.toFixed(2).replace('.', ',') : String(m.alavancagem ?? '0,00'),
      valuation: formatCurrency(m.enterpriseValue),
      equity: formatCurrency(m.equityValue),
      // 🔥 CORREÇÃO: era hardcoded como '0' — o M&A Readiness Score no
      // relatório do Consultor nunca refletia a saúde real da empresa,
      // sempre mostrava 0/100 (ou o que quer que o PDF exibisse por engano).
      score: String(m.maScore ?? '0'),
      projectedEbitda: formatCurrency(m.projEbitda ?? m.ebitdaAjustado),
      simRevenue: simRevenue,
      simCosts: simCosts,
      ruptureRisk: m.ruptureRisk,
      sector: client.sector,
      debt: client.debt,
      custoNaoRecorrente: client.custoNaoRecorrente,
      custoDivida: client.custoDivida,
      ebitdaAjustado: m.ebitdaAjustado,
      pontoEquilibrio: client.costs ?? null,
      caixaMinimo: (Number(client.costs) || 0) + (Number(client.proLabore) || 0),
      ncg: m.ncg ?? 0,
      // 🔥 CORRIGIDO (2026-10-02): o PDF do Consultor nunca recebia estes dois e
      // mostrava "Limites de saque: R$ 0,00" mesmo com a IA citando o valor real.
      safeDividends: formatCurrency(m.safeDividends),
      emergencyReserve: formatCurrency(m.emergencyReserve),
      dividaCurtoPrazo: client.dividaCurtoPrazo,
      prazoRecebimento: client.prazoRecebimento,
      prazoPagamento: client.prazoPagamento,
      tarefas, notas
    };
  };
  const salvarBrand = async () => {
    if (!auth.currentUser) return;
    setSalvandoBrand(true);
    try {
      await setDoc(doc(db, 'usuarios', auth.currentUser.uid), { brand }, { merge: true });
    } catch (e) {
      console.error('[PHOENIX] Erro ao salvar White-Label:', e);
    } finally {
      setSalvandoBrand(false);
    }
  };

  const brandColors = [
    { name: 'Limão Phoenix', hex: '#bef264' },
    { name: 'Azul Corporate', hex: '#3b82f6' },
    { name: 'Dourado Wealth', hex: '#eab308' },
    { name: 'Roxo Boutique', hex: '#a855f7' },
    { name: 'Vermelho Alerta', hex: '#ef4444' },
    { name: 'Cinza Executivo', hex: '#94a3b8' }
  ];

  // Começamos com uma lista vazia de clientes
  const [clients, setClients] = useState([]);
  // 🔥 Sem isso, entre o login e a chegada dos dados do Firestore a tela
  // mostrava "0 Ativas / R$ 0,00" — ou seja, um Consultor com carteira
  // cheia via, por um instante, um painel que parecia zerado. Agora dá
  // pra distinguir "ainda carregando" de "realmente não tem cliente".
  const [carregandoClientes, setCarregandoClientes] = useState(true);

  const [clienteParaAmostra, setClienteParaAmostra] = useState(null); // empresa escolhida pro PDF 360º no White-Label
  useEffect(() => {
    if (!clienteParaAmostra && clients.length > 0) setClienteParaAmostra(clients[0]);
  }, [clients]);

  // ==========================================
  // CADASTRO DE NOVO CLIENTE DIRETO PELO DASHBOARD
  // Escreve no MESMO formato que o app mobile já usa (coleção 'cenarios',
  // snapshot.formData com userType 'Consultor') — por isso a sincronização
  // funciona nos dois sentidos: o app já tem um botão "Atualizar da Nuvem"
  // que busca tudo que ainda não conhece, então um cliente cadastrado aqui
  // aparece lá sem precisar de nenhum código novo no app.
  // ==========================================
  const [modalNovoCliente, setModalNovoCliente] = useState(false);

  // ==========================================
  // COMPARAÇÃO LADO A LADO ENTRE EMPRESAS
  // ==========================================
  const [modoComparacao, setModoComparacao] = useState(false);

  // ==========================================
  // BUSCA GLOBAL / COMMAND PALETTE (Ctrl+K ou Cmd+K)
  // ==========================================
  const [buscaGlobalAberta, setBuscaGlobalAberta] = useState(false);
  const [termoBuscaGlobal, setTermoBuscaGlobal] = useState('');
  const [indiceSelecionado, setIndiceSelecionado] = useState(0);
  const inputBuscaGlobalRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setBuscaGlobalAberta(true);
        setTermoBuscaGlobal('');
        setIndiceSelecionado(0);
      }
      if (e.key === 'Escape') setBuscaGlobalAberta(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (buscaGlobalAberta) setTimeout(() => inputBuscaGlobalRef.current?.focus(), 50);
  }, [buscaGlobalAberta]);
  const [empresasParaComparar, setEmpresasParaComparar] = useState([]); // array de ids
  const [modalComparacao, setModalComparacao] = useState(false);

  const alternarSelecaoComparacao = (id) => {
    setEmpresasParaComparar(prev => prev.includes(id) ? prev.filter(x => x !== id) : (prev.length >= 4 ? prev : [...prev, id]));
  };

  // ==========================================
  // EXPORTAR CARTEIRA EM CSV (abre direto no Excel)
  // Respeita os filtros ativos (busca, setor, plano) — exporta o que
  // está sendo mostrado na tabela, não a carteira inteira sempre.
  // ==========================================
  const exportarCarteiraCSV = (listaFiltrada) => {
    const colunas = ['Empresa', 'CNPJ', 'Setor', 'Nicho', 'EBITDA Ajustado', 'Margem Operacional (%)', 'Alavancagem', 'Múltiplo Setorial', 'Equity Value', 'Risco de Ruptura (%)', 'Status', 'Plano'];
    // 🔥 Ponto e vírgula, não vírgula — o Excel em português (pt-BR) usa vírgula
    // como separador DECIMAL, então espera ; para separar colunas. Com vírgula
    // como delimitador, o Excel jogava tudo numa coluna só.
    const SEP = ';';
    // 🔥 Números também precisam sair com VÍRGULA decimal (não ponto) — senão o
    // Excel em português trata como texto, e fórmulas tipo SOMA não funcionam.
    const numeroBR = (v) => Number(v || 0).toString().replace('.', ',');
    const linhasCSV = listaFiltrada.map(client => {
      const m = calculateClientMetrics(client);
      const saude = calculateHealthStatus({ ebitda: m.ebitdaAjustado, alavancagem: m.alavancagem, deb: client.debt, rev: client.revenue, sector: client.sector });
      // 🔥 Escapa aspas pra não quebrar as colunas do CSV
      const escapar = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      return [
        escapar(client.name), escapar(client.cnpj), escapar(client.sector), escapar(client.niche === '-' ? '' : client.niche),
        numeroBR(m.ebitdaAjustado), numeroBR(m.currentMargin), numeroBR(m.alavancagem), numeroBR(m.multiple), numeroBR(m.equityValue),
        numeroBR(m.ruptureRisk.toFixed(1)), escapar(saude.label), escapar(client.plan)
      ].join(SEP);
    });
    // 🔥 BOM (\uFEFF) no início — sem isso o Excel abre acentos quebrados
    const csv = '\uFEFF' + [colunas.map(c => `"${c}"`).join(SEP), ...linhasCSV].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Phoenix_Carteira_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const [clienteEditandoId, setClienteEditandoId] = useState(null); // null = cadastro novo, id = editando existente

  const abrirEdicaoCliente = (client) => {
    setClienteEditandoId(client.id);
    // 🔥 Usa != null em vez de truthy — um valor 0 (zero) é um dado válido e
    // real (ex: dívida zerada), não "campo vazio". Antes isso virava string
    // vazia, e como a validação exige todos os campos preenchidos, editar
    // um cliente que tinha algum valor zerado travava o salvamento.
    const paraTexto = (v, comFormatoMonetario) => {
      if (v === undefined || v === null || v === '') return '';
      return comFormatoMonetario ? Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : String(v);
    };
    setNovoCliente({
      companyName: client.name || '',
      cnpj: client.cnpj === '00.000.000/0000-00' ? '' : (client.cnpj || ''),
      segment: client.sector || 'Varejo',
      niche: client.niche === '-' ? '' : (client.niche || ''),
      revenue: paraTexto(client.revenue, true),
      costs: paraTexto(client.costs, true),
      debt: paraTexto(client.debt, true),
      custoNaoRecorrente: paraTexto(client.custoNaoRecorrente, true),
      custoDivida: paraTexto(client.custoDivida, false),
      prazoRecebimento: paraTexto(client.prazoRecebimento, false),
      prazoPagamento: paraTexto(client.prazoPagamento, false),
      dividaCurtoPrazo: paraTexto(client.dividaCurtoPrazo, true),
      proLabore: paraTexto(client.proLabore, true)
    });
    setModalNovoCliente(true);
  };
  const [salvandoNovoCliente, setSalvandoNovoCliente] = useState(false);
  const [progressoCadastro, setProgressoCadastro] = useState(0);
  const [novoCliente, setNovoCliente] = useState({
    companyName: '', cnpj: '', segment: 'Varejo', niche: '',
    revenue: '', costs: '', debt: '', custoNaoRecorrente: '', custoDivida: '',
    prazoRecebimento: '', prazoPagamento: '', dividaCurtoPrazo: '', proLabore: ''
  });
  const setoresDisponiveis = ['Varejo', 'Alimentação', 'Indústria', 'Tech/SaaS', 'Serviços', 'Saúde/Clínicas', 'Logística/Frota', 'Holding / Grupo', 'Atacado/Distrib.', 'Agronegócio', 'Construção/Eng.'];

  // Regera o parecer da IA depois de editar os dados de um cliente. Mesma
  // trava do app/Gestor: se a "impressão digital" dos dados não mudou, não
  // chama a IA (é pago por uso).
  const _regerarParecerAposEdicao = async (dossieId, fd) => {
    try {
      const revNum = parseValorBR(fd.revenue);
      const costsNum = parseValorBR(fd.costs);
      const debtNum = parseValorBR(fd.debt);
      const cnrNum = parseValorBR(fd.custoNaoRecorrente);
      const cdNum = parseValorBR(fd.custoDivida);
      const cpNum = parseValorBR(fd.dividaCurtoPrazo);
      const plNum = parseValorBR(fd.proLabore);
      const prazoRec = Number(fd.prazoRecebimento) || 0;
      const prazoPag = Number(fd.prazoPagamento) || 0;

      const core = calculateCoreMetrics(revNum, costsNum, debtNum, fd.segment, { custoNaoRecorrente: cnrNum, custoDivida: cdNum });
      const health = calculateHealthStatus({ ebitda: core.ebitdaAjustado, alavancagem: core.alavancagem, deb: debtNum, rev: revNum, sector: fd.segment });
      const ext = calculateExtendedMetrics({
 sector: fd.segment,
        rev: revNum, cos: costsNum, ebitda: core.ebitdaAjustado, alavancagem: core.alavancagem, currentMargin: core.currentMargin,
        prazoRecebimento: prazoRec, prazoPagamento: prazoPag, dividaCurtoPrazo: cpNum, proLabore: plNum
      });
      const projecao = calculateStochasticProjection({ ebitda: core.ebitda, ebitdaAjustado: core.ebitdaAjustado, currentMargin: core.currentMargin }, revNum);
      const horizontesLiberados = getHorizontesLiberados(planoConsultorReal);
      const blocosProjecao = [];
      if (horizontesLiberados.includes(3) || horizontesLiberados.includes(6)) blocosProjecao.push('curto');
      if (horizontesLiberados.includes(12)) blocosProjecao.push('medio');
      if (horizontesLiberados.includes(24) || horizontesLiberados.includes(36)) blocosProjecao.push('longo');

      const retratoAtual = JSON.stringify({
        rev: revNum, cos: costsNum, deb: debtNum, segment: fd.segment,
        custoNaoRecorrente: cnrNum, custoDivida: cdNum,
        prazoRecebimento: prazoRec, prazoPagamento: prazoPag,
        dividaCurtoPrazo: cpNum, proLabore: plNum, horizontesLiberados,
        metodo: METODO_PARECER
      });
      const docAtual = await getDoc(doc(db, 'cenarios', String(dossieId)));
      if (docAtual.exists() && docAtual.data().parecerIAInputSnapshot === retratoAtual) return;

      await gerarParecerIA({
        dossieId: String(dossieId),
        tipoUsuario: 'Consultor',
        nicho: ((fd.niche && fd.niche !== '-') ? `${fd.niche} (setor: ${fd.segment || 'não informado'})` : (fd.segment || 'negócio geral')),
        metricas: {
          receita: revNum, custos: costsNum, divida: debtNum,
          ebitda: core.ebitda, ebitdaAjustado: core.ebitdaAjustado,
          currentMargin: core.currentMargin, alavancagem: core.alavancagem,
          coberturaJuros: core.coberturaJuros, ruptureRisk: core.ruptureRisk,
          statusSaude: health.label,
          ncg: ext.ncg, reservaEmergencia: ext.emergencyReserve,
          dividendosSeguros: ext.safeDividends, scoreMA: ext.maScore,
          coberturaCurtoPrazo: ext.coberturaCurtoPrazo, caixaMinimo: ext.caixaMinimo
        },
        projecao,
        inputSnapshot: retratoAtual,
        blocosProjecao,
        incluirOportunidades: String(planoConsultorReal || '').toLowerCase() === 'enterprise'
      });
    } catch (e) {
      console.error('[PHOENIX] Erro ao regerar parecer da IA após edição (Consultor):', e);
    }
  };

  // 🔒 FILA (2026-10-05): duas edições seguidas disparavam duas gerações ao mesmo tempo, e a mais
  // ANTIGA podia terminar por último e sobrescrever o parecer novo (PDF saía com texto dos dados
  // velhos). Agora as gerações de cada cliente rodam uma por vez, em ordem; "atualizando" só apaga quando
  // a última acaba (e o botão de exportar PDF fica travado até lá).
  const filaRegeracaoRef = useRef(Promise.resolve());
  const regeracoesPendentesRef = useRef(0);
  const regerarParecerAposEdicao = (...args) => {
    regeracoesPendentesRef.current += 1;
    setAtualizandoParecerIA(true);
    const p = filaRegeracaoRef.current.then(() => _regerarParecerAposEdicao(...args));
    filaRegeracaoRef.current = p.catch(() => {});
    return p.finally(() => {
      regeracoesPendentesRef.current -= 1;
      if (regeracoesPendentesRef.current <= 0) { regeracoesPendentesRef.current = 0; setAtualizandoParecerIA(false); }
    });
  };

  const salvarNovoCliente = async () => {
    // 🔥 Cadastro novo exige tudo (mesma regra do app). Edição só exige o
    // nome — um cliente antigo pode legitimamente não ter todos os campos
    // avançados preenchidos ainda, e a pessoa só quer corrigir um dado.
    const obrigatorios = clienteEditandoId ? ['companyName'] : ['companyName', 'niche', 'revenue', 'costs', 'debt', 'custoNaoRecorrente', 'custoDivida', 'prazoRecebimento', 'prazoPagamento', 'dividaCurtoPrazo', 'proLabore'];
    if (!auth.currentUser || obrigatorios.some(campo => !String(novoCliente[campo]).trim())) return;
    setSalvandoNovoCliente(true);
    setProgressoCadastro(0);
    // 🔥 Mesmo efeito visual do "scanning" do app (círculo de progresso) —
    // aqui é só cosmético/tranquilizador durante o salvamento real, sem
    // fingir etapas de análise que não existem nesse fluxo.
    const intervalo = setInterval(() => {
      setProgressoCadastro(p => (p >= 90 ? p : p + 10));
    }, 320);
    const inicioSalvamento = Date.now();
    try {
      const id = clienteEditandoId || `${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
      const formDataCliente = {
        companyName: novoCliente.companyName.trim(),
        cnpj: novoCliente.cnpj.trim() || '00.000.000/0000-00',
        segment: novoCliente.segment,
        niche: novoCliente.niche.trim(),
        revenue: novoCliente.revenue,
        costs: novoCliente.costs,
        debt: novoCliente.debt,
        custoNaoRecorrente: novoCliente.custoNaoRecorrente,
        custoDivida: novoCliente.custoDivida,
        prazoRecebimento: novoCliente.prazoRecebimento,
        prazoPagamento: novoCliente.prazoPagamento,
        dividaCurtoPrazo: novoCliente.dividaCurtoPrazo,
        proLabore: novoCliente.proLabore,
        expertise: 'Avançado',
        userType: 'Consultor'
      };
      const dadosParaSalvar = {
        name: formDataCliente.companyName,
        plan: String(planoConsultorReal || 'advisor').toUpperCase(),
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        userType: 'Consultor',
        snapshot: { formData: formDataCliente, userType: 'Consultor' },
        timestamp: new Date().toISOString(),
        // 🔥 CORRIGIDO (2026-10-02): o painel LÊ os campos soltos do documento
        // antes do snapshot (dados.revenue || origem.revenue). Dossiês criados
        // pelo app gravam os valores soltos, então editar só o snapshot não
        // mudava nada na tela. Grava nos dois lugares pra edição valer.
        companyName: formDataCliente.companyName,
        cnpj: formDataCliente.cnpj,
        segment: formDataCliente.segment,
        niche: formDataCliente.niche,
        revenue: formDataCliente.revenue,
        costs: formDataCliente.costs,
        debt: formDataCliente.debt,
        custoNaoRecorrente: formDataCliente.custoNaoRecorrente,
        custoDivida: formDataCliente.custoDivida,
        prazoRecebimento: formDataCliente.prazoRecebimento,
        prazoPagamento: formDataCliente.prazoPagamento,
        dividaCurtoPrazo: formDataCliente.dividaCurtoPrazo,
        proLabore: formDataCliente.proLabore
      };
      // 🔥 GRÁFICO DE EVOLUÇÃO REAL: guarda um retrato dos números atuais
      // toda vez que o cliente é salvo/editado — é isso que alimenta o
      // gráfico de verdade no Diagnóstico Executivo, não mais um enfeite.
      // 🔥 Isolado em seu próprio try/catch: se esse cálculo falhar por
      // qualquer motivo, o cadastro/edição do cliente continua salvando
      // normalmente — antes um erro aqui travava TUDO silenciosamente.
      try {
        const revNum = parseValorBR(novoCliente.revenue);
        const costsNum = parseValorBR(novoCliente.costs);
        const debtNum = parseValorBR(novoCliente.debt);
        const coreSnapshot = calculateCoreMetrics(revNum, costsNum, debtNum, novoCliente.segment, {
          custoNaoRecorrente: parseValorBR(novoCliente.custoNaoRecorrente),
          custoDivida: parseValorBR(novoCliente.custoDivida)
        });
        dadosParaSalvar.historico = arrayUnion({
          date: new Date().toLocaleDateString('pt-BR'),
          revenue: revNum, costs: costsNum, debt: debtNum,
          ebitda: coreSnapshot.ebitdaAjustado ?? 0,
          equityValue: coreSnapshot.equityValue ?? 0,
          alavancagem: typeof coreSnapshot.alavancagem === 'number' ? coreSnapshot.alavancagem : 0,
          currentMargin: coreSnapshot.currentMargin ?? 0
        });
      } catch (erroHistorico) {
        console.error('[PHOENIX] Não foi possível salvar o retrato do histórico (cliente será salvo mesmo assim):', erroHistorico);
      }
      // 🔥 Só grava data de criação/exibição em cadastro NOVO — editar um
      // cliente existente não deve "voltar no tempo" a data dele.
      if (!clienteEditandoId) {
        dadosParaSalvar.date = new Date().toLocaleDateString('pt-BR');
        dadosParaSalvar.dataCriacao = new Date().toISOString();
      }
      await setDoc(doc(db, 'cenarios', id), dadosParaSalvar, { merge: true });
      // 🔥 CORRIGIDO (2026-10-02): editar um cliente deixava o parecer da IA
      // com os números ANTIGOS (o Gestor já regerava; o Consultor não). Roda
      // em segundo plano — não segura o fechamento do modal.
      // Cliente NOVO também gera o parecer na hora (antes só a edição chamava a IA).
      regerarParecerAposEdicao(id, formDataCliente);
      clearInterval(intervalo);
      // 🔥 Garante pelo menos ~2s de animação, mesmo se o Firestore responder
      // na hora — senão o círculo mal aparece antes de já ter sumido.
      const tempoDecorrido = Date.now() - inicioSalvamento;
      if (tempoDecorrido < 2000) await new Promise(resolve => setTimeout(resolve, 2000 - tempoDecorrido));
      setProgressoCadastro(100);
      // 🔥 Segura mais um instante no 100% pra dar a sensação de conclusão.
      await new Promise(resolve => setTimeout(resolve, 700));
      setModalNovoCliente(false);
      setClienteEditandoId(null);
      setNovoCliente({ companyName: '', cnpj: '', segment: 'Varejo', niche: '', revenue: '', costs: '', debt: '', custoNaoRecorrente: '', custoDivida: '', prazoRecebimento: '', prazoPagamento: '', dividaCurtoPrazo: '', proLabore: '' });
    } catch (e) {
      clearInterval(intervalo);
      console.error('[PHOENIX] Erro ao cadastrar novo cliente:', e);
      alert('Não foi possível salvar. Verifica sua conexão e tenta de novo. Detalhe técnico: ' + (e?.message || 'erro desconhecido'));
    } finally {
      setSalvandoNovoCliente(false);
      setProgressoCadastro(0);
    }
  };

  // 🔥 CAUSA RAIZ do "comprei no Dashboard mas não aparece em lugar
  // nenhum": o webhookAsaas grava a compra em 'usuarios/{uid}.consultorPlan'
  // (a fonte real e imediata da compra) — mas até agora o Dashboard IGNORAVA
  // esse campo por completo (só olhava o plano espelhado dentro de cada
  // dossiê em 'cenarios', que só quem atualiza é o APP, ao abrir). Resultado:
  // comprando pelo Dashboard, nada em lugar nenhum sabia disso até a pessoa
  // abrir o app de novo (e mesmo aí, um bug separado no app — corrigido
  // junto nesta sessão — podia reverter a compra de volta pro plano antigo).
  // Agora o Dashboard escuta 'usuarios/{uid}' ao vivo, e sempre que esse
  // campo mostrar um plano MAIOR do que o refletido nos dossiês, ele (1)
  // já usa esse valor mais alto pra liberar a tela na hora, sem esperar o
  // app abrir, e (2) empurra a atualização pra dentro dos dossiês, pra o
  // app também já achar tudo certo da próxima vez que abrir.
  const [planoUsuarioDoc, setPlanoUsuarioDoc] = useState(null);
  const [pagamentoAtrasado, setPagamentoAtrasado] = useState(null); // { tipo, desde } — vem direto do Firestore, só leitura
  const [acessoSuspensoPorAtraso, setAcessoSuspensoPorAtraso] = useState(null); // { tipo } — idem, só depois das 48h
  const [temGestorAtivoTambem, setTemGestorAtivoTambem] = useState(false); // só pra decidir se mostra "(Consultor)" no aviso ou não
  // 🔥 Precisa saber se a leitura do plano JÁ terminou (mesmo que o
  // resultado seja "sem plano nenhum") pra poder bloquear o Dashboard com
  // segurança — sem isso, não dá pra diferenciar "ainda carregando" de
  // "confirmado que essa conta não tem assinatura Consultor".
  const [planoCarregado, setPlanoCarregado] = useState(false);
  // 🔥 Guarda o valor anterior do plano, pra saber se ele MUDOU agora (ou
  // seja: se o webhook acabou de confirmar uma assinatura nova) — sem
  // isso não dá pra distinguir "acabei de assinar" de "só abri o
  // Dashboard e já tinha esse plano".
  const planoAnteriorConsultorRef = useRef({ valor: null, inicializado: false });
  const [avisoAssinaturaConfirmada, setAvisoAssinaturaConfirmada] = useState(null);

  // 🤖 TEASER "AGENTES DE IA" (Em Breve) — mesmo princípio do app: sem
  // funcionalidade real, só antecipa a feature e mede interesse com um
  // clique só (sem e-mail, sem conta separada). "jaRegistrouInteresseAgentes"
  // é local (por aparelho/navegador) — não precisa de mais que isso.
  const [contadorInteresseAgentes, setContadorInteresseAgentes] = useState(null);
  const [jaRegistrouInteresseAgentes, setJaRegistrouInteresseAgentes] = useState(
    () => localStorage.getItem('phoenix_interesse_agentes_ia') === '1'
  );
  const [registrandoInteresseAgentes, setRegistrandoInteresseAgentes] = useState(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'metricas', 'interesseAgentesIA'), (snap) => {
      if (snap.exists()) setContadorInteresseAgentes(snap.data());
    }, () => { });
    return () => unsubscribe();
  }, []);

  const registrarInteresseAgentesIA = async () => {
    if (jaRegistrouInteresseAgentes || registrandoInteresseAgentes) return;
    setRegistrandoInteresseAgentes(true);
    try {
      const functionsInstance = getFunctions(app, 'southamerica-east1');
      const chamar = httpsCallable(functionsInstance, 'registrarInteresseAgentesIA');
      await chamar({ perfil: 'consultor' });
      localStorage.setItem('phoenix_interesse_agentes_ia', '1');
      setJaRegistrouInteresseAgentes(true);
    } catch (e) {
      console.error('[PHOENIX] Erro ao registrar interesse em Agentes de IA:', e);
    } finally {
      setRegistrandoInteresseAgentes(false);
    }
  };
  useEffect(() => {
    if (!auth.currentUser) return;
    const unsubscribe = onSnapshot(doc(db, 'usuarios', auth.currentUser.uid), (snap) => {
      const novoPlano = snap.exists() ? (String(snap.data().consultorPlan || '').toLowerCase() || null) : null;
      setPlanoUsuarioDoc(novoPlano);
      setCancelamentoConsultorDoc(snap.exists() ? (snap.data().cancelamentoConsultor || null) : null);
      setPlanoCarregado(true);
      setPagamentoAtrasado(snap.exists() ? (snap.data().pagamentoAtrasado || null) : null);
      setAcessoSuspensoPorAtraso(snap.exists() ? (snap.data().acessoSuspensoPorAtraso || null) : null);
      setTemGestorAtivoTambem(snap.exists() ? ['tactical', 'plus', 'total'].includes(String(snap.data().plan || snap.data().gestorPlan || '').toLowerCase()) : false);

      const anterior = planoAnteriorConsultorRef.current;
      if (anterior.inicializado && novoPlano && novoPlano !== anterior.valor) {
        const nomesPlano = { advisor: 'Advisor PRO', boutique: 'Boutique Elite', enterprise: 'Enterprise Ultra' };
        setAvisoAssinaturaConfirmada(nomesPlano[novoPlano] || novoPlano);
      }
      planoAnteriorConsultorRef.current = { valor: novoPlano, inicializado: true };
    }, () => setPlanoCarregado(true)); // não trava pra sempre se a leitura falhar
    return () => unsubscribe();
  }, [auth.currentUser?.uid]);

  const NIVEL_PLANO_CONSULTOR = { advisor: 2, boutique: 3, enterprise: 4 };
  const [cancelamentoConsultorDoc, setCancelamentoConsultorDoc] = useState(null); // { acessoAte } — cancelamento agendado, só leitura

  // 🔒 SEGURANÇA — MUDANÇA IMPORTANTE:
  // Antes o plano vinha do carimbo gravado dentro do DOSSIÊ (coleção
  // 'cenarios') e, se fosse maior, de 'usuarios/{uid}'. O problema: o
  // usuário pode editar os próprios dossiês (é dono deles), então bastava
  // abrir o console do navegador e gravar plan:'ENTERPRISE' num dossiê pra
  // destravar recursos de R$ 3.997/mês sem pagar.
  // Agora o plano vem EXCLUSIVAMENTE de 'usuarios/{uid}.consultorPlan',
  // que passou a ser gravável só pelo servidor (webhookAsaas, depois do
  // pagamento confirmado). O campo 'plan' do dossiê continua existindo,
  // mas serve só como rótulo histórico — não decide mais acesso a nada.
  const planoConsultorReal = planoUsuarioDoc || null;

  // 🔒 REMOVIDO: existia aqui uma rotina que "empurrava" o plano pra dentro
  // dos dossiês. Não é mais necessária, já que o dossiê não decide acesso —
  // e ela também seria rejeitada pelas novas regras do Firestore.


  // Este "useEffect" é o nosso radar: fica escutando o Firebase em tempo real
  useEffect(() => {
    if (!auth.currentUser) return;

    // 🔥 Só os dossiês DESTE consultor (não de todo mundo) e SÓ os de perfil
    // Consultor (dossiês do Gestor não devem aparecer aqui — mesma regra
    // espelhada que já existe no GestorDashboardInterface, ao contrário).
    const q = query(collection(db, "cenarios"), where("userId", "==", auth.currentUser.uid));
    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const clientesReais = snapshot.docs.map((doc) => {
          const dados = doc.data();

          const userType = String(dados.userType || dados.snapshot?.userType || 'Gestor').toLowerCase();
          if (userType !== 'consultor') return null; // Dossiês de Gestor não aparecem aqui

          // 🔥 Os dados podem estar "soltos" no documento OU dentro de
          // snapshot.formData — depende de qual tela gerou a sincronização.
          // Lê dos dois lugares, com prioridade pro nível solto.
          const origem = dados.snapshot?.formData || {};

          // 1. TRATAMENTO DE NÚMEROS BRASILEIROS: Remove pontos de milhar e ajusta decimais
          const higienizarNumero = (valor) => {
            if (!valor) return 0;
            if (typeof valor === 'number') return valor;
            const valorLimpo = valor.toString().replace(/\./g, '').replace(',', '.');
            return Number(valorLimpo) || 0;
          };

          // 2. NOMENCLATURA DO PLANO — Consultor e Gestor têm planos DIFERENTES,
          // não devem ser "traduzidos" um pro outro (antes Enterprise virava
          // "Total" e Boutique virava "Plus", nomes que nem existem no Consultor).
          // 🔥 IMPORTANTE: dados.plan vem primeiro na prioridade — é o campo que
          // realmente representa o plano ativo. dados.expertise/origem.expertise
          // é um campo DIFERENTE ("Avançado"/"Básico", nível de detalhe da
          // análise), não o plano — usá-lo antes do plano de verdade fazia todo
          // Consultor cair sempre em "Advisor" por engano.
          const planoBruto = (dados.plan || dados.selectedPlan || origem.expertise || dados.expertise || 'ENTRY').toString().toUpperCase();
          let planoNormalizado = 'ADVISOR';
          if (planoBruto.includes('ENTERPRISE')) planoNormalizado = 'ENTERPRISE';
          else if (planoBruto.includes('BOUTIQUE')) planoNormalizado = 'BOUTIQUE';
          else if (planoBruto.includes('ADVISOR')) planoNormalizado = 'ADVISOR';

          return {
            id: doc.id,
            name: dados.companyName || origem.companyName || dados.name || 'Empresa Sem Nome',
            cnpj: dados.cnpj || origem.cnpj || '00.000.000/0000-00',
            sector: dados.segment || origem.segment || 'Não Informado',
            niche: dados.profile || origem.profile || dados.niche || origem.niche || '-',
            revenue: higienizarNumero(dados.revenue || origem.revenue),
            costs: higienizarNumero(dados.costs || origem.costs),
            debt: higienizarNumero(dados.debt || origem.debt),

            // 🔥 Campos avançados do Consultor (obrigatórios no formulário do app) —
            // faltavam aqui, e por isso o EBITDA/Valuation do dashboard não batia
            // com o do app: o app aplica "EBITDA Ajustado" com esses valores, o
            // dashboard calculava só com receita-custos, sem o ajuste.
            custoNaoRecorrente: higienizarNumero(dados.custoNaoRecorrente || origem.custoNaoRecorrente),
            custoDivida: higienizarNumero(dados.custoDivida || origem.custoDivida),
            // 🔥 Também precisamos desses pra pré-preencher o formulário na edição
            prazoRecebimento: dados.prazoRecebimento || origem.prazoRecebimento || '',
            prazoPagamento: dados.prazoPagamento || origem.prazoPagamento || '',
            dividaCurtoPrazo: higienizarNumero(dados.dividaCurtoPrazo || origem.dividaCurtoPrazo),
            proLabore: higienizarNumero(dados.proLabore || origem.proLabore),
            historico: dados.historico || [],

            // 🤖 Textos já gerados pela IA no app — o Dashboard só LÊ,
            // nunca reprocessa (mesmo princípio do Gestor).
            parecerIA: dados.parecerIA || null,
            leituraProjecaoCurto: dados.leituraProjecaoCurto || null,
            leituraProjecaoMedio: dados.leituraProjecaoMedio || null,
            leituraProjecaoLongo: dados.leituraProjecaoLongo || null,
            oportunidadesIA: dados.oportunidadesIA || null,
            parecerIAInputSnapshot: dados.parecerIAInputSnapshot || null,

            // Agora o plano vai com o token perfeito para a tabela!
            plan: planoNormalizado,

            date: dados.dataCriacao ? new Date(dados.dataCriacao).toLocaleDateString('pt-BR') : (dados.date || '-'),
            _timestampBruto: dados.timestamp || dados.dataCriacao || '',

            // 🔥 Já vem junto na mesma consulta — usado pro contador de tarefas
            // atrasadas no sino, sem precisar de uma busca extra ao Firestore.
            tarefas: dados.tarefasConsultor || [],

            // 🔥 Estágio do funil de M&A — usado pelo board Kanban. Default 'analise'
            // pra dossiês antigos que ainda não tinham esse campo.
            pipelineStatus: dados.pipelineStatus || 'analise'
          };
        }).filter(Boolean);

        setClients(clientesReais);
        setCarregandoClientes(false);
      },
      (error) => {
        console.error("Erro na sincronização Phoenix OS:", error);
        setCarregandoClientes(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const calculateClientMetrics = (client) => {
    if (!client) return null;
    const core = calculateCoreMetrics(client.revenue, client.costs, client.debt, client.sector, {
      custoNaoRecorrente: client.custoNaoRecorrente,
      custoDivida: client.custoDivida
    });

    const sRev = Number(simRevenue) || 0;
    const sCos = Number(simCosts) || 0;
    // 🔥 CORRIGIDO: a projeção precisa partir do mesmo EBITDA AJUSTADO usado no
    // Enterprise Value base (core.ebitdaAjustado) — antes partia do EBITDA
    // bruto (receita-custos, sem os ajustes de Custo Não Recorrente/Dívida).
    // Isso fazia o "ganho" aparecer negativo mesmo com os sliders em 0%,
    // já que a base de comparação nunca batia com o Enterprise Value.
    const deltaReceita = client.revenue * (sRev / 100);
    const deltaCustos = client.costs * (sCos / 100);
    const projEbitda = core.ebitdaAjustado + deltaReceita + deltaCustos;
    const projValuation = (projEbitda > 0 ? projEbitda : 0) * core.multiple * 12;
    const valGains = projValuation - core.enterpriseValue;
    const projAnualizedGains = (projEbitda - core.ebitdaAjustado) * 12;

    // 🔥 CORREÇÃO: o Consultor nunca calculava NCG em lugar nenhum — o campo
    // "Necessidade/Folga de Capital de Giro" no relatório sempre saía zerado.
    // Mesma fórmula usada no Gestor e no app, pra bater igual nos três.
    const extended = calculateExtendedMetrics({
 sector: client.sector,
      rev: client.revenue, cos: client.costs, ebitda: core.ebitdaAjustado,
      alavancagem: core.alavancagem, currentMargin: core.currentMargin,
      prazoRecebimento: client.prazoRecebimento, prazoPagamento: client.prazoPagamento,
      dividaCurtoPrazo: client.dividaCurtoPrazo, proLabore: client.proLabore
    });

    // Equity projetado = EV projetado - dívida (mesma base do Equity atual). Antes a tela
    // comparava Equity ATUAL com EV PROJETADO e mostrava ganho sem nenhuma simulação.
    const projEquity = Math.max(0, projValuation - client.debt);
    const revProj = client.revenue * (1 + sRev / 100);
    const projMargin = revProj > 0 ? Number((((core.ebitda + (projEbitda - core.ebitdaAjustado)) / revProj) * 100).toFixed(1)) : core.currentMargin;
    return { ...core, projEbitda, projValuation, projEquity, projMargin, valGains, projAnualizedGains, ncg: extended.ncg, maScore: extended.maScore, safeDividends: extended.safeDividends, emergencyReserve: extended.emergencyReserve };
  };

  const metrics = calculateClientMetrics(selectedClient);

  const userLevelMap = {
    'ENTRY': 1, 'TACTICAL': 2, 'PLUS': 3, 'TOTAL': 4, 'BOARDROOM': 4,
    'ADVISOR': 2, 'BOUTIQUE': 3, 'ENTERPRISE': 4
  };
  const currentLevel = userLevelMap[String(planoConsultorReal || '').toUpperCase()] || 1;
  const checkAccess = (requiredLevel) => currentLevel >= requiredLevel;

  // 🤖 Números da projeção estocástica (calculados na hora, sem custo de IA)
  const projecaoPorPrazo = selectedClient && metrics ? calculateStochasticProjection(metrics, selectedClient.revenue) : [];

  // 🔥 Igual ao Gestor: se o Consultor fez upgrade e um bloco (médio/longo)
  // ainda não foi gerado pela IA, completa sozinho — sem regerar o parecer
  // inteiro nem os blocos que já existem.
  const blocosJaTentadosRef = useRef(new Set());
  useEffect(() => {
    if (!selectedClient || !selectedClient.parecerIA || atualizandoParecerIA) return;
    const horizontesLiberados = HORIZONTES_POR_PLANO_CONSULTOR[String(planoConsultorReal || '').toLowerCase()] || [];
    const camposPorBloco = { curto: 'leituraProjecaoCurto', medio: 'leituraProjecaoMedio', longo: 'leituraProjecaoLongo' };
    const blocosLiberados = [];
    if (horizontesLiberados.includes(3) || horizontesLiberados.includes(6)) blocosLiberados.push('curto');
    if (horizontesLiberados.includes(12)) blocosLiberados.push('medio');
    if (horizontesLiberados.includes(24) || horizontesLiberados.includes(36)) blocosLiberados.push('longo');
    const blocosFaltantes = blocosLiberados
      .filter(b => !selectedClient[camposPorBloco[b]])
      .filter(b => !blocosJaTentadosRef.current.has(`${selectedClient.id}:${b}`));

    if (blocosFaltantes.length === 0) return;
    blocosFaltantes.forEach(b => blocosJaTentadosRef.current.add(`${selectedClient.id}:${b}`));

    (async () => {
      setAtualizandoParecerIA(true);
      try {
        const core = calculateCoreMetrics(selectedClient.revenue, selectedClient.costs, selectedClient.debt, selectedClient.sector, {
          custoNaoRecorrente: selectedClient.custoNaoRecorrente, custoDivida: selectedClient.custoDivida
        });
        const projecao = calculateStochasticProjection({ ebitda: core.ebitda, ebitdaAjustado: core.ebitdaAjustado, currentMargin: core.currentMargin }, selectedClient.revenue);
        await gerarParecerIA({
          dossieId: String(selectedClient.id),
          tipoUsuario: 'Consultor',
          nicho: ((selectedClient.niche && selectedClient.niche !== '-') ? `${selectedClient.niche} (setor: ${selectedClient.sector || 'não informado'})` : (selectedClient.sector || 'negócio geral')),
          metricas: {
            receita: selectedClient.revenue, custos: selectedClient.costs, divida: selectedClient.debt,
            ebitda: core.ebitda, ebitdaAjustado: core.ebitdaAjustado, currentMargin: core.currentMargin, alavancagem: core.alavancagem
          },
          projecao,
          inputSnapshot: selectedClient.parecerIAInputSnapshot || null,
          blocosProjecao: blocosFaltantes,
          apenasComplementar: true
        });
      } catch (e) {
        console.error('[PHOENIX] Erro ao completar blocos de projeção (Dashboard Consultor):', e);
      } finally {
        setAtualizandoParecerIA(false);
      }
    })();
  }, [selectedClient?.id, planoConsultorReal, selectedClient?.parecerIA]);

  // 🔄 Texto da IA guardado com metodologia antiga (os números mudaram depois): refaz UMA vez ao abrir o cliente.
  useEffect(() => {
    if (!selectedClient || !selectedClient.parecerIA || atualizandoParecerIA || !planoConsultorReal) return;
    const snap = String(selectedClient.parecerIAInputSnapshot || '');
    if (snap.includes('"metodo":"' + METODO_PARECER + '"')) return;
    const chave = selectedClient.id + ':metodo';
    if (blocosJaTentadosRef.current.has(chave)) return;
    blocosJaTentadosRef.current.add(chave);
    regerarParecerAposEdicao(selectedClient.id, {
      companyName: selectedClient.name, niche: selectedClient.niche && selectedClient.niche !== '-' ? selectedClient.niche : '',
      segment: selectedClient.sector, revenue: selectedClient.revenue, costs: selectedClient.costs, debt: selectedClient.debt,
      custoNaoRecorrente: selectedClient.custoNaoRecorrente, custoDivida: selectedClient.custoDivida,
      prazoRecebimento: selectedClient.prazoRecebimento, prazoPagamento: selectedClient.prazoPagamento,
      dividaCurtoPrazo: selectedClient.dividaCurtoPrazo, proLabore: selectedClient.proLabore
    }).catch(e => console.error('[PHOENIX] Erro ao atualizar parecer de metodologia antiga:', e));
  }, [selectedClient?.id, selectedClient?.parecerIA, selectedClient?.parecerIAInputSnapshot, planoConsultorReal]);

  // 🔥 Mesmo princípio do app: o checkout só oferece planos ACIMA do atual.
  const planosParaUpgrade = PLANOS_CONSULTOR_CHECKOUT.filter(p => (userLevelMap[p.id.toUpperCase()] || 1) > currentLevel);

  const handleSecureLogout = () => {
    setLogoutState({ isLoggingOut: true, step: 1 });
    setTimeout(() => setLogoutState({ isLoggingOut: true, step: 2 }), 800);
    setTimeout(() => setLogoutState({ isLoggingOut: true, step: 3 }), 1600);
    setTimeout(() => {
      setLogoutState({ isLoggingOut: false, step: 0 });
      if (onLogout) onLogout();
    }, 2400);
  };

  // 🔥 Excluir conta de verdade — mesmo comportamento do app (apaga TODOS
  // os dossiês do usuário, tanto de Gestor quanto de Consultor, já que é a
  // mesma conta; depois apaga o perfil e a conta no Firebase Auth).
  // 🔥 CORREÇÃO IMPORTANTE que fiz por conta própria: antes de apagar a
  // conta, agora CANCELA a assinatura ativa no Asaas primeiro. Sem isso,
  // excluir a conta apagava o login mas a cobrança recorrente continuava
  // rodando pra sempre no Asaas — e a pessoa perderia o acesso à própria
  // conta pra conseguir cancelar, sem nenhum jeito de parar de ser cobrada.
  const excluirContaDefinitivamente = async () => {
    if (!auth.currentUser) return;
    setExcluindoConta(true);
    try {
      const uid = auth.currentUser.uid;
      const perfilSnap = await getDoc(doc(db, 'usuarios', uid));
      const dadosPerfil = perfilSnap.exists() ? perfilSnap.data() : {};
      if (dadosPerfil.consultorAsaasSubscriptionId) {
        await cancelarAssinaturaAsaas({ subscriptionId: dadosPerfil.consultorAsaasSubscriptionId, imediato: true }).catch(e =>
          console.error('[PHOENIX] Erro ao cancelar assinatura Consultor no Asaas (excluindo mesmo assim):', e)
        );
      }
      if (dadosPerfil.gestorAsaasSubscriptionId) {
        await cancelarAssinaturaAsaas({ subscriptionId: dadosPerfil.gestorAsaasSubscriptionId, imediato: true }).catch(e =>
          console.error('[PHOENIX] Erro ao cancelar assinatura Gestor no Asaas (excluindo mesmo assim):', e)
        );
      }
      const q = query(collection(db, 'cenarios'), where('userId', '==', uid));
      const snapshot = await getDocs(q);
      await Promise.all(snapshot.docs.map((d) => deleteDoc(doc(db, 'cenarios', d.id))));
      await deleteDoc(doc(db, 'usuarios', uid)).catch(() => { });
      await deleteUser(auth.currentUser);
      setConfirmandoExclusaoConta(false);
      if (typeof onLogout === 'function') onLogout();
    } catch (e) {
      console.error('[PHOENIX] Erro ao excluir conta:', e);
      if (e?.code === 'auth/requires-recent-login') {
        alert('Por segurança, saia e faça login novamente antes de excluir sua conta.');
      } else {
        alert('Não foi possível excluir a conta agora. Tente novamente.');
      }
    } finally {
      setExcluindoConta(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBrand({ ...brand, logo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  // Nome do plano (texto usado no paywall) -> id real, pra abrir o
  // checkout já com o plano certo pré-selecionado.
  const idDoPlanoPorNome = {
    'Advisor PRO': 'advisor', 'Boutique Elite': 'boutique', 'Enterprise Ultra': 'enterprise',
  };

  const renderPaywall = (moduleName, planRequired) => (
    <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-[#010208]/90 backdrop-blur-md rounded-[3rem] border border-white/5 animate-in fade-in">
      <div className="w-20 h-20 bg-slate-900 border border-white/10 rounded-full flex items-center justify-center mb-6 shadow-[0_0_50px_rgba(0,0,0,0.6)]">
        <Lock size={32} className="text-slate-500" />
      </div>
      <h3 className="text-3xl font-black text-white uppercase italic tracking-tighter mb-4 text-center">Módulo Bloqueado</h3>
      <p className="text-sm text-slate-400 max-w-md text-center leading-relaxed mb-8 px-4">
        O acesso ao painel de <span className="text-white font-bold">{moduleName}</span> exige uma licença corporativa ativa. O plano atual deste cliente permite apenas o diagnóstico básico.
      </p>
      <button
        onClick={() => abrirCheckout(idDoPlanoPorNome[planRequired])}
        className="bg-lime-400 text-black px-10 py-5 rounded-full font-black uppercase tracking-widest text-xs shadow-[0_0_30px_rgba(190,242,100,0.3)] hover:scale-105 active:scale-95 transition-all"
      >
        Assinar Protocolo {planRequired}
      </button>
    </div>
  );

  // 🔥 BUG CORRIGIDO: o Dashboard Consultor nunca bloqueava o acesso pra
  // quem não tem NENHUM plano Consultor ativo — a pessoa conseguia abrir
  // a Carteira, ver "Cadastrar Primeiro Cliente" e usar o painel
  // livremente, sem nunca ter pago nada. O Dashboard Gestor já tinha essa
  // proteção (bloqueio pra conta Entry); o Consultor não tinha
  // equivalente nenhum. Só bloqueia depois que a leitura do plano
  // realmente terminou (planoCarregado) — nunca no meio do carregamento,
  // pra não mostrar esse aviso por engano pra quem tem plano sim.
  if (planoCarregado && !planoConsultorReal) {
    return (
      <div className="h-screen w-screen bg-[#010208] flex flex-col items-center justify-center font-dashboard text-center px-6">
        <div className="w-20 h-20 bg-slate-900 border border-white/10 rounded-full flex items-center justify-center mb-6 shadow-[0_0_50px_rgba(0,0,0,0.6)]">
          <Lock size={32} className="text-slate-500" />
        </div>
        <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter mb-3">Nenhuma assinatura Consultor ativa</h2>
        <p className="text-sm text-slate-500 max-w-md mb-10 leading-relaxed">
          O Painel Desktop do Consultor — com carteira de clientes, parecer por IA, valuation e M&A — é liberado a partir do protocolo <span className="text-lime-400 font-bold">Advisor PRO</span>.
        </p>
        <div className="w-full max-w-md bg-[#0a0a12] border border-white/10 rounded-[2rem] p-8 text-left">
          <CheckoutAsaasCard
            planos={PLANOS_CONSULTOR_CHECKOUT}
            planoSelecionadoId="advisor"
            tipoPerfil="Consultor"
          />
        </div>
        <button
          onClick={() => { if (typeof onSwitchMode === 'function') onSwitchMode(); }}
          className="mt-8 text-slate-500 hover:text-lime-400 text-[11px] font-black uppercase tracking-widest transition-colors"
        >
          Voltar pro App Móvel
        </button>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-hidden bg-[#010208] relative">
      <div
        style={{
          transform: `scale(${escalaFonte})`,
          transformOrigin: 'top left',
          width: `${100 / escalaFonte}%`,
          height: `${100 / escalaFonte}%`,
        }}
        className={`bg-[#010208] text-slate-300 font-dashboard flex overflow-hidden selection:bg-lime-400 selection:text-black relative ${tema === 'light' ? 'theme-light' : ''} ${tema === 'dark' && contrasteOled ? 'oled-absoluto' : ''}`}
      >
        <DashboardStyles />

        {!online && (
          <div className="fixed top-0 left-0 right-0 z-[200] bg-red-500/90 backdrop-blur-sm text-white text-center py-2 flex items-center justify-center gap-2 text-[11px] font-black uppercase tracking-widest">
            <WifiOff size={14} /> Sem conexão com a internet — tentando reconectar automaticamente
          </div>
        )}

        {atualizandoParecerIA && (
          <div className="fixed bottom-6 right-6 z-[200] flex items-center gap-3 bg-[#0a0a12] border border-purple-500/30 rounded-full pl-4 pr-5 py-3 shadow-2xl">
            <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
            <p className="text-[11px] font-black text-purple-300 uppercase tracking-widest">Atualizando parecer com IA...</p>
          </div>
        )}

        {/* 🎉 CONFIRMAÇÃO VISUAL DE ASSINATURA — antes o Dashboard já sabia
            na hora que uma assinatura tinha sido confirmada (o listener de
            plano já existia), mas nunca avisava a pessoa disso — ela só
            percebia se reparasse o plano mudando sozinho em algum canto da
            tela. Agora aparece um aviso fechável, como no app. */}
        {avisoAssinaturaConfirmada && (
          <div className="fixed bottom-6 right-6 z-[300] flex items-start gap-3 bg-[#0a0a12] border border-lime-400/30 rounded-2xl pl-4 pr-3 py-4 shadow-2xl max-w-sm animate-in fade-in slide-in-from-bottom-4 duration-300">
            <CheckCircle2 size={20} className="text-lime-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs font-black text-white uppercase tracking-wide">Assinatura confirmada!</p>
              <p className="text-[11px] text-slate-400 mt-1">Plano <strong className="text-lime-400">{avisoAssinaturaConfirmada}</strong> já está ativo.</p>
            </div>
            <button
              onClick={() => setAvisoAssinaturaConfirmada(null)}
              className="text-slate-500 hover:text-white transition-colors shrink-0"
              aria-label="Fechar aviso"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {logoutState.isLoggingOut && (
          <div className="absolute inset-0 z-[100] bg-[#010208]/95 backdrop-blur-xl flex flex-col items-center justify-center animate-in fade-in duration-300">
            <div className="relative flex items-center justify-center">
              <div className="absolute w-40 h-40 bg-lime-400/20 blur-[50px] rounded-full"></div>
              {logoutState.step === 1 && <Shield className="text-lime-400 w-16 h-16 animate-pulse relative z-10" />}
              {logoutState.step === 2 && <Trash2 className="text-lime-400 w-16 h-16 animate-pulse relative z-10" />}
              {logoutState.step === 3 && <CheckCircle2 className="text-lime-400 w-16 h-16 relative z-10" />}
            </div>
            <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter mt-8 mb-2 text-center">
              {logoutState.step === 1 && "Encerrando Túneis de Criptografia..."}
              {logoutState.step === 2 && "Limpando Cache de Dados Locais..."}
              {logoutState.step === 3 && "Sessão Corporativa Encerrada"}
            </h2>
            <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest text-center max-w-xs">
              Protocolo de Segurança Phoenix OS®
            </p>
          </div>
        )}

        {/* SIDEBAR */}
        <aside className="w-72 bg-[#020617] border-r border-white/5 flex flex-col relative z-20 shadow-[20px_0_50px_rgba(0,0,0,0.5)] shrink-0 no-print">
          <div className="h-24 flex items-center px-8 border-b border-white/5">
            <span className="text-white font-black italic tracking-tighter text-3xl uppercase">PHOENIX<span className="text-lime-400">OS</span><sup className="text-xs ml-1">®</sup></span>
          </div>

          <div className="p-6">
            <button
              onClick={() => { setBuscaGlobalAberta(true); setTermoBuscaGlobal(''); setIndiceSelecionado(0); }}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 mb-6 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all text-left"
            >
              <span className="flex items-center gap-2.5 text-slate-500 text-xs font-bold">
                <Search size={15} /> Buscar...
              </span>
              <span className="text-[11px] font-black text-slate-600 uppercase tracking-widest bg-black/30 border border-white/10 px-1.5 py-0.5 rounded">Ctrl K</span>
            </button>
            <p className="text-xs font-black text-slate-600 uppercase tracking-widest mb-4 italic">Painel de Controle</p>
            <nav className="space-y-2">
              <button onClick={() => { setActiveTab('overview'); setSelectedClient(null); }} className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl font-bold text-sm transition-all ${activeTab === 'overview' && !selectedClient ? 'bg-lime-400/10 text-lime-400 border border-lime-400/20' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}>
                <LayoutDashboard size={18} /> Carteira
              </button>
              {/* 🤖 TEASER — Agentes de IA (Em Breve). Movido pro menu
              principal (sempre visível), igual no app — antes ficava
              junto de Configurações/Sair lá embaixo. */}
              <button onClick={() => { setActiveTab('agentes-ia'); setSelectedClient(null); }} className={`w-full flex items-center justify-between gap-2 px-3 py-4 rounded-2xl font-bold text-[13px] transition-all ${activeTab === 'agentes-ia' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}>
                <div className="flex items-center gap-3 whitespace-nowrap"><Bot size={18} className="shrink-0" /> Agentes de IA</div>
                <span className="whitespace-nowrap shrink-0 text-[11px] font-black uppercase tracking-wide bg-amber-500/15 text-amber-400 px-2 py-1 rounded-full border border-amber-500/30">Em Breve</span>
              </button>
              <button onClick={() => { setActiveTab('white-label'); setSelectedClient(null); }} className={`w-full flex items-center justify-between px-5 py-4 rounded-2xl font-bold text-sm transition-all ${activeTab === 'white-label' ? 'bg-white/10 text-white border border-white/20' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}>
                <div className="flex items-center gap-4"><Palette size={18} /> Marca & White-Label</div>
                {!checkAccess(4) && <Lock size={14} className="text-slate-500" />}
              </button>
            </nav>
          </div>

          <div className={`overflow-hidden transition-all duration-500 ease-in-out px-6 ${selectedClient ? 'max-h-[500px] opacity-100 pb-6' : 'max-h-0 opacity-0 pointer-events-none'}`}>
            <p className="text-xs font-black text-slate-600 uppercase tracking-widest mb-4 italic mt-2 border-t border-white/5 pt-6">Empresa Ativa</p>
            <div className="bg-white/5 border border-white/10 p-4 rounded-2xl mb-4">
              <p className="text-white font-black uppercase text-sm truncate">{selectedClient?.name}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="px-2 py-0.5 rounded text-[11px] font-black uppercase bg-lime-400/20 text-lime-400 border border-lime-400/30">
                  Plano {planoConsultorReal ? PLANOS_CONSULTOR[planoConsultorReal]?.nome || planoConsultorReal : '...'}
                </span>
              </div>
            </div>

            <nav className="space-y-1">
              <button onClick={() => setActiveTab('client-overview')} className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl font-bold text-sm transition-all ${activeTab === 'client-overview' ? 'bg-white/10 text-white border border-white/20' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}>
                <Activity size={18} /> Diagnóstico Geral
              </button>
              <button onClick={() => setActiveTab('mesa-operacoes')} className={`w-full flex items-center justify-between px-5 py-4 rounded-2xl font-bold text-sm transition-all ${activeTab === 'mesa-operacoes' ? 'bg-white/10 text-white border border-white/20' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}>
                <div className="flex items-center gap-4"><Sliders size={18} /> Simulador Avançado</div>
                {!checkAccess(2) && <Lock size={14} className="text-slate-500" />}
              </button>
              <button onClick={() => setActiveTab('cfo-virtual')} className={`w-full flex items-center justify-between px-5 py-4 rounded-2xl font-bold text-sm transition-all ${activeTab === 'cfo-virtual' ? 'bg-white/10 text-white border border-white/20' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}>
                <div className="flex items-center gap-4"><Landmark size={18} /> APEX</div>
                {!checkAccess(3) && <Lock size={14} className="text-slate-500" />}
              </button>
              <button onClick={() => setActiveTab('boardroom')} className={`w-full flex items-center justify-between px-5 py-4 rounded-2xl font-bold text-sm transition-all ${activeTab === 'boardroom' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}>
                <div className="flex items-center gap-4"><Briefcase size={18} /> Boardroom M&A</div>
                {!checkAccess(4) && <Lock size={14} className="text-slate-500" />}
              </button>
            </nav>
          </div>

          <div className="mt-auto p-6 border-t border-white/5">
            <p className="text-xs font-black text-slate-600 uppercase tracking-widest mb-4 italic">Sistema</p>
            <nav className="space-y-1">
              <button onClick={() => { setActiveTab('settings'); setSelectedClient(null); }} className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl font-bold text-sm transition-all ${activeTab === 'settings' ? 'bg-white/10 text-white border border-white/20' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}>
                <Settings size={18} /> Configurações
              </button>
              <button onClick={handleSecureLogout} className="w-full flex items-center gap-4 px-5 py-4 rounded-2xl font-bold text-sm text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all mt-4 border border-transparent hover:border-red-500/20">
                <LogOut size={18} /> Sair do Painel
              </button>
            </nav>
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <main className="flex-1 flex flex-col h-full relative overflow-hidden bg-gradient-to-br from-[#010208] to-[#020617]">
          <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-lime-400/5 blur-[120px] rounded-full pointer-events-none dark-glow"></div>

          {pagamentoAtrasado && (
            <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)', borderBottom: '1px solid rgba(239, 68, 68, 0.5)', padding: '12px 40px', textAlign: 'center', position: 'relative', zIndex: 30 }}>
              <span style={{ color: '#f87171', fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                ⚠️ Não conseguimos identificar seu pagamento. Verifique sua forma de pagamento nas próximas{' '}
                {Math.max(0, Math.ceil(48 - (Date.now() - new Date(pagamentoAtrasado.desde).getTime()) / 3600000))}h para continuar usando o Phoenix OS{!temGestorAtivoTambem ? ' (Consultor)' : ''}.
              </span>
            </div>
          )}
          {acessoSuspensoPorAtraso && (
            <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)', borderBottom: '1px solid rgba(239, 68, 68, 0.5)', padding: '12px 40px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '12px', textAlign: 'center', position: 'relative', zIndex: 30 }}>
              <span style={{ color: '#f87171', fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                ⚠️ Não conseguimos identificar seu pagamento{!temGestorAtivoTambem ? ' do Consultor' : ''}. Atualize sua forma de pagamento pra continuar usando {temGestorAtivoTambem ? 'o Phoenix OS' : 'esse perfil'}.
              </span>
              <button
                onClick={() => setActiveTab('settings')}
                style={{ backgroundColor: '#f87171', color: '#000', fontSize: '10px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '6px 16px', borderRadius: '9999px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                Atualizar pagamento
              </button>
            </div>
          )}

          <header className="h-24 flex items-center justify-between px-10 border-b border-white/5 bg-[#010208]/80 backdrop-blur-xl relative z-30 shrink-0">

            {/* LADO ESQUERDO: Títulos Dinâmicos */}
            <div>
              <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter">
                {activeTab === 'overview' && 'Mesa de Operações'}
                {activeTab === 'client-overview' && 'Diagnóstico Executivo'}
                {activeTab === 'mesa-operacoes' && 'Simulador Avançado'}
                {activeTab === 'cfo-virtual' && 'APEX — Múltiplo & Sensibilidade'}
                {activeTab === 'boardroom' && 'Boardroom & M&A'}
                {activeTab === 'white-label' && 'Configurações de Marca (White-Label)'}
                {activeTab === 'agentes-ia' && 'Agentes de IA'}
                {activeTab === 'settings' && 'Configurações Globais'}
              </h2>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">
                  {selectedClient ? `${selectedClient.name} • ${selectedClient.sector}` : 'Visão Geral do Consultor'}
                </p>
                {selectedClient && metrics && activeTab !== 'overview' && (() => {
                  const saudeHdr = calculateHealthStatus({ ebitda: metrics.ebitdaAjustado, alavancagem: metrics.alavancagem, deb: selectedClient.debt, rev: selectedClient.revenue, sector: selectedClient.sector });
                  return (
                    <span title="Status de saúde financeira do cliente (mesma classificação da Carteira)" className={`px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest border flex items-center gap-1.5 ${saudeHdr.label === 'Saudável' ? 'text-lime-400 bg-lime-400/10 border-lime-500/20' : saudeHdr.label === 'Atenção' ? 'text-amber-400 bg-amber-400/10 border-amber-500/20' : saudeHdr.label === 'Falência Iminente' ? 'text-red-600 bg-red-600/10 border-red-600/20' : 'text-red-500 bg-red-500/10 border-red-500/20'}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                      Saúde: {saudeHdr.label}
                    </span>
                  );
                })()}
              </div>
            </div>

            {/* LADO DIREITO: Notificações Inteligentes + Perfil do Auditor */}
            <div className="flex items-center gap-6 relative no-print">

              {/* Acessibilidade: diminuir / aumentar fonte */}
              <div className="flex items-center bg-white/5 rounded-full border border-white/10 overflow-hidden z-40">
                <button
                  onClick={() => ajustarFonte(-0.05)}
                  className="px-3 py-3 hover:bg-white/10 transition-colors text-slate-300 text-[11px] font-black"
                  title="Diminuir fonte"
                >
                  A-
                </button>
                <div className="w-px h-4 bg-white/10"></div>
                <button
                  onClick={() => ajustarFonte(0.05)}
                  className="px-3 py-3 hover:bg-white/10 transition-colors text-slate-300 text-[11px] font-black"
                  title="Aumentar fonte"
                >
                  A+
                </button>
              </div>

              {/* Alternar Tema Claro/Escuro */}
              <button
                onClick={() => setTema(t => t === 'light' ? 'dark' : 'light')}
                className="relative p-3 bg-white/5 rounded-full border border-white/10 hover:bg-white/10 transition-colors z-40"
                title={tema === 'light' ? 'Mudar para tema escuro' : 'Mudar para tema claro'}
              >
                {tema === 'light' ? <Moon size={18} className="text-slate-300" /> : <Sun size={18} className="text-slate-300" />}
              </button>

              {/* 1. Botão do Sino */}
              <div ref={notificationsRef} className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-3 bg-white/5 rounded-full border border-white/10 hover:bg-white/10 transition-colors z-40"
                >
                  <Bell size={18} className="text-slate-300" />
                  {(() => {
                    const totalAlertas = calcularAlertasAtivos().length + calcularTarefasAtrasadas().length;
                    return totalAlertas > 0 && (
                      <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 rounded-full text-[11px] font-black text-white flex items-center justify-center animate-pulse">
                        {totalAlertas}
                      </span>
                    );
                  })()}
                </button>

                {/* 2. Dropdown de Alertas */}
                {showNotifications && (
                  <div className="absolute right-0 top-16 w-80 bg-[#020617] border border-white/10 rounded-2xl p-4 shadow-2xl z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="flex justify-between items-center border-b border-white/5 pb-2 mb-3">
                      <span className="text-[11px] font-black uppercase tracking-widest text-slate-400">Alertas do Portfólio</span>
                      <button
                        onClick={() => {
                          const idsCriticos = calcularAlertasAtivos().map(c => c.id);
                          setClearedAlerts(prev => [...prev, ...idsCriticos]);
                          setShowNotifications(false);
                        }}
                        className="text-[11px] font-bold text-slate-500 hover:text-red-400 uppercase tracking-widest px-2 py-0.5 rounded transition-colors"
                      >
                        Limpar
                      </button>
                    </div>

                    <div className="space-y-2 max-h-72 overflow-y-auto custom-scroll">
                      {(() => {
                        const alertasAtivos = calcularAlertasAtivos();
                        const tarefasAtrasadas = calcularTarefasAtrasadas();

                        if (alertasAtivos.length === 0 && tarefasAtrasadas.length === 0) {
                          return <p className="text-xs text-slate-600 text-center py-4 italic">Nenhum alerta pendente.</p>;
                        }

                        return (
                          <>
                            {alertasAtivos.map(c => (
                              <div key={`risco-${c.id}`} className="p-3 bg-white/5 rounded-xl border border-white/5 hover:border-red-500/50 transition-colors">
                                <p className="text-xs font-bold text-red-400 flex items-center gap-1.5 uppercase">
                                  <AlertTriangle size={12} /> Risco Crítico
                                </p>
                                <p className="text-[11px] text-slate-300 mt-1">{c.name} atingiu nível crítico.</p>
                              </div>
                            ))}
                            {tarefasAtrasadas.map(t => (
                              <button
                                key={`tarefa-${t.id}`}
                                onClick={() => {
                                  const cliente = clients.find(c => c.id === t.clienteId);
                                  if (cliente) {
                                    setSelectedClient(cliente);
                                    setActiveTab('client-overview');
                                  }
                                  setShowNotifications(false);
                                }}
                                className="w-full text-left p-3 bg-white/5 rounded-xl border border-white/5 hover:border-orange-500/50 transition-colors"
                              >
                                <p className="text-xs font-bold text-orange-400 flex items-center gap-1.5 uppercase">
                                  <CheckCircle2 size={12} /> Tarefa Atrasada
                                </p>
                                <p className="text-[11px] text-slate-300 mt-1">{t.texto} — {t.clienteNome}</p>
                              </button>
                            ))}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* 3. Perfil do Consultor logado */}
              <div className="flex items-center gap-4 bg-white/5 px-4 py-2 rounded-full border border-white/10 cursor-pointer hover:bg-white/10 transition-colors">
                <div className="w-8 h-8 bg-lime-400/20 rounded-full flex items-center justify-center border border-lime-400/30">
                  <User size={14} className="text-lime-400" />
                </div>
                <div>
                  <p className="text-sm font-black text-white uppercase italic truncate max-w-[160px]">{userEmail || 'Consultor'}</p>
                  <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Sessão Ativa</p>
                </div>
              </div>

            </div>
          </header>

          {selectedClient && (
            (activeTab === 'mesa-operacoes' && !checkAccess(2)) ||
            (activeTab === 'cfo-virtual' && !checkAccess(3)) ||
            (activeTab === 'boardroom' && !checkAccess(4)) ||
            (activeTab === 'client-overview' && selectedClient.plan === 'ENTRY')
          ) && (
              <div className="bg-gradient-to-r from-lime-400/10 to-transparent border-b border-lime-400/20 px-10 py-3 flex items-center gap-4 relative z-10 shrink-0">
                <Wand size={18} className="text-lime-400 animate-pulse" />
                <p className="text-xs text-lime-400 font-medium">
                  <strong className="font-black uppercase tracking-widest mr-2">Modo Visualização:</strong>
                  O plano atual restringe o acesso aos simuladores e inteligência preditiva desta aba.
                  <button
                    onClick={() => {
                      const planoNecessario = activeTab === 'boardroom' ? 'enterprise'
                        : activeTab === 'cfo-virtual' ? 'boutique'
                          : 'advisor';
                      abrirCheckout(planoNecessario);
                    }}
                    className="ml-2 underline font-bold hover:text-white transition-colors"
                  >
                    Fazer upgrade agora para desbloquear o cockpit completo.
                  </button>
                </p>
              </div>
            )}

          {/* ÁREA DE SCROLL DO CONTEÚDO */}
          <div className="flex-1 overflow-y-auto custom-scroll relative z-10 p-10 flex flex-col">

            {/* TAB 1: VISÃO GERAL DO PORTFÓLIO */}
            {activeTab === 'overview' && !selectedClient && (() => {
              // 🔥 Mesma lista filtrada (busca/setor/plano) usada tanto na tabela
              // quanto no botão de exportar CSV — assim exporta exatamente o que
              // está sendo mostrado na tela, não a carteira inteira sempre.
              const clientesFiltrados = clients.filter(c => (c.name.toLowerCase().includes(searchQuery.toLowerCase()) || c.cnpj.includes(searchQuery)) && (sectorFilter === 'Todos' || c.sector === sectorFilter) && (statusFilter === 'Todos' || c.plan === statusFilter));
              return (
                <div className="space-y-8 animate-in fade-in duration-500 mb-8">
                  {/* 🔥 ESTADO DE CARREGAMENTO — antes, entre o login e a
                      chegada dos dados, o Consultor via um painel zerado
                      ("0 Ativas", "R$ 0,00") como se tivesse perdido a
                      carteira toda. Agora fica claro que está carregando. */}
                  {carregandoClientes ? (
                    <div className="flex flex-col items-center justify-center py-32 gap-5">
                      <div className="w-10 h-10 border-[3px] border-lime-400 border-t-transparent rounded-full animate-spin" />
                      <p className="text-xs font-black text-slate-500 uppercase tracking-widest">Carregando sua carteira...</p>
                    </div>
                  ) : clients.length === 0 ? (
                    /* 🔥 ESTADO VAZIO — primeiro acesso de um Consultor novo.
                       Antes ele caía num painel de métricas todo zerado, sem
                       nenhuma orientação do que fazer. */
                    <div className="flex flex-col items-center justify-center py-28 text-center">
                      <div className="w-20 h-20 bg-lime-400/10 rounded-3xl flex items-center justify-center border border-lime-400/20 mb-6">
                        <Briefcase size={34} className="text-lime-400" />
                      </div>
                      <h3 className="text-2xl font-black text-white uppercase italic tracking-tighter mb-3">Sua carteira está vazia</h3>
                      <p className="text-sm text-slate-400 max-w-md leading-relaxed mb-8">
                        Cadastre seu primeiro cliente para começar a monitorar performance, valuation e risco de ruptura em um só lugar.
                      </p>
                      <button
                        onClick={() => { setClienteEditandoId(null); setNovoCliente({ companyName: '', cnpj: '', segment: 'Varejo', niche: '', revenue: '', costs: '', debt: '', custoNaoRecorrente: '', custoDivida: '', prazoRecebimento: '', prazoPagamento: '', dividaCurtoPrazo: '', proLabore: '' }); setModalNovoCliente(true); }}
                        className="bg-lime-400 text-black px-8 py-4 rounded-full font-black uppercase text-xs tracking-widest hover:bg-lime-500 active:scale-95 transition-all flex items-center gap-2 shadow-[0_0_20px_rgba(190,242,100,0.2)]"
                      >
                        <Plus size={16} /> Cadastrar Primeiro Cliente
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => exportarCarteiraCSV(clientesFiltrados)}
                          disabled={clientesFiltrados.length === 0}
                          className="px-6 py-3.5 rounded-full font-black uppercase text-xs tracking-widest active:scale-95 transition-all flex items-center gap-2 border bg-white/5 text-slate-300 border-white/10 hover:bg-white/10 disabled:opacity-30 disabled:pointer-events-none"
                        >
                          <Download size={16} /> Exportar CSV
                        </button>
                        <button
                          onClick={() => { setModoComparacao(m => !m); setEmpresasParaComparar([]); }}
                          className={`px-6 py-3.5 rounded-full font-black uppercase text-xs tracking-widest active:scale-95 transition-all flex items-center gap-2 border ${modoComparacao ? 'bg-blue-500/20 text-blue-400 border-blue-500/40' : 'bg-white/5 text-slate-300 border-white/10 hover:bg-white/10'}`}
                        >
                          <Sliders size={16} /> {modoComparacao ? 'Cancelar Comparação' : 'Comparar Empresas'}
                        </button>
                        <button onClick={() => { setClienteEditandoId(null); setNovoCliente({ companyName: '', cnpj: '', segment: 'Varejo', niche: '', revenue: '', costs: '', debt: '', custoNaoRecorrente: '', custoDivida: '', prazoRecebimento: '', prazoPagamento: '', dividaCurtoPrazo: '', proLabore: '' }); setModalNovoCliente(true); }} className="bg-lime-400 text-black px-6 py-3.5 rounded-full font-black uppercase text-xs tracking-widest hover:bg-lime-500 active:scale-95 transition-all flex items-center gap-2 shadow-[0_0_20px_rgba(190,242,100,0.2)]">
                          <Plus size={16} /> Novo Cliente
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-slate-900/60 p-8 pr-24 rounded-[2rem] border border-white/5 shadow-lg relative overflow-hidden group">
                          <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity"><Briefcase size={60} className="text-lime-400" /></div>
                          <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-2 relative z-10">Valuation sob Gestão</p>
                          <p className="text-2xl xl:text-3xl font-black text-white italic tracking-tighter drop-shadow-[0_0_15px_rgba(190,242,100,0.2)] relative z-10 break-words">
                            R$ {formatCurrency(clients.reduce((acc, c) => acc + calculateClientMetrics(c).equityValue, 0))}
                          </p>
                        </div>
                        <div className="bg-slate-900/60 p-8 pr-24 rounded-[2rem] border border-white/5 shadow-lg relative overflow-hidden group">
                          <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity"><Users size={60} className="text-blue-400" /></div>
                          <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-2 relative z-10">Empresas Monitoradas</p>
                          <p className="text-3xl xl:text-4xl font-black text-white italic tracking-tighter drop-shadow-[0_0_15px_rgba(59,130,246,0.2)] relative z-10">
                            {clients.length} <span className="text-lg text-slate-500 font-bold not-italic">Ativas</span>
                          </p>
                        </div>
                        <div className="bg-slate-900/60 p-8 pr-24 rounded-[2rem] border border-white/5 shadow-lg relative overflow-hidden group">
                          <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity"><AlertTriangle size={60} className="text-orange-400" /></div>
                          <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-2 relative z-10">Risco Médio Ruptura</p>
                          <p className="text-3xl xl:text-4xl font-black text-orange-400 italic tracking-tighter drop-shadow-[0_0_15px_rgba(251,146,60,0.2)] relative z-10">
                            {/* 🔥 BUG CORRIGIDO: sem clientes, isso dividia por zero
                            e mostrava literalmente "NaN%" na tela — a primeira
                            coisa que um Consultor novo veria ao entrar. */}
                            {clients.length > 0
                              ? (clients.reduce((acc, c) => acc + calculateClientMetrics(c).ruptureRisk, 0) / clients.length).toFixed(1).replace('.', ',')
                              : '0,0'}%
                          </p>
                        </div>
                      </div>

                      {/* IA ESTOCÁSTICA — teaser em destaque na Mesa de Operações.
                    Antes só aparecia dentro do módulo APEX de cada cliente;
                    agora fica visível assim que o Consultor entra no painel,
                    com o horizonte liberado pelo plano atual. */}
                      <div className="bg-gradient-to-br from-purple-900/20 to-transparent border border-purple-500/20 p-8 rounded-[2rem]">
                        <div className="flex items-center gap-2 mb-2">
                          <Zap size={16} className="text-purple-400" />
                          <p className="text-sm font-black text-purple-400 uppercase tracking-widest">IA Estocástica — Previsão de Futuro</p>
                          <span className="ml-auto text-[11px] font-black tracking-normal bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full border border-purple-500/30">
                            Advisor: 3 e 6 meses · Boutique: até 12 · Enterprise: até 36
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 max-w-2xl">
                          Projeção do futuro financeiro de cada cliente a partir do histórico salvo — o horizonte depende do plano de cada empresa na carteira. Abra qualquer cliente e acesse o módulo APEX para ver a projeção completa.
                        </p>
                      </div>

                      <div className="flex gap-4 items-center bg-[#020617]/50 p-4 rounded-[2rem] border border-white/5 shadow-inner">
                        <div className="flex-1 relative">
                          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                          <input type="text" placeholder="Pesquisar por Nome ou CNPJ..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full bg-black/40 border border-white/5 py-4 pl-12 pr-4 rounded-xl text-sm font-bold text-white outline-none focus:border-lime-400/50 transition-all placeholder:text-slate-600" />
                        </div>
                        <select value={sectorFilter} onChange={(e) => setSectorFilter(e.target.value)} className="bg-black/40 border border-white/5 py-4 px-4 rounded-xl text-sm font-bold text-slate-300 outline-none focus:border-lime-400/50 transition-all cursor-pointer">
                          <option value="Todos">Todos os Setores</option>
                          {Array.from(new Set(clients.map(c => c.sector))).map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-black/40 border border-white/5 py-4 px-4 rounded-xl text-sm font-bold text-slate-300 outline-none focus:border-lime-400/50 transition-all cursor-pointer">
                          <option value="Todos">Todos os Planos</option>
                          <option value="ADVISOR">Advisor</option>
                          <option value="BOUTIQUE">Boutique Elite</option>
                          <option value="ENTERPRISE">Enterprise Ultra</option>
                        </select>
                        <div className="flex bg-black/40 border border-white/5 rounded-xl p-1 shrink-0">
                          <button
                            onClick={() => setViewMode('tabela')}
                            className={`px-4 py-2.5 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${viewMode === 'tabela' ? 'bg-lime-400 text-black' : 'text-slate-400 hover:text-white'}`}
                          >
                            <LayoutDashboard size={13} /> Tabela
                          </button>
                          <button
                            onClick={() => setViewMode('kanban')}
                            className={`px-4 py-2.5 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${viewMode === 'kanban' ? 'bg-lime-400 text-black' : 'text-slate-400 hover:text-white'}`}
                          >
                            <Sliders size={13} className="rotate-90" /> Kanban
                          </button>
                        </div>
                      </div>

                      {modoComparacao && empresasParaComparar.length > 0 && (
                        <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-5 flex items-center justify-between flex-wrap gap-4">
                          <p className="text-xs font-black text-blue-400 uppercase tracking-widest">
                            {empresasParaComparar.length} empresa{empresasParaComparar.length > 1 ? 's' : ''} selecionada{empresasParaComparar.length > 1 ? 's' : ''} <span className="text-blue-400/50 normal-case font-medium">(mínimo 2, máximo 4)</span>
                          </p>
                          <button
                            onClick={() => setModalComparacao(true)}
                            disabled={empresasParaComparar.length < 2}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-full font-black uppercase text-xs tracking-widest transition-all disabled:opacity-30 disabled:pointer-events-none"
                          >
                            Comparar Agora
                          </button>
                        </div>
                      )}

                      {viewMode === 'kanban' ? (
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                          {COLUNAS_PIPELINE.map(coluna => {
                            const empresasDaColuna = clients.filter(c =>
                              (c.pipelineStatus || 'analise') === coluna.key &&
                              (c.name.toLowerCase().includes(searchQuery.toLowerCase()) || c.cnpj.includes(searchQuery)) &&
                              (sectorFilter === 'Todos' || c.sector === sectorFilter) &&
                              (statusFilter === 'Todos' || c.plan === statusFilter)
                            );
                            const corBorda = {
                              slate: 'border-t-slate-500', blue: 'border-t-blue-500',
                              lime: 'border-t-lime-400', red: 'border-t-red-500'
                            }[coluna.cor];
                            const corTexto = {
                              slate: 'text-slate-400', blue: 'text-blue-400',
                              lime: 'text-lime-400', red: 'text-red-400'
                            }[coluna.cor];

                            return (
                              <div
                                key={coluna.key}
                                onDragOver={(e) => { e.preventDefault(); setColunaArrastandoSobre(coluna.key); }}
                                onDragLeave={() => setColunaArrastandoSobre(null)}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  const clientId = e.dataTransfer.getData('text/plain');
                                  if (clientId) moverPipeline(clientId, coluna.key);
                                  setColunaArrastandoSobre(null);
                                }}
                                className={`bg-[#020617] border-t-4 ${corBorda} border-x border-b border-white/5 rounded-2xl p-4 min-h-[500px] transition-all ${colunaArrastandoSobre === coluna.key ? 'ring-2 ring-lime-400/50 bg-lime-400/[0.02]' : ''
                                  }`}
                              >
                                <div className="flex items-center justify-between mb-4 px-1">
                                  <p className={`text-xs font-black uppercase tracking-widest ${corTexto}`}>{coluna.label}</p>
                                  <span className="text-[11px] font-black text-slate-600 bg-white/5 px-2 py-0.5 rounded-full">{empresasDaColuna.length}</span>
                                </div>

                                <div className="space-y-3">
                                  {empresasDaColuna.length === 0 ? (
                                    <p className="text-[11px] text-slate-700 text-center py-8 italic">Arraste uma empresa pra cá</p>
                                  ) : (
                                    empresasDaColuna.map(client => (
                                      <div
                                        key={client.id}
                                        draggable
                                        onDragStart={(e) => e.dataTransfer.setData('text/plain', client.id)}
                                        onClick={() => { setSelectedClient(client); setActiveTab('client-overview'); }}
                                        className="bg-white/5 border border-white/5 hover:border-lime-400/30 rounded-xl p-4 cursor-grab active:cursor-grabbing transition-all hover:bg-white/[0.07]"
                                      >
                                        <p className="text-sm font-black text-white truncate">{client.name}</p>
                                        <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest mt-1">{client.sector}</p>
                                        <span className={`inline-block mt-2 px-2.5 py-1 rounded text-[11px] font-black uppercase tracking-widest ${client.plan === 'ENTERPRISE' ? 'bg-purple-500/20 text-purple-400' :
                                          client.plan === 'BOUTIQUE' ? 'bg-indigo-500/20 text-indigo-400' :
                                            client.plan === 'ADVISOR' ? 'bg-blue-500/20 text-blue-400' :
                                              'bg-lime-400/20 text-lime-400'
                                          }`}>{client.plan}</span>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="bg-[#020617] border border-white/5 rounded-[2rem] overflow-hidden shadow-2xl min-h-[400px]">
                          <table className="w-full text-left border-collapse">
                            <thead>
                              <tr className="border-b border-white/5 bg-white/5">
                                {modoComparacao && <th className="py-5 px-6 w-10"></th>}
                                <th className="py-5 px-6 text-[11px] font-black text-slate-500 uppercase tracking-widest">Empresa</th>
                                <th className="py-5 px-6 text-[11px] font-black text-slate-500 uppercase tracking-widest">Setor</th>
                                <th className="py-5 px-6 text-[11px] font-black text-slate-500 uppercase tracking-widest">EBITDA Real</th>
                                <th className="py-5 px-6 text-[11px] font-black text-slate-500 uppercase tracking-widest">Valuation (Equity)</th>
                                <th className="py-5 px-6 text-[11px] font-black text-slate-500 uppercase tracking-widest text-center">Risco</th>
                                <th className="py-5 px-6 text-[11px] font-black text-slate-500 uppercase tracking-widest text-center">Plano</th>
                                <th className="py-5 px-6 text-[11px] font-black text-slate-500 uppercase tracking-widest text-right">Ação</th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* 🏢 HIERARQUIA MATRIZ → FILIAIS: em vez de listar
                              tudo solto, reordena pra cada filial aparecer
                              logo abaixo da própria matriz, indentada. Assim
                              o Consultor bate o olho e entende a estrutura do
                              grupo sem precisar abrir cliente por cliente.
                              Empresas sem grupo continuam normais, na ordem. */}
                              {(() => {
                                const jaIncluidos = new Set();
                                const listaHierarquica = [];
                                clientesFiltrados.forEach((c) => {
                                  if (jaIncluidos.has(c.id)) return;
                                  const irmas = irmasDoMesmoGrupo(c, clientesFiltrados);
                                  if (irmas.length === 0) {
                                    listaHierarquica.push({ cliente: c, nivel: 0, matrizDoGrupo: null });
                                    jaIncluidos.add(c.id);
                                    return;
                                  }
                                  // Monta o grupo inteiro de uma vez, matriz primeiro.
                                  const grupo = [c, ...irmas].sort((a, b) => String(a.cnpj).localeCompare(String(b.cnpj)));
                                  const matrizDoGrupo = grupo.find(u => ehMatriz(u.cnpj)) || null;
                                  grupo.forEach((u) => {
                                    if (jaIncluidos.has(u.id)) return;
                                    const ehAMatriz = matrizDoGrupo && u.id === matrizDoGrupo.id;
                                    listaHierarquica.push({
                                      cliente: u,
                                      nivel: ehAMatriz ? 0 : 1,
                                      matrizDoGrupo: ehAMatriz ? null : matrizDoGrupo
                                    });
                                    jaIncluidos.add(u.id);
                                  });
                                });
                                return listaHierarquica;
                              })()
                                .map(({ cliente: client, nivel, matrizDoGrupo }) => {
                                  const m = calculateClientMetrics(client);
                                  // 🔥 Mesmo motor de status de saúde usado no app mobile e no resto do
                                  // dashboard — antes esse cálculo de risco era duplicado à mão aqui e
                                  // podia divergir do resto do sistema.
                                  const saude = calculateHealthStatus({ ebitda: m.ebitdaAjustado, alavancagem: m.alavancagem, deb: client.debt, rev: client.revenue, sector: client.sector });

                                  return (
                                    <tr key={client.id} className={`border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer group ${empresasParaComparar.includes(client.id) ? 'bg-blue-500/5' : nivel === 1 ? 'bg-blue-500/[0.03]' : ''}`} onClick={() => { if (modoComparacao) { alternarSelecaoComparacao(client.id); } else { setSelectedClient(client); setActiveTab('client-overview'); } }}>

                                      {modoComparacao && (
                                        <td className="py-4 px-6" onClick={(e) => e.stopPropagation()}>
                                          <button onClick={() => alternarSelecaoComparacao(client.id)} className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${empresasParaComparar.includes(client.id) ? 'bg-blue-500 border-blue-500' : 'border-slate-500 hover:border-blue-400 bg-white/5'}`}>
                                            {empresasParaComparar.includes(client.id) && <CheckCircle2 size={14} className="text-white" />}
                                          </button>
                                        </td>
                                      )}
                                      {/* 1. Empresa */}
                                      <td className="py-4 px-6">
                                        <div className="flex items-start gap-2">
                                          {/* Conector visual da hierarquia — só nas filiais */}
                                          {nivel === 1 && (
                                            <div className="flex items-center gap-1.5 pt-1 shrink-0" aria-hidden="true">
                                              <span className="w-4 h-px bg-blue-500/40" />
                                              <span className="w-1.5 h-1.5 rounded-full bg-blue-500/60" />
                                            </div>
                                          )}
                                          <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <p className={`font-black text-white ${nivel === 1 ? 'text-sm' : 'text-base'}`}>{client.name}</p>
                                              {/* 🏢 Selo de grupo — só aparece quando existe outra
                                              unidade com a mesma raiz de CNPJ na carteira. */}
                                              {irmasDoMesmoGrupo(client, clients).length > 0 && (
                                                <span className={`text-[11px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 flex items-center gap-1 ${ehMatriz(client.cnpj) ? 'bg-lime-400/15 text-lime-400 border border-lime-400/30' : 'bg-blue-500/15 text-blue-300 border border-blue-500/30'}`}>
                                                  <Building2 size={9} /> {rotuloUnidade(client.cnpj)}
                                                </span>
                                              )}
                                            </div>
                                            {/* Diz de QUAL empresa essa filial faz parte */}
                                            {nivel === 1 && matrizDoGrupo && (
                                              <p className="text-[11px] text-blue-300/70 font-bold mt-0.5">
                                                filial de {matrizDoGrupo.name}
                                              </p>
                                            )}
                                            <p className="text-xs text-slate-500 font-bold mt-1 tracking-widest">{client.cnpj}</p>
                                          </div>
                                        </div>
                                      </td>

                                      {/* 2. Setor */}
                                      <td className="py-4 px-6 text-sm text-slate-300 font-medium whitespace-nowrap">
                                        {client.sector}
                                      </td>

                                      {/* 3. EBITDA (Blindado e com alerta vermelho para negativo) */}
                                      <td className="py-4 px-6 text-sm font-medium whitespace-nowrap">
                                        <span className={m?.ebitda < 0 ? "text-red-500" : "text-slate-300"}>
                                          R$ {m?.ebitda ? m.ebitda.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '0,00'}
                                        </span>
                                      </td>

                                      {/* 4. RealValuation (Equity) — usa o EQUITY VALUE real do financeEngine
                                (múltiplo do setor + dívida deduzida), não mais um "x5" fixo pra todo mundo */}
                                      <td className="py-4 px-6 text-sm font-medium whitespace-nowrap">
                                        <span className={m.equityValue <= 0 ? "text-red-500" : "text-slate-300"}>
                                          R$ {m.equityValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                      </td>

                                      {/* 5. Risco (usa a mesma classificação de saúde do resto do sistema) */}
                                      <td className="py-4 px-6 text-center">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold border flex items-center justify-center gap-1.5 w-fit mx-auto ${saude.label === 'Saudável' ? 'text-lime-400 bg-lime-400/10 border-lime-500/20' :
                                          saude.label === 'Atenção' ? 'text-amber-400 bg-amber-400/10 border-amber-500/20' :
                                            saude.label === 'Falência Iminente' ? 'text-red-600 bg-red-600/10 border-red-600/20' :
                                              'text-red-500 bg-red-500/10 border-red-500/20'
                                          }`}>
                                          <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                                          {saude.label}
                                        </span>
                                      </td>

                                      {/* 6. Plano */}
                                      <td className="py-4 px-6 text-center">
                                        <span className={`px-4 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest border ${client.plan === 'ENTERPRISE' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' :
                                          client.plan === 'BOUTIQUE' ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' :
                                            client.plan === 'ADVISOR' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                                              'bg-lime-400/20 text-lime-400 border-lime-400/30'
                                          }`}>
                                          {client.plan}
                                        </span>
                                      </td>

                                      {/* 7. Ação */}
                                      <td className="py-4 px-6 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                          <button
                                            onClick={(e) => { e.stopPropagation(); abrirEdicaoCliente(client); }}
                                            className="w-8 h-8 rounded-full bg-white/5 text-slate-400 hover:bg-blue-500/20 hover:text-blue-400 flex items-center justify-center transition-colors"
                                            title="Editar dados"
                                          >
                                            <FileSignature size={13} />
                                          </button>
                                          <button className="text-slate-500 group-hover:text-lime-400 transition-colors">
                                            <ChevronRight size={24} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}

            {/* TAB 2: DIAGNÓSTICO DO CLIENTE */}
            {activeTab === 'client-overview' && selectedClient && metrics && (
              <div className="flex flex-col space-y-8 animate-in fade-in duration-500 mb-8">
                <div className="flex justify-between items-end border-b border-white/5 pb-6">
                  <div>
                    <h3 className="text-4xl font-black text-white uppercase tracking-tighter leading-none">{selectedClient.name}</h3>
                    <div className="flex items-center gap-2 mt-3">
                      <p className="text-xs text-slate-500 font-black uppercase tracking-[0.3em] italic">{selectedClient.niche} •</p>
                      {editandoCnpj ? (
                        <>
                          <input
                            type="text"
                            value={cnpjEditado}
                            onChange={(e) => setCnpjEditado(e.target.value)}
                            placeholder="00.000.000/0000-00"
                            autoFocus
                            className="bg-black/40 border border-lime-400/30 rounded-lg px-3 py-1 text-xs text-white outline-none w-48"
                          />
                          <button onClick={salvarCnpj} className="text-[11px] font-black text-lime-400 uppercase hover:text-lime-300">Salvar</button>
                          <button onClick={() => setEditandoCnpj(false)} className="text-[11px] font-black text-slate-500 uppercase hover:text-slate-300">Cancelar</button>
                        </>
                      ) : (
                        <button
                          onClick={() => { setCnpjEditado(selectedClient.cnpj === '00.000.000/0000-00' ? '' : selectedClient.cnpj); setEditandoCnpj(true); }}
                          className="text-xs text-slate-500 font-black uppercase tracking-[0.3em] italic hover:text-lime-400 transition-colors flex items-center gap-2"
                          title="Editar CNPJ"
                        >
                          {selectedClient.cnpj}
                          <span className="w-5 h-5 rounded-full bg-white/5 flex items-center justify-center border border-white/10">
                            <FileSignature size={10} />
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => abrirEdicaoCliente(selectedClient)} className="bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 px-6 py-4 rounded-full font-black uppercase text-xs tracking-widest transition-all border border-blue-500/20 flex items-center gap-2 no-print">
                      <FileSignature size={16} /> Editar Dados
                    </button>
                    <button
                      onClick={() => setConfirmandoDownload(true)}
                      disabled={exportando || atualizandoParecerIA}
                      className="bg-white/5 hover:bg-white/10 text-white px-8 py-4 rounded-full font-black uppercase text-xs tracking-widest transition-all border border-white/10 flex items-center gap-2 no-print disabled:opacity-50"
                    >
                      <Download size={16} /> {exportando ? 'Gerando PDF...' : (atualizandoParecerIA ? 'Atualizando parecer…' : 'Exportar Relatório')}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[
                    { label: "Faturamento Mensal", value: `R$ ${formatCurrency(selectedClient.revenue)}`, color: "#3b82f6", campo: "revenue", desc: "Volume de receita bruta auferida no período." },
                    { label: "Custos Totais", value: `R$ ${formatCurrency(selectedClient.costs)}`, color: "#ef4444", campo: "costs", desc: "Desembolsos operacionais estruturais acumulados." },
                    { label: "Dívida Bancária", value: `R$ ${formatCurrency(selectedClient.debt)}`, color: "#ef4444", campo: "debt", desc: "Exposição de alavancagem financeira passiva." },
                    { label: "EBITDA Real Time", value: `R$ ${formatCurrency(metrics.ebitda)}`, color: "#bef264", campo: "ebitda", desc: "Lucro operacional antes de juros, taxas e depreciação." },
                    { label: "Margem Operacional", value: `${String(metrics.currentMargin).replace('.', ',')}%`, color: tema === 'light' ? "#0f172a" : "#ffffff", campo: "currentMargin", desc: "Eficiência de conversão de receita em EBITDA." },
                    { label: "Alavancagem x EBITDA", value: `${String(metrics.alavancagem).replace('.', ',')}x`, color: tema === 'light' ? "#0f172a" : "#ffffff", campo: "alavancagem", desc: "Tempo de amortização da dívida líquida gerada." },
                    { label: "Múltiplo Setorial", value: `${metrics.multiple}x`, color: tema === 'light' ? "#0f172a" : "#ffffff", campo: null, desc: "Métrica homologada para o segmento ativo, fixa por setor." },
                    { label: "Equity Value", value: `R$ ${formatCurrency(metrics.equityValue)}`, color: "#bef264", campo: "equityValue", desc: "Patrimônio líquido de mercado atribuído aos sócios." }
                  ].map((card, i) => {
                    // 🔥 Sparkline agora usa o HISTÓRICO REAL do cliente (mesmo salvo
                    // pelo gráfico de Evolução abaixo) — antes cada card tinha uma
                    // curva inventada e fixa no código, igual pra qualquer empresa,
                    // o que era enganoso (parecia sempre "em alta").
                    const historicoValido = (selectedClient.historico || []).filter(h => card.campo && h[card.campo] !== undefined);
                    const temHistorico = historicoValido.length >= 2;
                    const pontos = temHistorico ? historicoValido.map(h => h[card.campo]) : null;

                    return (
                      <div key={i} className="bg-slate-900/40 p-8 rounded-[2rem] border border-white/5 shadow-lg flex flex-col justify-between group hover:border-white/10 transition-colors">
                        <div className="overflow-hidden w-full">
                          <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-2">{card.label}</p>

                          {/* 💡 CTO NOTE: Tamanho do texto adaptativo baseado no comprimento do valor financeiro */}
                          <p
                            className={`font-dashboard font-black italic tracking-tighter whitespace-nowrap transition-all duration-300 ${card.value.length > 18 ? 'text-lg lg:text-xl' :
                              card.value.length > 14 ? 'text-xl lg:text-2xl' :
                                'text-2xl lg:text-3xl'
                              }`}
                            style={{ color: card.color }}
                          >
                            {card.value}
                          </p>
                        </div>
                        {card.campo === null ? (
                          (() => {
                            // O múltiplo é fixo por setor (não tem histórico). Em vez de uma linha vazia, mostra ONDE ele
                            // fica na faixa de mercado usada pelo Phoenix OS (4x a 8,5x), com os extremos como referência.
                            const MIN_M = 4, MAX_M = 8.5;
                            const mult = Number(metrics.multiple) || 0;
                            const pct = Math.max(0, Math.min(100, ((mult - MIN_M) / (MAX_M - MIN_M)) * 100));
                            return (
                              <div className="w-full">
                                <div className="relative h-2 rounded-full bg-white/10 mt-3">
                                  <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-slate-500 to-purple-400" style={{ width: `${Math.max(pct, 4)}%` }}></div>
                                  <div className="absolute -top-1 w-4 h-4 rounded-full bg-white border-2 border-purple-400 shadow" style={{ left: `calc(${pct}% - 8px)` }}></div>
                                </div>
                                <div className="flex justify-between text-[11px] text-slate-500 font-bold uppercase tracking-widest mt-2">
                                  <span>{MIN_M}x · menor</span>
                                  <span className="text-purple-300">{selectedClient.sector}</span>
                                  <span>maior · {String(MAX_M).replace('.', ',')}x</span>
                                </div>
                              </div>
                            );
                          })()
                        ) : temHistorico ? (
                          <Sparkline data={pontos} color={card.color} />
                        ) : (
                          <div className="h-8 flex items-center">
                            <p className="text-[11px] text-slate-600 font-bold uppercase tracking-widest">— sem histórico suficiente ainda</p>
                          </div>
                        )}
                        <p className="text-[11px] text-slate-500 leading-relaxed border-t border-white/5 pt-3 italic mt-2">
                          {card.desc}
                          {temHistorico && <span className="block text-[11px] text-slate-600 not-italic font-bold uppercase tracking-widest mt-1">Últimos {historicoValido.length} registros salvos</span>}
                        </p>
                      </div>
                    );
                  })}
                </div>

                {/* PONTO DE EQUILÍBRIO E CAIXA MÍNIMO — informação primordial,
                  sempre calculada automaticamente (nunca fica atrás de plano).
                  Caixa Mínimo usa o pró-labore, campo coletado no cadastro
                  mas que até então não entrava em nenhum cálculo. */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8">
                    <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Ponto de Equilíbrio</p>
                    {checkAccess(3) ? (
                      <>
                        <p className="text-2xl font-black text-white tracking-tighter">R$ {formatCurrency(selectedClient.costs)}</p>
                        <p className="text-[11px] text-slate-500 leading-relaxed border-t border-white/5 pt-3 mt-3 italic">Faturamento mínimo pra cobrir os custos, sem lucro nem prejuízo.</p>
                      </>
                    ) : (
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-sm text-slate-600 font-bold">Disponível no Boutique Elite</p>
                        <Lock size={14} className="text-slate-500" />
                      </div>
                    )}
                  </div>
                  <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8">
                    <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Caixa Mínimo Mensal</p>
                    {checkAccess(3) ? (
                      <>
                        <p className="text-2xl font-black text-white tracking-tighter">R$ {formatCurrency((Number(selectedClient.costs) || 0) + (Number(selectedClient.proLabore) || 0))}</p>
                        <p className="text-[11px] text-slate-500 leading-relaxed border-t border-white/5 pt-3 mt-3 italic">Custos fixos somados ao pró-labore dos sócios.</p>
                      </>
                    ) : (
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-sm text-slate-600 font-bold">Disponível no Boutique Elite</p>
                        <Lock size={14} className="text-slate-500" />
                      </div>
                    )}
                  </div>
                  {/* RESERVA DE EMERGÊNCIA — completa a linha de 3, mesma família
                    de indicadores de "colchão de caixa" que Ponto de Equilíbrio
                    e Caixa Mínimo, evitando o card sobrando sozinho na grade. */}
                  <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8">
                    <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Reserva de Emergência</p>
                    {checkAccess(3) ? (
                      <>
                        <p className="text-2xl font-black text-white tracking-tighter">R$ {formatCurrency((Number(selectedClient.costs) || 0) * 6)}</p>
                        <p className="text-[11px] text-slate-500 leading-relaxed border-t border-white/5 pt-3 mt-3 italic">Caixa alvo pra suportar 6 meses sem faturar nada.</p>
                      </>
                    ) : (
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-sm text-slate-600 font-bold">Disponível no Boutique Elite</p>
                        <Lock size={14} className="text-slate-500" />
                      </div>
                    )}
                  </div>
                </div>

                {/* EVOLUÇÃO HISTÓRICA REAL — a cada edição do cliente, um retrato dos
                  números fica salvo. Diferente das sparklines dos cards acima
                  (que são só decorativas), isso é o valor de verdade ao longo do tempo. */}
                <div className="bg-[#020617] border border-white/5 p-10 rounded-[3rem] shadow-2xl">
                  <h4 className="text-sm font-black text-white uppercase tracking-[0.4em] italic leading-none mb-8 flex items-center gap-3">
                    <TrendingUp className="text-lime-400" size={20} /> Evolução Histórica
                  </h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                      <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-3">Equity Value (R$)</p>
                      <GraficoEvolucao historico={selectedClient.historico} campo="equityValue" cor="#bef264" formatador={formatCurrency} />
                    </div>
                    <div>
                      <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-3">EBITDA Ajustado (R$)</p>
                      <GraficoEvolucao historico={selectedClient.historico} campo="ebitda" cor="#3b82f6" formatador={formatCurrency} />
                    </div>
                  </div>
                </div>

                {/* ANOTAÇÕES / HISTÓRICO DO CLIENTE */}
                <div className="flex items-center justify-between">
                  <p className="text-xs font-black text-white uppercase tracking-widest">Acompanhamento</p>
                  <button
                    onClick={exportarBlocoDeNotas}
                    disabled={exportandoNotas}
                    className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400 hover:text-lime-400 transition-colors border border-white/10 px-4 py-2 rounded-full hover:bg-white/5 disabled:opacity-50"
                  >
                    <Download size={12} /> {exportandoNotas ? 'Gerando...' : 'Baixar Bloco de Notas'}
                  </button>
                </div>
                <div className="bg-[#020617] border border-white/5 p-10 rounded-[3rem] shadow-2xl">
                  <h4 className="text-sm font-black text-white uppercase tracking-[0.4em] italic leading-none mb-6 flex items-center gap-3">
                    <FileText className="text-lime-400" size={20} /> Anotações e Histórico
                  </h4>

                  <div className="flex gap-3 mb-8">
                    <textarea
                      value={novaNota}
                      onChange={(e) => setNovaNota(e.target.value)}
                      placeholder="Registrar ligação, decisão tomada, próximo passo combinado com o cliente..."
                      rows={2}
                      className="flex-1 bg-black/40 border border-white/5 rounded-2xl px-5 py-4 text-sm text-white outline-none focus:border-lime-400/50 transition-all placeholder:text-slate-600 resize-none"
                    />
                    <button
                      onClick={adicionarNota}
                      disabled={salvandoNota || !novaNota.trim()}
                      className="bg-lime-400 text-black px-6 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-lime-500 active:scale-95 transition-all disabled:opacity-30 disabled:pointer-events-none shrink-0"
                    >
                      {salvandoNota ? '...' : 'Adicionar'}
                    </button>
                  </div>

                  {notas.length === 0 ? (
                    <p className="text-xs text-slate-600 font-bold uppercase tracking-widest text-center py-6">Nenhuma anotação registrada ainda.</p>
                  ) : (
                    <div className="space-y-4 max-h-80 overflow-y-auto custom-scroll pr-2">
                      {notas.map((nota, i) => (
                        <div key={i} className="border-l-2 border-lime-400/20 pl-5 py-1 group/nota">
                          {notaEditandoIndex === i ? (
                            <div className="flex gap-2">
                              <textarea
                                value={textoEdicao}
                                onChange={(e) => setTextoEdicao(e.target.value)}
                                rows={2}
                                autoFocus
                                className="flex-1 bg-black/40 border border-lime-400/30 rounded-xl px-4 py-2 text-sm text-white outline-none resize-none"
                              />
                              <div className="flex flex-col gap-1.5 shrink-0">
                                <button
                                  onClick={() => { editarNota(i, textoEdicao); setNotaEditandoIndex(null); }}
                                  className="bg-lime-400 text-black px-3 py-1.5 rounded-lg text-[11px] font-black uppercase hover:bg-lime-500 transition-all"
                                >
                                  Salvar
                                </button>
                                <button
                                  onClick={() => setNotaEditandoIndex(null)}
                                  className="bg-white/5 text-slate-400 px-3 py-1.5 rounded-lg text-[11px] font-black uppercase hover:bg-white/10 transition-all"
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-sm text-slate-300 leading-relaxed">{nota.texto}</p>
                                <p className="text-[11px] text-slate-600 font-bold uppercase tracking-widest mt-1.5">
                                  {new Date(nota.data).toLocaleString('pt-BR')} • {nota.autor}
                                  {nota.editadoEm && <span className="italic text-slate-700"> • editado</span>}
                                </p>
                              </div>
                              <div className="flex gap-1.5 opacity-0 group-hover/nota:opacity-100 transition-opacity shrink-0">
                                <button
                                  onClick={() => { setNotaEditandoIndex(i); setTextoEdicao(nota.texto); }}
                                  className="w-8 h-8 rounded-full bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white flex items-center justify-center transition-colors"
                                  title="Editar"
                                >
                                  <FileSignature size={13} />
                                </button>
                                <button
                                  onClick={() => setNotaParaExcluir(i)}
                                  className="w-8 h-8 rounded-full bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center transition-colors"
                                  title="Excluir"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 🏢 MATRIZ & FILIAIS — só aparece quando o cliente aberto
                    realmente faz parte de um grupo (outras unidades com a
                    mesma raiz de CNPJ na carteira). Detectado automaticamente
                    pelo CNPJ, sem o Consultor precisar cadastrar vínculo. */}
                {(() => {
                  const irmas = irmasDoMesmoGrupo(selectedClient, clients);
                  if (irmas.length === 0) return null;
                  const grupoCompleto = [selectedClient, ...irmas].sort((a, b) =>
                    String(a.cnpj).localeCompare(String(b.cnpj))
                  );
                  const consolidado = consolidarGrupo(grupoCompleto, calculateClientMetrics);
                  const maiorReceita = Math.max(...grupoCompleto.map(u => Number(u.revenue) || 0), 1);

                  return (
                    <div className="bg-gradient-to-br from-blue-900/20 to-transparent border border-blue-500/20 rounded-[2rem] p-8">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 size={16} className="text-blue-400" />
                        <p className="text-sm font-black text-blue-400 uppercase tracking-widest">Grupo Econômico</p>
                        <span className="ml-auto text-[11px] font-black uppercase tracking-widest bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full border border-blue-500/30">
                          {grupoCompleto.length} unidades
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 mb-6">
                        Detectado pela raiz do CNPJ ({formatarCNPJ(selectedClient.cnpj).slice(0, 10)}) — matriz e filiais da mesma empresa.
                      </p>

                      {/* CONSOLIDADO DO GRUPO */}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                        <div className="bg-black/20 rounded-2xl p-4 border border-white/5">
                          <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">Receita do Grupo</p>
                          <p className="text-base font-black text-white">R$ {formatCurrency(consolidado.revenue)}</p>
                        </div>
                        <div className="bg-black/20 rounded-2xl p-4 border border-white/5">
                          <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">EBITDA do Grupo</p>
                          <p className={`text-base font-black ${consolidado.ebitda >= 0 ? 'text-lime-400' : 'text-red-400'}`}>R$ {formatCurrency(consolidado.ebitda)}</p>
                        </div>
                        <div className="bg-black/20 rounded-2xl p-4 border border-white/5">
                          <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">Margem Consolidada</p>
                          <p className={`text-base font-black ${consolidado.margemConsolidada >= 0 ? 'text-lime-400' : 'text-red-400'}`}>{consolidado.margemConsolidada.toFixed(1)}%</p>
                        </div>
                        <div className="bg-black/20 rounded-2xl p-4 border border-white/5">
                          <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">Dívida do Grupo</p>
                          <p className="text-base font-black text-orange-400">R$ {formatCurrency(consolidado.debt)}</p>
                        </div>
                      </div>

                      {/* COMPARATIVO ENTRE UNIDADES */}
                      <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Comparativo entre Unidades</p>
                      <div className="space-y-2.5">
                        {grupoCompleto.map((unidade) => {
                          const m = calculateClientMetrics(unidade);
                          const receita = Number(unidade.revenue) || 0;
                          const larguraBarra = (receita / maiorReceita) * 100;
                          const ehAtual = unidade.id === selectedClient.id;
                          const margem = m?.currentMargin ?? 0;
                          return (
                            <button
                              key={unidade.id}
                              onClick={() => !ehAtual && setSelectedClient(unidade)}
                              className={`w-full text-left rounded-2xl p-4 border transition-all ${ehAtual ? 'bg-blue-500/10 border-blue-500/40' : 'bg-black/20 border-white/5 hover:border-blue-500/30'}`}
                            >
                              <div className="flex items-center justify-between mb-2 gap-3">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className={`text-[11px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 ${ehMatriz(unidade.cnpj) ? 'bg-lime-400/15 text-lime-400 border border-lime-400/30' : 'bg-white/5 text-slate-400 border border-white/10'}`}>
                                    {rotuloUnidade(unidade.cnpj)}
                                  </span>
                                  <p className="text-xs font-black text-white truncate">{unidade.name}</p>
                                  {ehAtual && <span className="text-[11px] font-black text-blue-300 uppercase shrink-0">• vendo agora</span>}
                                </div>
                                <div className="flex items-center gap-4 shrink-0">
                                  <p className="text-xs font-black text-white">R$ {formatCurrency(receita)}</p>
                                  <p className={`text-[11px] font-black w-14 text-right ${margem >= 0 ? 'text-lime-400' : 'text-red-400'}`}>{Number(margem).toFixed(1)}%</p>
                                </div>
                              </div>
                              <div className="h-1.5 bg-black/40 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${ehAtual ? 'bg-blue-400' : 'bg-slate-600'}`}
                                  style={{ width: `${larguraBarra}%` }}
                                />
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[11px] text-slate-600 mt-4">
                        Barra proporcional à receita. Clique numa unidade para abrir o diagnóstico dela.
                      </p>
                    </div>
                  );
                })()}

                {/* PARECER DA IA (presente) */}
                <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles size={16} className="text-lime-400" />
                    <p className="text-sm font-black text-lime-400 uppercase tracking-widest">Parecer da IA</p>
                  </div>
                  {selectedClient.parecerIA ? (
                    <div className="bg-black/20 border border-white/5 rounded-2xl p-6">
                      {formatarParecerIA(selectedClient.parecerIA)}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 text-center py-8">
                      Salve o diagnóstico desse cliente no app pra gerar o parecer.
                    </p>
                  )}
                </div>

                {/* PROJEÇÃO ESTOCÁSTICA EM BLOCOS — Consultor só tem
                    Médio (Boutique Elite) e Longo (Enterprise Ultra).
                    Não existe "Curto Prazo" pro Consultor. */}
                <div className="bg-gradient-to-br from-purple-900/20 to-transparent border border-purple-500/20 p-8 rounded-[2rem] relative">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap size={16} className="text-purple-400" />
                    <p className="text-sm font-black text-purple-400 uppercase tracking-widest">Projeção por IA Estocástica</p>
                    <span className="ml-auto text-[11px] font-black uppercase tracking-widest bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full border border-purple-500/30">
                      {checkAccess(4) ? 'Enterprise Ultra' : checkAccess(3) ? 'Boutique Elite' : 'Advisor PRO'}
                    </span>
                  </div>
                  <p className="text-sm font-black text-purple-300 italic mb-2">Pra onde esse cliente está indo?</p>
                  <p className="text-[11px] text-slate-500 mb-5">Não é só um retrato de hoje — é pra onde a empresa está indo, em 3 cenários.</p>

                  <div className="space-y-4">
                    {[
                      { chave: 'curto', titulo: 'Curto Prazo', meses: [3, 6], liberado: checkAccess(2), texto: selectedClient.leituraProjecaoCurto, nomePlano: 'Advisor PRO', idPlano: 'advisor' },
                      { chave: 'medio', titulo: 'Médio Prazo', meses: [12], liberado: checkAccess(3), texto: selectedClient.leituraProjecaoMedio, nomePlano: 'Boutique Elite', idPlano: 'boutique' },
                      { chave: 'longo', titulo: 'Longo Prazo', meses: [24, 36], liberado: checkAccess(4), texto: selectedClient.leituraProjecaoLongo, nomePlano: 'Enterprise Ultra', idPlano: 'enterprise' },
                    ].map((grupo) => {
                      if (!grupo.liberado) {
                        return (
                          <button
                            key={grupo.chave}
                            onClick={() => abrirCheckout(grupo.idPlano)}
                            className="w-full flex items-center justify-between bg-black/20 border border-white/5 rounded-2xl px-5 py-4 hover:border-purple-500/30 transition-all"
                          >
                            <p className="text-[11px] font-black text-slate-600 uppercase tracking-widest flex items-center gap-1.5">
                              <Lock size={11} /> {grupo.titulo} ({grupo.meses.join(' e ')} meses)
                            </p>
                            <p className="text-[11px] font-black text-purple-300 uppercase tracking-widest">Desbloquear no {grupo.nomePlano}</p>
                          </button>
                        );
                      }
                      return (
                        <div key={grupo.chave} className="bg-purple-500/5 border border-purple-500/20 rounded-2xl p-5">
                          <p className="text-[11px] font-black text-purple-300 uppercase tracking-widest mb-4">{grupo.titulo} ({grupo.meses.join(' e ')} meses)</p>
                          <div className="space-y-3">
                            {grupo.meses.map((h) => {
                              const dado = projecaoPorPrazo.find(p => p.months === h);
                              if (!dado) return null;
                              return (
                                <div key={h} className="bg-black/20 rounded-xl p-4">
                                  <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-3 text-center">{h} meses</p>
                                  {/* 🔥 CORRIGIDO: valores em bilhões encostavam uns nos
                                      outros. Agora cada cenário tem seu próprio painel,
                                      com espaçamento maior e o número quebrando pra
                                      baixo em vez de invadir a coluna vizinha. */}
                                  <div className="grid grid-cols-3 gap-4 text-center">
                                    <div className="bg-white/[0.03] rounded-lg px-2 py-2.5">
                                      <p className="text-[11px] text-slate-500 uppercase font-bold tracking-wide mb-1">Pessimista</p>
                                      <p className="text-[13px] leading-tight font-black text-red-400 break-words">R$ {formatCurrency(dado.pessimista)}</p>
                                    </div>
                                    <div className="bg-white/[0.03] rounded-lg px-2 py-2.5">
                                      <p className="text-[11px] text-slate-500 uppercase font-bold tracking-wide mb-1">Provável</p>
                                      <p className="text-[13px] leading-tight font-black text-white break-words">R$ {formatCurrency(dado.provavel)}</p>
                                    </div>
                                    <div className="bg-white/[0.03] rounded-lg px-2 py-2.5">
                                      <p className="text-[11px] text-slate-500 uppercase font-bold tracking-wide mb-1">Otimista</p>
                                      <p className="text-[13px] leading-tight font-black text-lime-400 break-words">R$ {formatCurrency(dado.otimista)}</p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {grupo.texto ? (
                            <div className="mt-4 pt-4 border-t border-white/10">
                              {formatarParecerIA(grupo.texto)}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/10">
                              <div className="w-3 h-3 border-2 border-purple-400 border-t-transparent rounded-full animate-spin shrink-0" />
                              <p className="text-[11px] text-slate-500 italic">Gerando a análise desse prazo com IA...</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-slate-600 uppercase tracking-widest font-bold mt-5 flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-purple-400" /> Previsão gerada por IA
                  </p>
                </div>

                {/* OPORTUNIDADES ESTRATÉGICAS — exclusivo Enterprise Ultra */}
                {checkAccess(4) && selectedClient.oportunidadesIA && (
                  <div className="bg-gradient-to-br from-amber-900/10 to-transparent border border-amber-500/20 p-8 rounded-[2rem]">
                    <div className="flex items-center gap-2 mb-4">
                      <Crown size={16} className="text-amber-400" />
                      <p className="text-sm font-black text-amber-400 uppercase tracking-widest">Oportunidades Estratégicas</p>
                    </div>
                    <div className="bg-black/20 border border-amber-500/10 rounded-2xl p-6">
                      {formatarParecerIA(selectedClient.oportunidadesIA)}
                    </div>
                  </div>
                )}

                {/* TAREFAS / LEMBRETES DO CLIENTE */}
                <div className="bg-[#020617] border border-white/5 p-10 rounded-[3rem] shadow-2xl">
                  <h4 className="text-sm font-black text-white uppercase tracking-[0.4em] italic leading-none mb-6 flex items-center gap-3">
                    <CheckCircle2 className="text-lime-400" size={20} /> Tarefas e Lembretes
                  </h4>

                  <div className="flex flex-col sm:flex-row gap-3 mb-8">
                    <input
                      type="text"
                      value={novaTarefaTexto}
                      onChange={(e) => setNovaTarefaTexto(e.target.value)}
                      placeholder="Ex: Enviar minuta do NDA para assinatura..."
                      className="flex-1 bg-black/40 border border-white/5 rounded-2xl px-5 py-4 text-sm text-white outline-none focus:border-lime-400/50 transition-all placeholder:text-slate-600"
                    />
                    <input
                      type="date"
                      value={novaTarefaPrazo}
                      onChange={(e) => setNovaTarefaPrazo(e.target.value)}
                      className="bg-black/40 border border-white/5 rounded-2xl px-4 py-4 text-sm text-slate-300 outline-none focus:border-lime-400/50 transition-all sm:w-48"
                    />
                    <button
                      onClick={adicionarTarefa}
                      disabled={salvandoTarefa || !novaTarefaTexto.trim()}
                      className="bg-lime-400 text-black px-6 py-4 sm:py-0 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-lime-500 active:scale-95 transition-all disabled:opacity-30 disabled:pointer-events-none shrink-0"
                    >
                      {salvandoTarefa ? '...' : 'Adicionar'}
                    </button>
                  </div>

                  {tarefas.length === 0 ? (
                    <p className="text-xs text-slate-600 font-bold uppercase tracking-widest text-center py-6">Nenhuma tarefa registrada ainda.</p>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto custom-scroll pr-2">
                      {[...tarefas].sort((a, b) => {
                        if (a.concluida !== b.concluida) return a.concluida ? 1 : -1; // concluídas vão pro fim
                        if (!a.prazo) return 1;
                        if (!b.prazo) return -1;
                        return new Date(a.prazo) - new Date(b.prazo);
                      }).map((tarefa) => {
                        const hoje = new Date().toISOString().slice(0, 10);
                        const atrasada = tarefa.prazo && tarefa.prazo < hoje && !tarefa.concluida;
                        return (
                          <div key={tarefa.id} className={`flex items-center gap-4 p-4 rounded-2xl border transition-all group/tarefa ${tarefa.concluida ? 'bg-white/[0.02] border-white/5 opacity-50'
                            : atrasada ? 'bg-red-500/5 border-red-500/20'
                              : 'bg-white/5 border-white/5'
                            }`}>
                            {tarefaEditandoId === tarefa.id ? (
                              <div className="flex-1 flex flex-col sm:flex-row gap-2">
                                <input
                                  type="text"
                                  value={textoEdicaoTarefa}
                                  onChange={(e) => setTextoEdicaoTarefa(e.target.value)}
                                  autoFocus
                                  className="flex-1 bg-black/40 border border-lime-400/30 rounded-xl px-4 py-2 text-sm text-white outline-none"
                                />
                                <input
                                  type="date"
                                  value={prazoEdicaoTarefa}
                                  onChange={(e) => setPrazoEdicaoTarefa(e.target.value)}
                                  className="bg-black/40 border border-lime-400/30 rounded-xl px-3 py-2 text-sm text-slate-300 outline-none sm:w-40"
                                />
                                <div className="flex gap-1.5 shrink-0">
                                  <button
                                    onClick={() => { editarTarefa(tarefa.id, textoEdicaoTarefa, prazoEdicaoTarefa); setTarefaEditandoId(null); }}
                                    className="bg-lime-400 text-black px-3 py-2 rounded-lg text-[11px] font-black uppercase hover:bg-lime-500 transition-all"
                                  >
                                    Salvar
                                  </button>
                                  <button
                                    onClick={() => setTarefaEditandoId(null)}
                                    className="bg-white/5 text-slate-400 px-3 py-2 rounded-lg text-[11px] font-black uppercase hover:bg-white/10 transition-all"
                                  >
                                    Cancelar
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <button
                                  onClick={() => alternarTarefaConcluida(tarefa.id)}
                                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${tarefa.concluida ? 'bg-lime-400 border-lime-400' : 'border-slate-600 hover:border-lime-400'
                                    }`}
                                >
                                  {tarefa.concluida && <CheckCircle2 size={14} className="text-black" />}
                                </button>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-bold truncate ${tarefa.concluida ? 'text-slate-500 line-through' : 'text-slate-200'}`}>{tarefa.texto}</p>
                                  {tarefa.prazo && (
                                    <p className={`text-[11px] font-black uppercase tracking-widest mt-1 ${atrasada ? 'text-red-400' : 'text-slate-600'}`}>
                                      {atrasada ? '⚠ Atrasada • ' : 'Prazo: '}{new Date(tarefa.prazo + 'T00:00:00').toLocaleDateString('pt-BR')}
                                    </p>
                                  )}
                                </div>
                                <div className="flex gap-1.5 opacity-0 group-hover/tarefa:opacity-100 transition-opacity shrink-0">
                                  <button
                                    onClick={() => { setTarefaEditandoId(tarefa.id); setTextoEdicaoTarefa(tarefa.texto); setPrazoEdicaoTarefa(tarefa.prazo || ''); }}
                                    className="w-8 h-8 rounded-full bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white flex items-center justify-center transition-colors"
                                    title="Editar"
                                  >
                                    <FileSignature size={13} />
                                  </button>
                                  <button
                                    onClick={() => setTarefaParaExcluir(tarefa.id)}
                                    className="w-8 h-8 rounded-full bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center transition-colors"
                                    title="Excluir"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TAB 3: MESA DE OPERAÇÕES */}
            {activeTab === 'mesa-operacoes' && selectedClient && metrics && (
              <div className="relative flex flex-col mb-8">
                {!checkAccess(2) && renderPaywall('Simulador Avançado', 'Advisor PRO')}
                <div className={`flex-1 flex flex-col justify-center ${!checkAccess(2) ? 'blur-md pointer-events-none select-none' : 'animate-in fade-in duration-500'}`}>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                    <div className="bg-[#020617] p-10 rounded-[2rem] border border-white/5 shadow-2xl flex flex-col justify-between relative overflow-hidden group">
                      <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none"><Sliders size={120} className="text-white" /></div>
                      <div className="flex items-center gap-3 pb-2 shrink-0 relative z-10">
                        <Sliders className="text-lime-400" size={24} />
                        <h3 className="text-lg font-black text-white uppercase italic tracking-tighter">Painel de Simulação</h3>
                      </div>
                      <div className="space-y-10 my-8 relative z-10">
                        <div className="space-y-4">
                          <div className="flex justify-between items-center text-xs font-black uppercase tracking-widest italic">
                            <span className="text-slate-400">Expansão de Receita</span>
                            <span className="text-lime-400">+{simRevenue}%</span>
                          </div>
                          <input type="range" min="0" max="50" value={simRevenue} onChange={(e) => setSimRevenue(e.target.value)} className="w-full accent-lime-400 h-2 bg-slate-800 rounded-full appearance-none cursor-pointer" />
                          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest flex items-center gap-1.5">
                            <ArrowLeft size={10} className="rotate-180 animate-pulse" /> Arraste a barra pra simular
                          </p>
                        </div>
                        <div className="space-y-4">
                          <div className="flex justify-between items-center text-xs font-black uppercase tracking-widest italic">
                            <span className="text-slate-400">Corte de OPEX</span>
                            <span className="text-lime-400">-{simCosts}%</span>
                          </div>
                          <input type="range" min="0" max="30" value={simCosts} onChange={(e) => setSimCosts(e.target.value)} className="w-full accent-lime-400 h-2 bg-slate-800 rounded-full appearance-none cursor-pointer" />
                          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest flex items-center gap-1.5">
                            <ArrowLeft size={10} className="rotate-180 animate-pulse" /> Arraste a barra pra simular
                          </p>
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-600 uppercase tracking-widest font-bold text-center mt-auto relative z-10 pt-4 border-t border-white/5">
                        Projeção com IA disponível no <span className="text-lime-400">Diagnóstico Executivo</span>
                      </p>
                    </div>

                    <div className="bg-slate-900/40 p-10 rounded-[2rem] border border-white/5 shadow-lg flex flex-col justify-between relative overflow-hidden group">
                      <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none"><Activity size={120} className="text-blue-400" /></div>

                      <div className="relative z-10 w-full">
                        <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-2">EBITDA Preditivo Target</p>
                        <p className="text-3xl xl:text-4xl font-black text-white tracking-tighter whitespace-nowrap">R$ {formatCurrency(metrics.projEbitda)}</p>
                      </div>

                      {/* ENVOLTÓRIO LIMPO - SEM ALTURAS FIXAS OU OVERFLOWS CORTANDO */}
                      <div className="relative z-10 w-full mt-4">
                        <Sparkline data={[metrics.ebitda * 0.9, metrics.ebitda, metrics.projEbitda]} color="#3b82f6" />
                      </div>

                      <p className="text-xs text-slate-500 border-t border-white/5 pt-4 italic leading-relaxed mt-4 relative z-10">
                        Aumento imediato projetado de <span className="text-lime-400 font-bold whitespace-nowrap">+ R$ {formatCurrency(metrics.projAnualStrFormat || metrics.projAnualizedGains)}</span> no caixa anual corporativo.
                      </p>
                    </div>

                    <div className="bg-lime-400/5 p-10 rounded-[2rem] border border-lime-400/20 shadow-lg flex flex-col justify-between relative overflow-hidden group">
                      <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none"><TrendingUp size={120} className="text-lime-400" /></div>
                      <div className="relative z-10 overflow-hidden w-full">
                        <p className="text-xs font-black text-lime-500 uppercase tracking-widest mb-2">Valuation Projetado (Equity)</p>
                        <p className="text-3xl xl:text-4xl font-black text-lime-400 tracking-tighter whitespace-nowrap drop-shadow-[0_0_15px_rgba(190,242,100,0.3)]">R$ {formatCurrency(metrics.projEquity)}</p>
                      </div>
                      <div className="relative z-10">
                        <Sparkline data={[metrics.enterpriseValue * 0.85, metrics.enterpriseValue, metrics.projValuation]} color="#bef264" />
                      </div>
                      <p className="text-xs text-lime-500/70 border-t border-lime-400/10 pt-4 italic leading-relaxed mt-4 relative z-10">
                        A quebra de ineficiências destrava um ganho real estimado em <span className="text-white font-bold whitespace-nowrap">R$ {formatCurrency(metrics.valGains)}</span>.
                      </p>
                    </div>

                  </div>

                  {/* Antes x Depois + Tabela de Sensibilidade Combinada */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                    <div className="bg-slate-900/40 p-8 rounded-[2rem] border border-white/5 shadow-lg">
                      <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-6">Antes × Depois da Simulação</p>
                      <div className="space-y-6">
                        {[
                          { label: 'EBITDA', atual: metrics.ebitdaAjustado, projetado: metrics.projEbitda, cor: 'bg-blue-500' },
                          { label: 'Valuation (Equity)', atual: metrics.equityValue, projetado: metrics.projEquity, cor: 'bg-lime-400' },
                        ].map((item) => {
                          const maior = Math.max(item.atual, item.projetado, 1);
                          return (
                            <div key={item.label}>
                              <div className="flex justify-between text-xs font-black uppercase tracking-widest text-slate-400 mb-2">
                                <span>{item.label}</span>
                              </div>
                              <div className="space-y-1.5">
                                <div className="flex items-center gap-3">
                                  <span className="text-[11px] text-slate-500 uppercase font-bold w-16 shrink-0">Atual</span>
                                  <div className="flex-1 bg-white/5 rounded-full h-3 overflow-hidden"><div className="h-full bg-slate-600 rounded-full" style={{ width: `${(item.atual / maior) * 100}%` }}></div></div>
                                  <span className="text-[11px] text-slate-300 font-bold w-32 text-right shrink-0">R$ {formatCurrency(item.atual)}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-[11px] text-slate-500 uppercase font-bold w-16 shrink-0">Depois</span>
                                  <div className="flex-1 bg-white/5 rounded-full h-3 overflow-hidden"><div className={`h-full ${item.cor} rounded-full`} style={{ width: `${(item.projetado / maior) * 100}%` }}></div></div>
                                  <span className="text-[11px] text-white font-bold w-32 text-right shrink-0">R$ {formatCurrency(item.projetado)}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="bg-slate-900/40 p-8 rounded-[2rem] border border-white/5 shadow-lg">
                      <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-6">Sensibilidade Combinada (Receita + OPEX juntos)</p>
                      <table className="w-full text-left">
                        <thead>
                          <tr className="text-[11px] text-slate-600 uppercase font-black tracking-widest border-b border-white/5">
                            <th className="pb-3">Métrica</th>
                            <th className="pb-3 text-right">Atual</th>
                            <th className="pb-3 text-right">Projetado</th>
                            <th className="pb-3 text-right">Variação</th>
                          </tr>
                        </thead>
                        <tbody className="text-xs">
                          {[
                            { label: 'EBITDA', atual: metrics.ebitdaAjustado, projetado: metrics.projEbitda },
                            { label: 'Valuation', atual: metrics.equityValue, projetado: metrics.projEquity },
                            { label: 'Margem', atual: metrics.currentMargin, projetado: metrics.projMargin, isPct: true },
                          ].map((row) => {
                            const variacao = row.atual !== 0 ? ((row.projetado - row.atual) / Math.abs(row.atual)) * 100 : 0;
                            return (
                              <tr key={row.label} className="border-b border-white/5">
                                <td className="py-3 font-bold text-slate-300">{row.label}</td>
                                <td className="py-3 text-right text-slate-400">{row.isPct ? `${row.atual.toFixed(1)}%` : `R$ ${formatCurrency(row.atual)}`}</td>
                                <td className="py-3 text-right text-white font-bold">{row.isPct ? `${row.projetado.toFixed(1)}%` : `R$ ${formatCurrency(row.projetado)}`}</td>
                                <td className={`py-3 text-right font-black ${variacao >= 0 ? 'text-lime-400' : 'text-red-400'}`}>{variacao >= 0 ? '+' : ''}{variacao.toFixed(1)}%</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Projeção IA Estocástica — libera horizontes conforme o PLANO DO CLIENTE, igual no app */}
                  <div className="bg-gradient-to-br from-purple-900/10 to-transparent border border-purple-500/20 p-8 rounded-[2rem] mt-8">
                    <p className="text-xs font-black text-purple-400 uppercase tracking-widest mb-1">🔮 Projeção por IA Estocástica</p>
                    <p className="text-[11px] text-slate-500 mb-6">Não é só um retrato de hoje — é pra onde a empresa está indo, nos horizontes liberados pelo plano de {selectedClient.name}.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                      {calculateStochasticProjection(metrics).filter(h => getHorizontesLiberados(selectedClient.plan).includes(h.months)).map((h) => (
                        <div key={h.months} className="bg-black/30 border border-purple-500/10 rounded-2xl p-5">
                          <p className="text-xs font-black text-white uppercase tracking-widest mb-4">{h.months} meses</p>
                          <div className="space-y-2 text-xs">
                            <div className="flex justify-between"><span className="text-red-400 font-bold uppercase text-[11px]">Pessimista</span><span className="text-red-300 font-bold">R$ {formatCurrency(h.pessimista)}</span></div>
                            <div className="flex justify-between"><span className="text-slate-300 font-bold uppercase text-[11px]">Provável</span><span className="text-white font-bold">R$ {formatCurrency(h.provavel)}</span></div>
                            <div className="flex justify-between"><span className="text-lime-400 font-bold uppercase text-[11px]">Otimista</span><span className="text-lime-300 font-bold">R$ {formatCurrency(h.otimista)}</span></div>
                          </div>
                        </div>
                      ))}
                      {getHorizontesLiberados(selectedClient.plan).length === 0 && (
                        <p className="text-xs text-slate-500 italic col-span-full">Nenhum horizonte de projeção liberado no plano atual deste cliente.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 4: CFO VIRTUAL */}
            {activeTab === 'cfo-virtual' && selectedClient && metrics && (() => {
              // 🔥 APEX real — mesmo princípio do app mobile: sliders de Múltiplo e WACC
              // com referência do setor + Matriz de Sensibilidade 3x3. Isso substitui
              // o antigo conteúdo "Saque Seguro / Reserva de Emergência", que eram
              // conceitos do PERFIL GESTOR (retirada de sócio) e não fazem sentido
              // pro Consultor analisando um cliente terceiro.
              const baseEbitda = metrics.ebitdaAjustado > 0 ? metrics.ebitdaAjustado : 0;
              const apexMultiple = apexOverrides[selectedClient.id]?.multiple ?? metrics.multiple;
              const apexWacc = apexOverrides[selectedClient.id]?.wacc ?? 12.5;
              const setApexMultiple = (v) => setApexOverrides(prev => ({ ...prev, [selectedClient.id]: { ...prev[selectedClient.id], multiple: v } }));
              const setApexWacc = (v) => setApexOverrides(prev => ({ ...prev, [selectedClient.id]: { ...prev[selectedClient.id], wacc: v } }));

              const equityValueSimulado = Math.max(0, (baseEbitda * apexMultiple * 12) * (1 - (apexWacc - 12.5) / 100) - selectedClient.debt);
              const desvioMultiplo = metrics.multiple ? ((apexMultiple - metrics.multiple) / metrics.multiple) * 100 : 0;
              const desvioWacc = ((apexWacc - 12.5) / 12.5) * 100;

              return (
                <div className="relative flex flex-col mb-8">
                  {!checkAccess(3) && renderPaywall('APEX', 'Boutique Elite')}

                  <div className={`flex flex-col gap-8 ${!checkAccess(3) ? 'blur-md pointer-events-none select-none' : 'animate-in fade-in duration-500'}`}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* SLIDER MÚLTIPLO */}
                      <div className="bg-[#020617] p-8 rounded-[2rem] border border-white/5 shadow-lg">
                        <div className="flex justify-between items-center mb-2">
                          <p className="text-xs font-black text-white uppercase tracking-widest">Múltiplo de EBITDA Setorial</p>
                          <span className="text-sm font-mono font-black text-lime-400 bg-lime-400/10 px-3 py-1 rounded-lg">{apexMultiple.toFixed(1)}x</span>
                        </div>
                        <input type="range" min={3} max={15} step={0.1} value={apexMultiple}
                          onChange={(e) => setApexMultiple(parseFloat(e.target.value))}
                          className="w-full accent-lime-400 cursor-pointer my-4" />
                        <div className="flex justify-between items-center pt-3 border-t border-white/5">
                          <span className="text-[11px] text-slate-500 uppercase tracking-wider">Referência do Setor: <strong className="text-slate-300">{metrics.multiple.toFixed(1)}x</strong></span>
                          <span className={`text-[11px] font-bold ${desvioMultiplo > 0.5 ? 'text-lime-400' : desvioMultiplo < -0.5 ? 'text-red-400' : 'text-slate-400'}`}>{desvioMultiplo > 0 ? '+' : ''}{desvioMultiplo.toFixed(1)}% vs. ref.</span>
                        </div>
                      </div>

                      {/* SLIDER WACC */}
                      <div className="bg-[#020617] p-8 rounded-[2rem] border border-white/5 shadow-lg">
                        <div className="flex justify-between items-center mb-2">
                          <p className="text-xs font-black text-white uppercase tracking-widest">Taxa Cost of Capital / WACC</p>
                          <span className="text-sm font-mono font-black text-lime-400 bg-lime-400/10 px-3 py-1 rounded-lg">{apexWacc}%</span>
                        </div>
                        <input type="range" min={8} max={25} step={0.5} value={apexWacc}
                          onChange={(e) => setApexWacc(parseFloat(e.target.value))}
                          className="w-full accent-lime-400 cursor-pointer my-4" />
                        <div className="flex justify-between items-center pt-3 border-t border-white/5">
                          <span className="text-[11px] text-slate-500 uppercase tracking-wider">WACC Neutro (base): <strong className="text-slate-300">12.5%</strong></span>
                          <span className={`text-[11px] font-bold ${desvioWacc > 0.5 ? 'text-lime-400' : desvioWacc < -0.5 ? 'text-red-400' : 'text-slate-400'}`}>{desvioWacc > 0 ? '+' : ''}{desvioWacc.toFixed(1)}% vs. ref.</span>
                        </div>
                      </div>
                    </div>

                    {/* RESULTADO DA SIMULAÇÃO */}
                    <div className="bg-lime-400/5 p-8 rounded-[2rem] border border-lime-400/20 shadow-lg">
                      <p className="text-xs font-black text-lime-500 uppercase tracking-widest mb-2">Equity Value Simulado</p>
                      <div className="flex flex-col lg:flex-row lg:items-baseline gap-2 lg:gap-6">
                        <p className="text-3xl xl:text-4xl font-black text-lime-400 tracking-tighter drop-shadow-[0_0_15px_rgba(190,242,100,0.3)] whitespace-nowrap shrink-0">R$ {formatCurrency(equityValueSimulado)}</p>
                        <p className="text-xs text-lime-500/70 italic leading-relaxed lg:border-l lg:border-lime-400/20 lg:pl-6">Cenário atual: {apexMultiple.toFixed(1)}x de múltiplo com WACC de {apexWacc}%. Ajuste os sliders acima pra simular outros cenários de negociação.</p>
                      </div>
                    </div>

                    {equityValueSimulado === 0 && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-5 flex items-start gap-3">
                        <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5" />
                        <p className="text-xs text-red-300/80 leading-relaxed">
                          <strong className="text-red-400">⚠️ Atenção:</strong> O valuation está zerado porque o valor da dívida (R$ {formatCurrency(selectedClient.debt)}) é igual ou superior ao Enterprise Value calculado. Isso indica que a empresa está <strong className="text-red-400">tecnicamente insolvente</strong> — o passivo oneroso consome todo o patrimônio. Por isso a Matriz de Sensibilidade abaixo também aparece zerada em todos os cenários: nenhuma combinação razoável de múltiplo/WACC resolve isso, é preciso reestruturação de capital.
                        </p>
                      </div>
                    )}

                    {/* MATRIZ DE SENSIBILIDADE 3x3 */}
                    <div className="bg-[#020617] p-8 rounded-[2rem] border border-white/5 shadow-lg">
                      <p className="text-xs font-black text-white uppercase tracking-widest mb-6">Matriz de Sensibilidade (9 Cenários)</p>
                      <div className="grid grid-cols-4 gap-3 text-center">
                        <div />
                        {[apexWacc - 2, apexWacc, apexWacc + 2].map((wacc) => (
                          <div key={`h-${wacc}`} className="text-xs font-black text-purple-300 pb-1 flex items-center justify-center">{wacc}%</div>
                        ))}
                        {[apexMultiple - 1, apexMultiple, apexMultiple + 1].map((mult) => (
                          <React.Fragment key={`r-${mult}`}>
                            <div className="text-xs font-black text-lime-300 flex items-center justify-center">{mult.toFixed(1)}x</div>
                            {[apexWacc - 2, apexWacc, apexWacc + 2].map((wacc) => {
                              const val = Math.max(0, (baseEbitda * mult * 12) * (1 - (wacc - 12.5) / 100) - selectedClient.debt);
                              const isCenter = mult === apexMultiple && wacc === apexWacc;
                              return (
                                <div key={`${mult}-${wacc}`} className={`rounded-xl py-5 px-2 text-sm font-black ${isCenter ? 'bg-lime-400/20 border border-lime-400/40 text-lime-400' : val === 0 ? 'bg-red-500/10 border border-red-500/30 text-red-400/70' : 'bg-white/5 border border-white/5 text-white'}`}>
                                  {val === 0 ? '⚠️' : formatCurrency(val)}
                                </div>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>

                    {/* 🔮 LEITURA DA MATRIZ DE SENSIBILIDADE — calculada na
                    hora, sem chamada de IA (mesmo princípio da Projeção
                    Estocástica: interpretarHorizonte). Antes esse espaço
                    era um placeholder morto ("Inserir IA Aqui") duplicando
                    a Projeção Estocástica que já existe em Diagnóstico
                    Geral — agora entrega conteúdo útil e exclusivo desta
                    aba, sobre os 9 cenários da matriz acima. */}
                    <div className="bg-gradient-to-br from-purple-900/20 to-transparent border border-purple-500/20 p-8 rounded-[2rem]">
                      <div className="flex items-center gap-2 mb-4">
                        <Zap size={16} className="text-purple-400" />
                        <p className="text-sm font-black text-purple-400 uppercase tracking-widest">Leitura da Matriz de Sensibilidade</p>
                      </div>
                      {(() => {
                        const valoresMatriz = [apexMultiple - 1, apexMultiple, apexMultiple + 1].flatMap((mult) =>
                          [apexWacc - 2, apexWacc, apexWacc + 2].map((wacc) =>
                            Math.max(0, (baseEbitda * mult * 12) * (1 - (wacc - 12.5) / 100) - selectedClient.debt)
                          )
                        );
                        const texto = interpretarSensibilidade({
                          apexMultiple, apexWacc, baseEbitda, debt: selectedClient.debt,
                          valorAtual: equityValueSimulado,
                          valorMax: Math.max(...valoresMatriz),
                          valorMin: Math.min(...valoresMatriz),
                        });
                        return <p className="text-xs text-slate-400 leading-relaxed">{texto}</p>;
                      })()}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* TAB 5: BOARDROOM M&A */}
            {activeTab === 'boardroom' && selectedClient && metrics && (
              <div className="relative flex flex-col space-y-8 mb-8">
                {!checkAccess(4) && renderPaywall('Boardroom & M&A', 'Enterprise Ultra')}

                <div className={`flex-1 flex flex-col justify-center ${!checkAccess(4) ? 'blur-md pointer-events-none select-none' : 'animate-in fade-in duration-500'}`}>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                    <div className="bg-gradient-to-br from-[#020617] to-slate-900/80 p-10 pr-28 rounded-[2rem] border border-purple-500/20 shadow-lg flex flex-col relative overflow-hidden group">
                      <div className="phoenix-watermark-icon absolute top-0 right-0 p-8 opacity-10 pointer-events-none"><Briefcase size={120} className="text-purple-400" /></div>
                      <div className="relative z-10 overflow-hidden w-full">
                        <p className="text-xs font-black text-purple-400 uppercase tracking-widest italic mb-4">Tese de Valuation (Equity)</p>
                        <h3 className="text-2xl xl:text-3xl font-black text-white italic tracking-tighter mb-8 break-words">
                          R$ {formatCurrency(metrics.equityValue)}
                        </h3>
                      </div>
                      <div className="space-y-4 mt-auto relative z-10 border-t border-white/5 pt-6">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-slate-500 uppercase font-black tracking-widest">Múltiplo Setor</span>
                          <span className="text-sm text-white font-bold">{metrics.multiple}x</span>
                        </div>
                        <div className="flex justify-between items-center overflow-hidden">
                          <span className="text-xs text-slate-500 uppercase font-black tracking-widest whitespace-nowrap">Enterprise Value</span>
                          <span className="text-sm text-white font-bold whitespace-nowrap ml-4">R$ {formatCurrency(metrics.enterpriseValue)}</span>
                        </div>
                        <div className="flex justify-between items-center overflow-hidden">
                          <span className="text-xs text-slate-500 uppercase font-black tracking-widest whitespace-nowrap">Dívida Subtraída</span>
                          <span className="text-sm text-red-400 font-bold whitespace-nowrap ml-4">- R$ {formatCurrency(selectedClient.debt)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-slate-900/40 p-10 rounded-[2rem] border border-white/5 shadow-lg flex flex-col justify-between relative overflow-hidden group">
                      <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none"><Target size={120} className="text-purple-400" /></div>
                      <div className="relative z-10 overflow-hidden w-full">
                        <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4">M&A Readiness Score</p>
                        <span className="text-4xl lg:text-5xl font-black italic tracking-tighter text-purple-400 whitespace-nowrap">{metrics.maScore ?? 0}<span className="text-xl font-bold text-slate-600 not-italic">/100</span></span>
                      </div>
                      <div className="relative z-10">
                        <Sparkline data={[50, 55, metrics.maScore ?? 0]} color="#a855f7" />
                      </div>
                      <p className="text-xs text-slate-500 border-t border-white/5 pt-4 italic leading-relaxed mt-4 relative z-10">
                        Grau analítico de atratividade da estrutura de capital e governança para recepção de fundos de Private Equity.
                      </p>
                    </div>

                    <div className="bg-slate-900/40 p-10 rounded-[2rem] border border-white/5 shadow-lg flex flex-col justify-between relative overflow-hidden group">
                      <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none"><FileSignature size={120} className="text-purple-400" /></div>
                      <div className="flex items-center gap-3 pb-2 shrink-0 relative z-10">
                        <FileSignature size={20} className="text-purple-400" />
                        <p className="text-xs font-black text-slate-500 uppercase tracking-widest">Diretriz de Sucessão</p>
                      </div>
                      <div className="space-y-4 flex-1 flex flex-col justify-center text-left my-4 relative z-10">
                        <div className="flex gap-3 items-start">
                          {metrics.currentMargin > 10 ? <CheckCircle2 size={18} className="text-lime-400 shrink-0 mt-0.5" /> : <AlertTriangle size={18} className="text-orange-400 shrink-0 mt-0.5" />}
                          <p className="text-xs text-slate-400 font-medium leading-relaxed">{metrics.currentMargin > 10 ? 'Margem operacional informada em faixa saudável para o setor (referência aproximada de mercado).' : 'Margem operacional abaixo da faixa saudável: recomenda-se revisar custos antes de uma operação de M&A.'}</p>
                        </div>
                        <div className="flex gap-3 items-start">
                          {selectedClient.debt > 0 ? <AlertTriangle size={18} className="text-orange-400 shrink-0 mt-0.5" /> : <CheckCircle2 size={18} className="text-lime-400 shrink-0 mt-0.5" />}
                          <p className="text-xs text-slate-400 font-medium leading-relaxed">{selectedClient.debt > 0 ? "Há passivos onerosos: recomenda-se organizar e revisar a dívida antes de uma operação de M&A." : "Sem dívida onerosa informada, o que tende a facilitar as negociações."}</p>
                        </div>
                      </div>
                      <p className="text-[11px] lg:text-xs text-slate-500 border-t border-white/5 pt-4 italic leading-relaxed mt-4 relative z-10">
                        Veredito estrutural da IA sobre governança técnica e plano estratégico de transição patrimonial de controle corporativo.
                      </p>
                    </div>

                  </div>

                  <div className="w-full bg-[#020617] border border-white/5 p-6 rounded-2xl flex items-center justify-between shadow-xl mt-8 animate-in slide-in-from-bottom-2 shrink-0">
                    <div className="flex items-center gap-4">
                      <div className="w-3 h-3 rounded-full bg-purple-500 shadow-[0_0_15px_#a855f7] animate-pulse" />
                      <p className="text-xs font-black text-slate-400 tracking-normal">Dossiê Técnico pronto para consolidação de mercado</p>
                    </div>
                    <button onClick={() => exportarPDF(imRef, `Phoenix_IM_${selectedClient.name.replace(/\s+/g, '_')}.pdf`)} disabled={exportando} className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 px-8 py-4 rounded-xl font-black uppercase text-xs tracking-widest transition-all border border-purple-500/30 flex items-center gap-3 no-print disabled:opacity-50">
                      <FileSignature size={18} /> {exportando ? 'Gerando PDF...' : 'Gerar Information Memorandum (IM)'}
                    </button>
                  </div>

                  {/* Estrutura de Capital + Checklist de Prontidão + Benchmark de Múltiplos */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">

                    {(() => {
                      const totalCapital = Math.max(1, selectedClient.debt + metrics.equityValue);
                      const pctDivida = (selectedClient.debt / totalCapital) * 100;
                      const pctEquity = 100 - pctDivida;
                      return (
                        <div className="bg-slate-900/40 p-8 rounded-[2rem] border border-white/5 shadow-lg flex flex-col items-center">
                          <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-6 self-start">Estrutura de Capital</p>
                          <div
                            className="w-36 h-36 rounded-full shrink-0"
                            style={{ background: `conic-gradient(#a855f7 0% ${pctDivida}%, #bef264 ${pctDivida}% 100%)` }}
                          ></div>
                          <div className="w-full space-y-2 mt-6">
                            <div className="flex items-center justify-between text-xs">
                              <span className="flex items-center gap-2 text-slate-400 font-bold"><span className="w-2.5 h-2.5 rounded-full bg-purple-500"></span> Dívida</span>
                              <span className="text-white font-black">{pctDivida.toFixed(0)}%</span>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                              <span className="flex items-center gap-2 text-slate-400 font-bold"><span className="w-2.5 h-2.5 rounded-full bg-lime-400"></span> Patrimônio Líquido</span>
                              <span className="text-white font-black">{pctEquity.toFixed(0)}%</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    <div className="bg-slate-900/40 p-8 rounded-[2rem] border border-white/5 shadow-lg">
                      <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-6">Checklist de Prontidão para M&A</p>
                      <div className="space-y-4">
                        {[
                          { ok: metrics.currentMargin > 10, texto: 'Margem operacional saudável (acima de 10%)' },
                          { ok: selectedClient.debt === 0 || metrics.alavancagem < 2, texto: 'Nível de endividamento controlado' },
                          { ok: metrics.ruptureRisk < 40, texto: 'Baixo risco de ruptura de caixa' },
                          { ok: metrics.ebitdaAjustado > 0, texto: 'EBITDA ajustado positivo' },
                          { ok: !!selectedClient.name && !!selectedClient.sector, texto: 'Cadastro e setor da empresa completos' },
                        ].map((item, i) => (
                          <div key={i} className="flex items-start gap-3">
                            {item.ok
                              ? <CheckCircle2 size={16} className="text-lime-400 shrink-0 mt-0.5" />
                              : <AlertTriangle size={16} className="text-orange-400 shrink-0 mt-0.5" />}
                            <p className="text-xs text-slate-300 font-medium leading-relaxed">{item.texto}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {(() => {
                      const refSetor = getSectorMultiple(selectedClient.sector);
                      const desvio = refSetor ? ((metrics.multiple - refSetor) / refSetor) * 100 : 0;
                      return (
                        <div className="bg-slate-900/40 p-8 rounded-[2rem] border border-white/5 shadow-lg flex flex-col justify-between">
                          <div>
                            <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-6">Benchmark de Múltiplos do Setor</p>
                            <div className="flex items-baseline gap-2 mb-2">
                              <span className="text-3xl font-black text-white italic tracking-tighter">{metrics.multiple}x</span>
                              <span className="text-xs text-slate-500 font-bold">usado nesta análise</span>
                            </div>
                            <div className="w-full bg-white/5 rounded-full h-2.5 overflow-hidden my-4">
                              <div className="h-full bg-purple-400 rounded-full" style={{ width: `${Math.min(100, (metrics.multiple / (refSetor * 2)) * 100)}%` }}></div>
                            </div>
                          </div>
                          <p className="text-xs text-slate-500 border-t border-white/5 pt-4 italic leading-relaxed">
                            Referência do setor ({selectedClient.sector}): <span className="text-white font-bold">{refSetor}x</span> · <span className={desvio >= 0 ? 'text-lime-400 font-bold' : 'text-orange-400 font-bold'}>{desvio >= 0 ? '+' : ''}{desvio.toFixed(1)}% vs. ref.</span>
                          </p>
                        </div>
                      );
                    })()}

                  </div>
                </div>
              </div>
            )}

            {/* TAB 6: CONFIGURAÇÕES WHITE-LABEL */}
            {activeTab === 'white-label' && !selectedClient && (
              <div className="relative animate-in fade-in duration-500 w-full flex-1 flex flex-col min-h-fit pb-12">
                {!checkAccess(4) && renderPaywall('Marca & White-Label', 'Enterprise Ultra')}
                <div className={!checkAccess(4) ? 'blur-md pointer-events-none select-none' : ''}>
                  <div className="flex items-center gap-4 mb-8 border-b border-white/5 pb-6">
                    <Palette className="text-lime-400" size={32} />
                    <div>
                      <h3 className="text-3xl font-black text-white uppercase italic tracking-tighter">Identidade Visual White-Label</h3>
                      <p className="text-xs text-slate-500 font-bold tracking-normal mt-1">Gere relatórios de alta performance com a sua própria marca corporativa</p>
                    </div>
                  </div>

                  {/* 🔥 Escolha de qual empresa o PDF 360º vai usar como dado real —
                  antes o botão de baixar sempre usava a primeira empresa da
                  carteira "por baixo dos panos", sem o Consultor poder escolher. */}
                  {clients.length > 0 && (
                    <div className="bg-lime-400/5 border border-lime-400/20 rounded-2xl p-5 mb-8 flex flex-col sm:flex-row sm:items-center gap-4">
                      <p className="text-xs font-black text-lime-400 uppercase tracking-widest shrink-0">Gerar PDF 360º para:</p>
                      <select
                        value={clienteParaAmostra?.id || ''}
                        onChange={(e) => setClienteParaAmostra(clients.find(c => c.id === e.target.value) || null)}
                        className="flex-1 bg-black/40 border border-lime-400/20 rounded-xl px-4 py-3 text-sm font-bold text-white outline-none focus:border-lime-400/50 transition-all cursor-pointer"
                      >
                        {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

                    {/* Formulário Esquerdo */}
                    <div className="bg-[#020617] p-10 rounded-[3rem] border border-white/5 shadow-2xl space-y-6">
                      <div className="space-y-3">
                        <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Nome do Consultor / Boutique</label>
                        <input type="text" value={brand.name} onChange={(e) => setBrand({ ...brand, name: e.target.value })} className="w-full bg-slate-900/50 border border-white/10 px-6 py-4 rounded-xl text-white font-bold outline-none focus:border-lime-400/50 transition-all text-base" />
                      </div>

                      <div className="space-y-3">
                        <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Logotipo Corporativo (Exclusivo Enterprise)</label>

                        {brand.logo ? (
                          <div className="w-full bg-slate-900/50 border border-white/10 p-4 rounded-xl flex items-center justify-between">
                            <img src={brand.logo} alt="Logo Corporativo" className="max-h-12 object-contain bg-black/20 p-2 rounded" />
                            <button onClick={() => setBrand({ ...brand, logo: null })} className="p-3 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors flex items-center gap-2">
                              <Trash2 size={16} />
                              <span className="text-xs font-black uppercase">Remover</span>
                            </button>
                          </div>
                        ) : (
                          <label className="w-full bg-slate-900/50 border-2 border-dashed border-white/10 p-6 rounded-xl text-center cursor-pointer hover:border-purple-500/30 transition-all flex flex-col items-center justify-center gap-2 group">
                            <input type="file" accept="image/png, image/jpeg" className="hidden" onChange={handleLogoUpload} />
                            <Download size={20} className="text-slate-500 group-hover:text-purple-400 transition-colors" />
                            <p className="text-xs font-black uppercase text-slate-400 tracking-wider">Clique para enviar seu Logotipo</p>
                            <p className="text-[11px] text-slate-600 font-bold">Recomendado: Fundo transparente (.PNG) • Máximo 400x100px</p>
                          </label>
                        )}
                      </div>

                      <div className="space-y-4">
                        <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Esquema de Cores Prontas</label>
                        <div className="grid grid-cols-3 gap-3">
                          {brandColors.map(c => (
                            <button key={c.hex} onClick={() => setBrand({ ...brand, color: c.hex })} className={`flex flex-col items-center justify-center p-4 rounded-xl border transition-all ${brand.color === c.hex ? 'bg-white/10 border-white shadow-[0_0_15px_rgba(255,255,255,0.05)]' : 'bg-slate-900/50 border-white/5 hover:border-white/10'}`}>
                              <div className="w-8 h-8 rounded-full mb-2 border border-white/10 shadow-inner" style={{ backgroundColor: c.hex }}></div>
                              <span className="text-[11px] font-black text-slate-400 uppercase tracking-tighter text-center">{c.name}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-3">
                        <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Cláusula de Isenção Legal (Rodapé Global)</label>
                        <textarea value={brand.disclaimer} onChange={(e) => setBrand({ ...brand, disclaimer: e.target.value })} rows="3" className="w-full bg-slate-900/50 border border-white/10 px-6 py-4 rounded-xl text-slate-400 text-sm outline-none focus:border-lime-400/50 transition-all custom-scroll resize-none leading-relaxed"></textarea>
                      </div>

                      <button onClick={salvarBrand} disabled={salvandoBrand} className="w-full bg-lime-400 text-black py-5 rounded-full font-black uppercase text-sm shadow-[0_0_20px_rgba(190,242,100,0.2)] hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50">
                        {salvandoBrand ? 'Salvando...' : 'Salvar Configurações White-Label'}
                      </button>
                    </div>

                    {/* Amostra Direita */}
                    <div className="flex flex-col w-full">
                      <label className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2"><Eye size={16} /> Amostra da Capa do Super Relatório 360º</label>
                      <div className="flex-1 bg-[#1a1a1a] rounded-[3rem] border border-white/5 flex items-center justify-center p-12 relative overflow-hidden min-h-[550px]">

                        <div className="w-full max-w-[300px] aspect-[1/1.414] rounded shadow-2xl flex flex-col justify-between text-left p-8 relative overflow-hidden" style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }}>
                          <div className="absolute top-0 left-0 bottom-0 w-2.5" style={{ backgroundColor: brand.color }}></div>

                          <div className="space-y-12 pl-4">
                            <div className="flex justify-between items-center">
                              {brand.logo ? (
                                <img src={brand.logo} alt="Logo Corporativo" className="max-h-10 max-w-[150px] object-contain" />
                              ) : (
                                <h1 className="text-sm font-black italic uppercase tracking-tighter truncate max-w-[180px]" style={{ color: brand.color }}>{brand.name}</h1>
                              )}
                              <ShieldCheck size={18} style={{ color: brand.color }} />
                            </div>

                            <div className="space-y-3 pt-8">
                              <div className="text-[11px] font-black uppercase tracking-[0.3em]" style={{ color: '#64748b' }}>Information Memorandum</div>
                              <h2 className="text-xl font-sans font-black uppercase italic tracking-tighter leading-none" style={{ color: '#0f172a' }}>Relatório de Governança Estratégica & Valuation</h2>
                              <div className="w-12 h-1 rounded-full" style={{ backgroundColor: '#cbd5e1' }}></div>
                            </div>
                          </div>

                          <div className="pl-4 space-y-4">
                            <div className="space-y-1">
                              <p className="text-[11px] font-black uppercase tracking-widest" style={{ color: '#64748b' }}>Preparado Exclusivamente para:</p>
                              <p className="text-[11px] font-bold" style={{ color: '#334155' }}>Empresa Ativa Portfólio S.A.</p>
                            </div>
                            <div className="pt-4" style={{ borderTop: '1px solid #e2e8f0', fontSize: '4px', color: '#94a3b8', textAlign: 'justify', lineHeight: 1.4 }}>
                              {brand.disclaimer}
                            </div>
                          </div>
                        </div>

                      </div>

                      <button onClick={() => exportarPDF(superPdfRef, nomeArquivoRelatorio('360', clienteParaAmostra?.name || 'Amostra'))} disabled={exportando || !clienteParaAmostra} className="mt-4 w-full bg-white/5 hover:bg-white/10 text-white py-4 rounded-full font-black uppercase text-xs tracking-widest transition-all border border-white/10 flex items-center justify-center gap-2 no-print disabled:opacity-50">
                        <Download size={16} /> {exportando ? 'Gerando PDF...' : `Baixar PDF 360º${clienteParaAmostra ? ` — ${clienteParaAmostra.name}` : ''}`}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB NOVA: TEASER "AGENTES DE IA" (Em Breve) — sem
            funcionalidade real por trás, só antecipa a feature e mede
            interesse. Não precisa de e-mail nem conta separada — só um
            clique, com contador compartilhado entre todos os Consultores. */}
            {activeTab === 'agentes-ia' && !selectedClient && (
              <div className="animate-in fade-in duration-500 max-w-2xl mx-auto w-full flex-1 flex flex-col justify-center pb-12">
                <div className="relative bg-gradient-to-br from-amber-950/30 via-[#020617] to-[#020617] p-14 rounded-[3rem] border border-amber-500/20 overflow-hidden text-center shadow-[0_0_60px_rgba(245,158,11,0.08)]">
                  <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #f59e0b 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
                  <div className="absolute -top-16 -right-16 w-56 h-56 bg-amber-500/10 rounded-full blur-3xl" />

                  <div className="relative z-10">
                    <div className="w-24 h-24 mx-auto mb-8 rounded-[2rem] bg-gradient-to-br from-amber-400/20 to-amber-600/5 border border-amber-400/30 flex items-center justify-center shadow-[0_0_40px_rgba(245,158,11,0.25)]">
                      <Bot size={44} className="text-amber-400" />
                    </div>

                    <span className="inline-flex items-center gap-2 bg-amber-500/15 text-amber-400 px-5 py-2 rounded-full text-[11px] font-black uppercase tracking-[0.25em] border border-amber-500/30 mb-6">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" /> Em Breve
                    </span>

                    <h2 className="text-4xl font-black text-white uppercase italic tracking-tighter leading-[0.95] mb-5">
                      Agentes de <span className="text-amber-400">IA</span>
                    </h2>
                    <p className="text-sm text-slate-400 leading-relaxed max-w-md mx-auto mb-10">
                      Sua carteira sendo monitorada sozinha: alertas automáticos de risco em cada cliente, tarefas geradas pra você sem precisar pedir, e due diligence assistida — o Phoenix OS trabalhando por trás, não só quando você abre o painel.
                    </p>

                    <div className="grid sm:grid-cols-3 gap-4 max-w-lg mx-auto mb-10">
                      {[
                        { titulo: 'Monitoramento 24/7', desc: 'Toda a carteira, sem precisar abrir cliente por cliente' },
                        { titulo: 'Tarefas Automáticas', desc: 'Alertas e ações sugeridas geradas sozinhas' },
                        { titulo: 'Due Diligence Assistida', desc: 'IA acompanha o processo de M&A com você' },
                      ].map((item, i) => (
                        <div key={i} className="bg-black/20 border border-amber-500/10 rounded-2xl p-5 text-left">
                          <div className="w-8 h-8 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-3">
                            <Check size={14} className="text-amber-400" />
                          </div>
                          <p className="text-xs font-black text-white uppercase tracking-widest mb-1.5">{item.titulo}</p>
                          <p className="text-[11px] text-slate-500 leading-relaxed">{item.desc}</p>
                        </div>
                      ))}
                    </div>

                    {jaRegistrouInteresseAgentes ? (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-full py-4 px-8 inline-flex items-center gap-2.5">
                        <CheckCircle2 size={18} className="text-amber-400" />
                        <p className="text-xs font-black text-amber-400 uppercase tracking-widest">Interesse registrado!</p>
                      </div>
                    ) : (
                      <button
                        onClick={registrarInteresseAgentesIA}
                        disabled={registrandoInteresseAgentes}
                        className="bg-amber-400 hover:bg-amber-300 text-black py-4 px-10 rounded-full font-black uppercase text-xs tracking-widest active:scale-95 transition-all disabled:opacity-60 shadow-[0_0_30px_rgba(245,158,11,0.2)]"
                      >
                        {registrandoInteresseAgentes ? 'Registrando...' : 'Tenho Interesse'}
                      </button>
                    )}

                    {contadorInteresseAgentes?.totalConsultor > 0 && (
                      <p className="text-xs text-slate-500 mt-5 font-bold">
                        <span className="text-amber-400 font-black">{contadorInteresseAgentes.totalConsultor}</span> {contadorInteresseAgentes.totalConsultor === 1 ? 'consultor já demonstrou' : 'consultores já demonstraram'} interesse
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* TAB 7: CONFIGURAÇÕES DA CONTA */}
            {activeTab === 'settings' && !selectedClient && (
              <div className="animate-in fade-in duration-500 max-w-4xl flex-1 flex flex-col min-h-fit pb-12 mx-auto w-full space-y-8">
                <div className="bg-[#020617] p-12 rounded-[3rem] border border-white/5 shadow-2xl mt-4">
                  <div className="flex items-center gap-5 mb-10 border-b border-white/5 pb-8">
                    <Settings className="text-lime-400" size={36} />
                    <div>
                      <h3 className="text-3xl font-black text-white uppercase italic tracking-tighter">Preferências do Terminal</h3>
                      <p className="text-xs text-slate-500 font-bold tracking-normal mt-1">Gerencie a segurança e chaves operacionais do consultor</p>
                    </div>
                  </div>

                  <div className="space-y-10">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="p-8 bg-slate-900/40 rounded-[2rem] border border-white/5">
                        <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Conta</p>
                        <p className="text-xl font-black text-white italic truncate">{auth.currentUser?.email || '—'}</p>
                      </div>
                      <div className="p-8 bg-slate-900/40 rounded-[2rem] border border-white/5">
                        <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Plano Ativo</p>
                        <p className="text-xl font-black text-lime-400 italic">
                          {planoConsultorReal ? PLANOS_CONSULTOR[planoConsultorReal]?.nome || planoConsultorReal : 'Carregando...'}
                        </p>
                        {planoConsultorReal && (
                          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest mt-1">{PLANOS_CONSULTOR[planoConsultorReal]?.cnpjs}</p>
                        )}
                      </div>
                    </div>

                    {planoConsultorReal && PLANOS_CONSULTOR[planoConsultorReal]?.proximo && (() => {
                      const idProximo = PLANOS_CONSULTOR[planoConsultorReal].proximo;
                      const proximo = PLANOS_CONSULTOR[idProximo];
                      return (
                        <div className="p-8 bg-gradient-to-r from-purple-500/10 to-transparent border border-purple-500/20 rounded-[2.5rem] flex flex-col md:flex-row items-start md:items-center justify-between shadow-lg gap-6">
                          <div className="space-y-2">
                            <div className="bg-purple-500/20 text-purple-400 px-3 py-1 rounded text-[11px] font-black uppercase tracking-widest italic w-fit border border-purple-500/30">Upgrade Disponível</div>
                            <h4 className="text-lg font-black text-white uppercase italic tracking-tighter">Plano {proximo.nome}</h4>
                            <p className="text-xs text-slate-400 max-w-xl leading-relaxed">Expanda sua carteira para <strong className="text-white">{proximo.cnpjs}</strong> monitorados simultaneamente, por <strong className="text-white">{proximo.preco}</strong>.</p>
                          </div>
                          <button
                            onClick={() => abrirCheckout(idProximo)}
                            className="bg-purple-500 text-white px-6 py-3 rounded-xl font-black uppercase text-[11px] tracking-widest shrink-0 hover:scale-105 transition-all"
                          >
                            Assinar {proximo.nome}
                          </button>
                        </div>
                      );
                    })()}

                    {planoConsultorReal === 'enterprise' && (
                      <div className="p-8 bg-lime-400/5 border border-lime-400/20 rounded-[2.5rem] flex items-center gap-4">
                        <ShieldCheck className="text-lime-400 shrink-0" size={28} />
                        <p className="text-xs text-lime-400/90 font-black uppercase tracking-widest">🏆 Você atingiu o nível máximo do Consultor</p>
                      </div>
                    )}

                    {!planoConsultorReal && (
                      <div className="p-8 bg-slate-900/40 border border-white/5 rounded-[2.5rem] text-center">
                        <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">Carregando plano...</p>
                      </div>
                    )}

                    {/* GERENCIAR PAGAMENTO — checkout embutido sempre
                        disponível aqui, não só no card de upgrade
                        sugerido. Mesmo componente do Dashboard Gestor. */}
                    <div className="p-8 bg-black/20 rounded-[2.5rem] border border-white/5">
                      <button
                        onClick={() => setMostrarCheckoutConfig(v => !v)}
                        className="w-full flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <CreditCard size={18} className="text-lime-400" />
                          <span className="text-sm font-black text-white uppercase tracking-widest">Gerenciar Pagamento / Trocar de Plano</span>
                        </div>
                        <ChevronDown size={18} className={`text-slate-500 transition-transform ${mostrarCheckoutConfig ? 'rotate-180' : ''}`} />
                      </button>
                      {mostrarCheckoutConfig && (
                        <div className="mt-6 pt-6 border-t border-white/5">
                          {planosParaUpgrade.length > 0 ? (
                            <CheckoutAsaasCard
                              planos={planosParaUpgrade}
                              planoSelecionadoId={PLANOS_CONSULTOR[planoConsultorReal]?.proximo}
                              tipoPerfil="Consultor"
                              onSuccess={() => setTimeout(() => setMostrarCheckoutConfig(false), 2500)}
                            />
                          ) : (
                            <p className="text-xs text-slate-500 text-center py-6">Você já está no plano máximo — não há upgrade disponível.</p>
                          )}
                        </div>
                      )}
                    </div>

                    {planoConsultorReal && (
                      <BlocoCancelarAssinatura perfil="consultor" cancelamento={cancelamentoConsultorDoc} />
                    )}

                    <div className="space-y-4 pt-6 border-t border-white/5">
                      <div
                        onClick={() => tema === 'dark' && setContrasteOled(v => !v)}
                        className={`flex flex-col md:flex-row md:items-center justify-between p-8 bg-slate-900/40 rounded-[2rem] border border-white/5 transition-colors gap-4 ${tema === 'dark' ? 'cursor-pointer hover:bg-white/5' : 'opacity-50 cursor-not-allowed'}`}
                      >
                        <div>
                          <p className="text-sm font-black text-white uppercase">Contraste Absoluto OLED</p>
                          <p className="text-[11px] text-slate-500 mt-2 font-bold tracking-widest">
                            {tema === 'dark' ? 'Ajusta as paletas escuras para longas jornadas de auditoria financeira' : 'Disponível apenas no tema escuro'}
                          </p>
                        </div>
                        <button
                          type="button"
                          aria-pressed={contrasteOled}
                          disabled={tema !== 'dark'}
                          onClick={(e) => { e.stopPropagation(); if (tema === 'dark') setContrasteOled(v => !v); }}
                          className={`w-14 h-7 rounded-full relative shrink-0 border shadow-inner transition-colors ${contrasteOled && tema === 'dark' ? 'bg-lime-400 border-lime-500' : 'bg-slate-700 border-slate-600'}`}
                        >
                          <div className={`w-6 h-6 bg-black rounded-full absolute top-0.5 transition-all ${contrasteOled && tema === 'dark' ? 'right-0.5' : 'left-0.5'}`}></div>
                        </button>
                      </div>

                      <div className="flex flex-col md:flex-row md:items-center justify-between p-8 bg-slate-900/40 rounded-[2rem] border border-white/5 cursor-pointer hover:bg-white/5 transition-colors gap-4">
                        <div>
                          <p className="text-sm font-black text-white uppercase">Notificações Críticas de Alavancagem</p>
                          <p className="text-[11px] text-slate-500 mt-2 font-bold tracking-widest">
                            Dispara alerta quando o risco de um cliente atinge <span className="text-lime-400">{limiteAlertaRisco}%</span> em relação ao limite do setor
                          </p>
                          <input
                            type="range"
                            min={50}
                            max={100}
                            step={5}
                            value={limiteAlertaRisco}
                            onChange={(e) => salvarLimiteAlerta(Number(e.target.value))}
                            className="w-full max-w-xs mt-4 accent-lime-400 cursor-pointer"
                          />
                          <div className="flex justify-between max-w-xs text-[11px] text-slate-600 font-bold uppercase tracking-widest mt-1">
                            <span>Mais sensível (50%)</span>
                            <span>Só crítico (100%)</span>
                          </div>
                        </div>
                        <span className="text-[11px] font-black text-slate-600 uppercase tracking-widest shrink-0">{salvandoLimite ? 'Salvando...' : ''}</span>
                      </div>

                      {/* SOBRE — dados institucionais/comerciais do Phoenix OS,
                        vindos de COMPANY_INFO (constants.js), mesma fonte
                        usada no app e no Dashboard Gestor. */}
                      <div className="flex flex-col md:flex-row md:items-center justify-between p-8 bg-slate-900/40 rounded-[2rem] border border-white/5 cursor-pointer hover:bg-white/5 transition-colors gap-4" onClick={() => setMostrandoSobre(true)}>
                        <div>
                          <p className="text-sm font-black text-white uppercase">Sobre o Phoenix OS</p>
                          <p className="text-[11px] text-slate-500 mt-2 font-bold tracking-widest">Dados da empresa, contato de suporte, política de privacidade e termos de uso.</p>
                        </div>
                        <span className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white px-5 py-3 rounded-full border border-white/10 text-xs font-black uppercase tracking-widest transition-all shrink-0">
                          <Info size={14} /> Ver
                        </span>
                      </div>

                      {/* EXCLUIR CONTA — existia só no app até agora. Apaga
                      TUDO (Gestor e Consultor, já que é a mesma conta),
                      então fica isolado no fim, com aviso claro. */}
                      <div className="pt-4 border-t border-white/5">
                        <button
                          onClick={() => setConfirmandoExclusaoConta(true)}
                          className="w-full text-red-500 font-bold py-5 text-[11px] uppercase tracking-widest hover:text-red-400 transition-colors"
                        >
                          Excluir Minha Conta
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* CONFIRMAÇÃO DE EXCLUSÃO DE CONTA — passo extra obrigatório
                pra uma ação irreversível e séria como essa. Mesmo texto e
                comportamento do app, de propósito. */}
            {confirmandoExclusaoConta && (
              <div className="fixed inset-0 z-[600] bg-[#010208]/95 backdrop-blur-xl flex items-center justify-center p-6 animate-in fade-in duration-200">
                <div className="bg-[#020617] border border-red-500/20 p-8 rounded-[3rem] shadow-2xl w-full max-w-sm text-center">
                  <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center border border-red-500/20 mb-6 mx-auto">
                    <Trash2 size={28} className="text-red-500" />
                  </div>
                  <h3 className="text-xl font-black text-white uppercase italic tracking-tighter mb-3">Excluir sua conta?</h3>
                  <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 mb-5 text-left">
                    <p className="text-[11px] text-red-400 font-black uppercase tracking-widest mb-1.5">⚠️ Isso apaga a conta inteira, não só o Consultor</p>
                    <p className="text-[11px] text-red-300/80 leading-relaxed">
                      Se essa mesma conta também tiver um plano <strong className="text-white">Gestor</strong> ativo, ele será apagado junto — é a mesma conta, um único login pros dois perfis.
                    </p>
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed mb-8">
                    Todos os seus clientes, planos e histórico serão apagados permanentemente, no Dashboard e no app. <strong className="text-white">Sua assinatura ativa (se houver) será cancelada automaticamente no Asaas</strong> — você não continuará sendo cobrado. Essa ação não pode ser desfeita.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setConfirmandoExclusaoConta(false)}
                      disabled={excluindoConta}
                      className="flex-1 bg-white/5 text-white py-4 rounded-full font-black uppercase text-xs border border-white/10 hover:bg-white/10 transition-all disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={excluirContaDefinitivamente}
                      disabled={excluindoConta}
                      className="flex-1 bg-red-500 text-white py-4 rounded-full font-black uppercase text-xs hover:bg-red-600 active:scale-95 transition-all disabled:opacity-50"
                    >
                      {excluindoConta ? 'Excluindo...' : 'Excluir'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* MODAL SOBRE — dados institucionais/comerciais, mesma fonte
              (COMPANY_INFO) usada no app e no Dashboard Gestor. */}
            {mostrandoSobre && (
              <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
                <div className="bg-[#020617] border border-white/10 rounded-[2rem] p-8 w-full max-w-lg max-h-[85vh] overflow-y-auto custom-scroll">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <Info size={20} className="text-lime-400" />
                      <h3 className="text-lg font-black text-white uppercase italic tracking-tighter">Sobre o {COMPANY_INFO.nomeFantasia}</h3>
                    </div>
                    <button onClick={() => setMostrandoSobre(false)} className="text-slate-500 hover:text-white transition-colors">
                      <X size={20} />
                    </button>
                  </div>

                  <div className="space-y-5">
                    <div className="bg-white/5 rounded-2xl p-5 border border-white/5">
                      <p className="text-sm text-slate-300 leading-relaxed">{COMPANY_INFO.descricaoCurta}</p>
                    </div>

                    <div>
                      <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-3">Dados da Empresa</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-black/20 rounded-xl p-4">
                          <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Operado por</p>
                          <p className="text-sm text-white font-bold mt-1">{COMPANY_INFO.nomeEmpresa}</p>
                        </div>
                        <div className="bg-black/20 rounded-xl p-4">
                          <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Razão Social</p>
                          <p className="text-sm text-white font-bold mt-1">{COMPANY_INFO.razaoSocial}</p>
                        </div>
                        <div className="bg-black/20 rounded-xl p-4">
                          <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">CNPJ</p>
                          <p className="text-sm text-white font-bold mt-1">{COMPANY_INFO.cnpj}</p>
                        </div>
                        <div className="bg-black/20 rounded-xl p-4 md:col-span-2">
                          <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Endereço</p>
                          <p className="text-sm text-white font-bold mt-1">{COMPANY_INFO.endereco}</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-3">Suporte</p>
                      <a href={`mailto:${COMPANY_INFO.emailSuporte}`} className="flex items-center gap-3 bg-black/20 rounded-xl p-4 text-sm text-lime-400 font-bold hover:bg-black/30 transition-colors w-fit">
                        <FileText size={14} /> {COMPANY_INFO.emailSuporte}
                      </a>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <a href={COMPANY_INFO.linkPoliticaPrivacidade} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between px-5 py-4 rounded-xl bg-white/5 border border-white/10 text-xs font-black text-white uppercase tracking-widest hover:bg-white/10 transition-colors">
                        Política de Privacidade
                      </a>
                      <a href={COMPANY_INFO.linkTermosDeUso} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between px-5 py-4 rounded-xl bg-white/5 border border-white/10 text-xs font-black text-white uppercase tracking-widest hover:bg-white/10 transition-colors">
                        Termos de Uso
                      </a>
                    </div>

                    <p className="text-[11px] text-slate-600 font-bold uppercase tracking-widest text-center pt-2">
                      © {COMPANY_INFO.anoFundacao} {COMPANY_INFO.nomeEmpresa} — {COMPANY_INFO.nomeFantasia} • Versão {COMPANY_INFO.versaoApp}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* RODAPÉ GLOBAL — mesma estrutura do Dashboard Gestor:
                1) Explica o Ecossistema Phoenix OS (app + Dashboard sincronizados)
                2) Lembra de manter os dados dos clientes atualizados
                3) Disclaimer legal (aqui usa brand.disclaimer, que respeita
                   o White-Label quando o Consultor tem marca própria) */}
            <div className="mt-auto pt-12 pb-4 border-t border-white/5 shrink-0 space-y-4">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-8 text-center sm:text-left max-w-4xl mx-auto px-4">
                <div className="flex items-center gap-2.5 text-slate-500">
                  <RefreshCw size={13} className="text-lime-400/70 shrink-0" />
                  <p className="text-[11px] leading-relaxed">
                    <strong className="text-slate-400">Ecossistema Phoenix OS:</strong> o app (celular) e este Dashboard (computador) ficam sincronizados em tempo real — acompanhe sua carteira em qualquer um dos dois, os dados são os mesmos.
                  </p>
                </div>
                <div className="flex items-center gap-2.5 text-slate-500">
                  <FileSignature size={13} className="text-amber-400/70 shrink-0" />
                  <p className="text-[11px] leading-relaxed">
                    <strong className="text-slate-400">Lembrete:</strong> mantenha os valores dos clientes atualizados. Use "Editar Dados" sempre que faturamento, custos ou dívida mudarem, pra manter o diagnóstico e a IA precisos.
                  </p>
                </div>
              </div>
              <p className="text-[11px] text-slate-600 font-bold uppercase tracking-widest px-4 max-w-5xl mx-auto leading-relaxed text-center">
                {brand.disclaimer}
              </p>
            </div>

          </div> {/* FECHA A ÁREA DE SCROLL DO CONTEÚDO */}
        </main> {/* FECHA O MAIN CONTENT */}

        {/* MODAL DE CHECKOUT EMBUTIDO — compra/upgrade sem sair do
            Dashboard. Mesmo componente usado no Dashboard Gestor. */}
        {checkoutAberto && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
            <div className="bg-[#0a0a12] border border-white/10 rounded-[2rem] p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto custom-scroll">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <CreditCard size={20} className="text-lime-400" />
                  <h3 className="text-lg font-black text-white uppercase italic tracking-tighter">Assinar Plano</h3>
                </div>
                <button onClick={() => setCheckoutAberto(false)} className="text-slate-500 hover:text-white transition-colors">
                  <X size={20} />
                </button>
              </div>
              {planosParaUpgrade.length > 0 ? (
                <CheckoutAsaasCard
                  planos={planosParaUpgrade}
                  planoSelecionadoId={planoSugeridoCheckout}
                  tipoPerfil="Consultor"
                  onClose={() => setCheckoutAberto(false)}
                  onSuccess={() => setTimeout(() => setCheckoutAberto(false), 2500)}
                />
              ) : (
                <p className="text-sm text-slate-400 text-center py-10">
                  Você já está no plano máximo (Enterprise Ultra) — não há upgrade disponível.
                </p>
              )}
            </div>
          </div>
        )}

        {/* MODAL DE CONFIRMAÇÃO — DOWNLOAD DO RELATÓRIO */}
        {confirmandoDownload && selectedClient && (
          <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6">
            <div className="bg-[#020617] border border-white/10 rounded-[2rem] p-8 max-w-sm w-full shadow-2xl">
              <div className="w-14 h-14 rounded-full bg-lime-400/10 flex items-center justify-center mb-5">
                <Download size={22} className="text-lime-400" />
              </div>
              <h4 className="text-lg font-black text-white uppercase italic tracking-tight mb-2">Baixar Relatório?</h4>
              <p className="text-sm text-slate-400 leading-relaxed mb-6">
                Vamos gerar o PDF de "{selectedClient.name}" com os dados mais recentes salvos.
              </p>
              {selectedClient.plan !== 'ADVISOR' && (
            <div className="grid grid-cols-1 gap-2 mb-6 text-left">
              <button
                type="button"
                onClick={() => setTipoRelatorio('completo')}
                className={`rounded-2xl border p-4 transition-all ${tipoRelatorio === 'completo' ? 'border-lime-400/60 bg-lime-400/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
              >
                <p className="text-xs font-black text-white uppercase tracking-widest">Relatório completo (Dossiê 360º)</p>
                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">Relatório completo: valuation, análise técnica, projeção por IA, oportunidades e veredito.</p>
              </button>
              <button
                type="button"
                onClick={() => setTipoRelatorio('master')}
                className={`rounded-2xl border p-4 transition-all ${tipoRelatorio === 'master' ? 'border-lime-400/60 bg-lime-400/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
              >
                <p className="text-xs font-black text-white uppercase tracking-widest">Relatório Master (resumo)</p>
                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">Versão curta e direta, a mesma do app: status, indicadores e parecer em 2 páginas.</p>
              </button>
            </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmandoDownload(false)}
                  className="flex-1 bg-white/5 text-slate-300 py-3.5 rounded-full font-black uppercase text-xs tracking-widest hover:bg-white/10 transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    setConfirmandoDownload(false);
                    const ehAdvisor = selectedClient.plan === 'ADVISOR';
                    const usarMaster = ehAdvisor || tipoRelatorio === 'master';
                    const ref = usarMaster ? basicPdfRef : imRef;
                    const sufixo = usarMaster ? 'Master' : 'Completo';
                    exportarPDF(ref, nomeArquivoRelatorio(usarMaster ? 'master' : '360', selectedClient.name));
                  }}
                  disabled={exportando}
                  className="flex-1 bg-lime-400 text-black py-3.5 rounded-full font-black uppercase text-xs tracking-widest hover:bg-lime-500 active:scale-95 transition-all disabled:opacity-50"
                >
                  {exportando ? 'Gerando...' : 'Baixar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL DE CONFIRMAÇÃO — EXCLUIR ANOTAÇÃO */}
        {notaParaExcluir !== null && (
          <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6">
            <div className="bg-[#020617] border border-white/10 rounded-[2rem] p-8 max-w-sm w-full shadow-2xl">
              <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mb-5">
                <Trash2 size={22} className="text-red-500" />
              </div>
              <h4 className="text-lg font-black text-white uppercase italic tracking-tight mb-2">Excluir anotação?</h4>
              <p className="text-sm text-slate-400 leading-relaxed mb-8">Essa ação não pode ser desfeita.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setNotaParaExcluir(null)}
                  className="flex-1 bg-white/5 text-slate-300 py-3.5 rounded-full font-black uppercase text-xs tracking-widest hover:bg-white/10 transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => { excluirNota(notaParaExcluir); setNotaParaExcluir(null); }}
                  className="flex-1 bg-red-500 text-white py-3.5 rounded-full font-black uppercase text-xs tracking-widest hover:bg-red-600 active:scale-95 transition-all"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL DE CONFIRMAÇÃO — EXCLUIR TAREFA */}
        {tarefaParaExcluir !== null && (
          <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6">
            <div className="bg-[#020617] border border-white/10 rounded-[2rem] p-8 max-w-sm w-full shadow-2xl">
              <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mb-5">
                <Trash2 size={22} className="text-red-500" />
              </div>
              <h4 className="text-lg font-black text-white uppercase italic tracking-tight mb-2">Excluir tarefa?</h4>
              <p className="text-sm text-slate-400 leading-relaxed mb-8">Essa ação não pode ser desfeita.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setTarefaParaExcluir(null)}
                  className="flex-1 bg-white/5 text-slate-300 py-3.5 rounded-full font-black uppercase text-xs tracking-widest hover:bg-white/10 transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => { excluirTarefa(tarefaParaExcluir); setTarefaParaExcluir(null); }}
                  className="flex-1 bg-red-500 text-white py-3.5 rounded-full font-black uppercase text-xs tracking-widest hover:bg-red-600 active:scale-95 transition-all"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* MODAL — BUSCA GLOBAL / COMMAND PALETTE (Ctrl+K) */}
        {buscaGlobalAberta && (() => {
          const termo = termoBuscaGlobal.toLowerCase().trim();

          const acoesRapidas = [
            { label: 'Novo Cliente', sublabel: 'Cadastrar empresa na carteira', icon: Plus, cor: 'text-lime-400', onSelect: () => { setActiveTab('overview'); setSelectedClient(null); setClienteEditandoId(null); setNovoCliente({ companyName: '', cnpj: '', segment: 'Varejo', niche: '', revenue: '', costs: '', debt: '', custoNaoRecorrente: '', custoDivida: '', prazoRecebimento: '', prazoPagamento: '', dividaCurtoPrazo: '', proLabore: '' }); setModalNovoCliente(true); } },
            { label: 'Carteira', sublabel: 'Ver todos os clientes', icon: LayoutDashboard, cor: 'text-blue-400', onSelect: () => { setActiveTab('overview'); setSelectedClient(null); } },
            { label: 'Comparar Empresas', sublabel: 'Comparação lado a lado', icon: Sliders, cor: 'text-blue-400', onSelect: () => { setActiveTab('overview'); setSelectedClient(null); setModoComparacao(true); } },
            { label: 'Exportar Carteira (CSV)', sublabel: 'Baixar planilha da carteira filtrada', icon: Download, cor: 'text-slate-300', onSelect: () => { const filtrados = clients.filter(c => (c.name.toLowerCase().includes(searchQuery.toLowerCase()) || c.cnpj.includes(searchQuery)) && (sectorFilter === 'Todos' || c.sector === sectorFilter) && (statusFilter === 'Todos' || c.plan === statusFilter)); exportarCarteiraCSV(filtrados); } },
            { label: 'Configurações', sublabel: 'Plano, alertas e preferências', icon: Settings, cor: 'text-slate-300', onSelect: () => { setActiveTab('settings'); setSelectedClient(null); } },
            { label: 'Marca & White-Label', sublabel: 'Configurar identidade visual', icon: Palette, cor: 'text-purple-400', onSelect: () => { setActiveTab('white-label'); setSelectedClient(null); } },
            { label: `Mudar para tema ${tema === 'light' ? 'escuro' : 'claro'}`, sublabel: 'Alternar aparência do painel', icon: tema === 'light' ? Moon : Sun, cor: 'text-slate-300', onSelect: () => setTema(t => t === 'light' ? 'dark' : 'light') },
          ];

          const clientesEncontrados = termo.length > 0
            ? clients.filter(c => c.name.toLowerCase().includes(termo) || c.cnpj.includes(termo) || c.sector.toLowerCase().includes(termo)).slice(0, 6)
            : [];
          const acoesFiltradas = termo.length > 0
            ? acoesRapidas.filter(a => a.label.toLowerCase().includes(termo))
            : acoesRapidas;

          const resultados = [
            ...clientesEncontrados.map(c => ({
              label: c.name, sublabel: `${c.sector} • ${c.plan}`, icon: Briefcase, cor: 'text-lime-400',
              onSelect: () => { setSelectedClient(c); setActiveTab('client-overview'); }
            })),
            ...acoesFiltradas
          ];

          const selecionarResultado = (item) => {
            item.onSelect();
            setBuscaGlobalAberta(false);
          };

          return (
            <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-start justify-center pt-[12vh] p-6 no-print" onClick={() => setBuscaGlobalAberta(false)}>
              <div className="bg-[#020617] border border-white/10 rounded-[1.5rem] w-full max-w-xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
                  <Search size={18} className="text-slate-500 shrink-0" />
                  <input
                    ref={inputBuscaGlobalRef}
                    type="text"
                    value={termoBuscaGlobal}
                    onChange={(e) => { setTermoBuscaGlobal(e.target.value); setIndiceSelecionado(0); }}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown') { e.preventDefault(); setIndiceSelecionado(i => Math.min(i + 1, resultados.length - 1)); }
                      if (e.key === 'ArrowUp') { e.preventDefault(); setIndiceSelecionado(i => Math.max(i - 1, 0)); }
                      if (e.key === 'Enter' && resultados[indiceSelecionado]) selecionarResultado(resultados[indiceSelecionado]);
                    }}
                    placeholder="Buscar cliente ou ação..."
                    className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-slate-600"
                  />
                  <span className="text-[11px] font-black text-slate-600 uppercase tracking-widest bg-white/5 border border-white/10 px-2 py-1 rounded">Esc</span>
                </div>

                <div className="max-h-[50vh] overflow-y-auto custom-scroll py-2">
                  {resultados.length === 0 ? (
                    <p className="text-xs text-slate-600 text-center py-8">Nenhum resultado pra "{termoBuscaGlobal}"</p>
                  ) : (
                    resultados.map((item, i) => {
                      const Icone = item.icon;
                      return (
                        <button
                          key={i}
                          onClick={() => selecionarResultado(item)}
                          onMouseEnter={() => setIndiceSelecionado(i)}
                          className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors ${i === indiceSelecionado ? 'bg-white/10' : 'hover:bg-white/5'}`}
                        >
                          <Icone size={16} className={`shrink-0 ${item.cor}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-white truncate">{item.label}</p>
                            <p className="text-[11px] text-slate-500 truncate">{item.sublabel}</p>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
                <div className="px-5 py-2.5 border-t border-white/10 flex items-center gap-4 text-[11px] text-slate-600 font-bold uppercase tracking-widest">
                  <span>↑↓ Navegar</span>
                  <span>↵ Selecionar</span>
                  <span>Esc Fechar</span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* MODAL — COMPARAÇÃO LADO A LADO ENTRE EMPRESAS */}
        {modalComparacao && (() => {
          const selecionadas = clients.filter(c => empresasParaComparar.includes(c.id));
          const linhas = [
            { label: 'Setor', get: (c) => c.sector },
            { label: 'Plano', get: (c) => c.plan },
            { label: 'Faturamento Mensal', get: (c) => `R$ ${formatCurrency(c.revenue)}`, destaqueMaior: true, valor: (c) => c.revenue },
            { label: 'EBITDA Ajustado', get: (c) => `R$ ${formatCurrency(calculateClientMetrics(c).ebitdaAjustado)}`, destaqueMaior: true, valor: (c) => calculateClientMetrics(c).ebitdaAjustado },
            { label: 'Margem Operacional', get: (c) => `${String(calculateClientMetrics(c).currentMargin).replace('.', ',')}%`, destaqueMaior: true, valor: (c) => calculateClientMetrics(c).currentMargin },
            { label: 'Alavancagem', get: (c) => `${String(calculateClientMetrics(c).alavancagem).replace('.', ',')}x`, destaqueMaior: false, valor: (c) => Number(calculateClientMetrics(c).alavancagem) || 0 },
            { label: 'Equity Value', get: (c) => `R$ ${formatCurrency(calculateClientMetrics(c).equityValue)}`, destaqueMaior: true, valor: (c) => calculateClientMetrics(c).equityValue },
            { label: 'Risco de Ruptura', get: (c) => `${calculateClientMetrics(c).ruptureRisk.toFixed(0)}%`, destaqueMaior: false, valor: (c) => calculateClientMetrics(c).ruptureRisk },
            { label: 'Status', get: (c) => calculateHealthStatus({ ebitda: calculateClientMetrics(c).ebitdaAjustado, alavancagem: calculateClientMetrics(c).alavancagem, deb: c.debt, rev: c.revenue, sector: c.sector }).label },
          ];
          return (
            <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6 no-print">
              <div className="bg-[#020617] border border-white/10 rounded-[2rem] p-8 max-w-5xl w-full shadow-2xl max-h-[90vh] overflow-y-auto custom-scroll">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0">
                      <Sliders size={20} className="text-blue-400" />
                    </div>
                    <div>
                      <h4 className="text-lg font-black text-white uppercase italic tracking-tight">Comparação Lado a Lado</h4>
                      <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest">{selecionadas.length} empresas</p>
                    </div>
                  </div>
                  <button onClick={() => setModalComparacao(false)} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white flex items-center justify-center transition-colors">
                    <X size={16} />
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <th className="text-left py-3 px-4 text-[11px] font-black text-slate-500 uppercase tracking-widest sticky left-0 bg-[#020617]">Métrica</th>
                        {selecionadas.map(c => (
                          <th key={c.id} className="text-left py-3 px-4 text-xs font-black text-white uppercase tracking-widest min-w-[180px]">{c.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {linhas.map((linha, i) => {
                        const valores = linha.valor ? selecionadas.map(c => linha.valor(c)) : null;
                        const melhor = valores ? (linha.destaqueMaior ? Math.max(...valores) : Math.min(...valores)) : null;
                        return (
                          <tr key={i} className="border-t border-white/5">
                            <td className="py-4 px-4 text-[11px] font-black text-slate-500 uppercase tracking-widest sticky left-0 bg-[#020617]">{linha.label}</td>
                            {selecionadas.map((c, idx) => {
                              const ehMelhor = valores && valores[idx] === melhor && new Set(valores).size > 1;
                              return (
                                <td key={c.id} className={`py-4 px-4 text-sm font-bold ${ehMelhor ? 'text-lime-400' : 'text-slate-300'}`}>
                                  {linha.get(c)}
                                  {ehMelhor && <span className="ml-2 text-[11px] text-lime-400/70 uppercase tracking-widest">● melhor</span>}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <button onClick={() => { setModalComparacao(false); setModoComparacao(false); setEmpresasParaComparar([]); }} className="w-full mt-8 bg-white/5 text-slate-300 py-3.5 rounded-full font-black uppercase text-xs tracking-widest hover:bg-white/10 transition-all">
                  Fechar Comparação
                </button>
              </div>
            </div>
          );
        })()}

        {/* MODAL — CADASTRAR NOVO CLIENTE */}
        {modalNovoCliente && (
          <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6 no-print">
            <div className="bg-[#020617] border border-white/10 rounded-[2rem] p-8 max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto custom-scroll">
              {salvandoNovoCliente ? (
                // 🔥 Mesmo efeito visual do "scanning" do app — círculo de progresso
                // com brilho, tranquilizando o usuário durante o salvamento real.
                <div className="flex flex-col items-center justify-center py-10 px-4">
                  <div className="relative w-40 h-40 flex items-center justify-center mb-8">
                    <div className="absolute inset-0 bg-lime-400/5 rounded-full blur-2xl animate-pulse"></div>
                    <svg className="w-full h-full transform -rotate-90 drop-shadow-[0_0_20px_rgba(190,242,100,0.3)]" viewBox="0 0 120 120">
                      <circle cx="60" cy="60" r="54" fill="none" stroke="#1e293b" strokeWidth="4" />
                      <circle cx="60" cy="60" r="54" fill="none" stroke="#bef264" strokeWidth="4"
                        strokeDasharray={2 * Math.PI * 54}
                        strokeDashoffset={2 * Math.PI * 54 - (progressoCadastro / 100) * (2 * Math.PI * 54)}
                        strokeLinecap="round" className="transition-all duration-300 ease-out" />
                    </svg>
                    <div className="absolute flex flex-col items-center justify-center">
                      <span className="text-3xl font-black text-white italic tracking-tighter">{progressoCadastro}%</span>
                    </div>
                  </div>
                  <h4 className="text-white font-black text-base uppercase italic tracking-tighter leading-none mb-2">
                    {progressoCadastro >= 100 ? (clienteEditandoId ? 'Alterações Salvas!' : 'Cliente Cadastrado!') : (clienteEditandoId ? 'Salvando Alterações...' : 'Sincronizando com o Phoenix OS...')}
                  </h4>
                  <p className="text-slate-500 text-[11px] font-black uppercase tracking-[0.3em] mb-8">
                    {progressoCadastro >= 100 ? 'Disponível no app e no dashboard' : `${novoCliente.companyName}`}
                  </p>

                  {/* 🔥 Mesmo checklist de "engines" do app — aqui é honesto porque
                    os mesmos cálculos (EBITDA, alavancagem, múltiplo, risco)
                    realmente rodam assim que o cliente é salvo no Firestore,
                    só que disparados pelo onSnapshot em vez de inline aqui. */}
                  <div className="w-full space-y-2.5">
                    {["Matrix Engine v4.2", "Solvency Analytics", "Risk Mapping AI", "Fiscal Compliance Core", "EBITDA Predictor"].map((eng, idx) => {
                      const isActive = progressoCadastro >= (idx * 20) && progressoCadastro < ((idx + 1) * 20);
                      const isDone = progressoCadastro >= ((idx + 1) * 20);
                      return (
                        <div key={idx} className={`p-3 rounded-xl border transition-all duration-500 flex items-center gap-3 ${isDone ? 'bg-lime-400/5 border-lime-400/20' : isActive ? 'bg-white/5 border-white/20 scale-[1.02]' : 'bg-transparent border-transparent opacity-20'}`}>
                          <div className="w-5 h-5 flex items-center justify-center shrink-0">
                            {isDone ? (
                              <CheckCircle2 size={16} className="text-lime-400" />
                            ) : isActive ? (
                              <div className="w-3.5 h-3.5 border-2 border-lime-400 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <div className="w-1.5 h-1.5 rounded-full bg-slate-600"></div>
                            )}
                          </div>
                          <span className={`text-[11px] font-black uppercase tracking-widest ${isDone ? 'text-lime-400' : isActive ? 'text-white' : 'text-slate-500'}`}>
                            {eng}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-lime-400/10 flex items-center justify-center shrink-0">
                      <Plus size={20} className="text-lime-400" />
                    </div>
                    <div>
                      <h4 className="text-lg font-black text-white uppercase italic tracking-tight">{clienteEditandoId ? 'Editar Cliente' : 'Novo Cliente'}</h4>
                      <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest">Sincroniza com o app automaticamente</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">Razão Social / Nome</label>
                      <input type="text" value={novoCliente.companyName} onChange={(e) => setNovoCliente({ ...novoCliente, companyName: e.target.value })} placeholder="Ex: Cliente S.A." className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-lime-400/50" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">CNPJ (opcional)</label>
                        <input type="text" value={novoCliente.cnpj} onChange={(e) => setNovoCliente({ ...novoCliente, cnpj: e.target.value })} placeholder="00.000.000/0000-00" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-lime-400/50" />
                      </div>
                      <div>
                        <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">Setor</label>
                        <select value={novoCliente.segment} onChange={(e) => setNovoCliente({ ...novoCliente, segment: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-lime-400/50 cursor-pointer">
                          {setoresDisponiveis.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">Nicho Específico <span className="text-slate-600 normal-case">(quanto mais específico, melhor a análise)</span></label>
                      <input type="text" value={novoCliente.niche} onChange={(e) => setNovoCliente({ ...novoCliente, niche: e.target.value })} placeholder="Ex: Restaurante vegano (não só 'Alimentação')" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-lime-400/50" />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">Receita Mensal</label>
                        <input type="text" value={novoCliente.revenue} onChange={(e) => setNovoCliente({ ...novoCliente, revenue: formatarEnquantoDigita(e.target.value) })} placeholder="0,00" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-lime-400/50" />
                      </div>
                      <div>
                        <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">Custos Totais</label>
                        <input type="text" value={novoCliente.costs} onChange={(e) => setNovoCliente({ ...novoCliente, costs: formatarEnquantoDigita(e.target.value) })} placeholder="0,00" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-lime-400/50" />
                      </div>
                      <div>
                        <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">Dívida</label>
                        <input type="text" value={novoCliente.debt} onChange={(e) => setNovoCliente({ ...novoCliente, debt: formatarEnquantoDigita(e.target.value) })} placeholder="0,00" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-lime-400/50" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-2 border-t border-white/5">
                      <div>
                        <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">Custo Não Recorrente</label>
                        <input type="text" value={novoCliente.custoNaoRecorrente} onChange={(e) => setNovoCliente({ ...novoCliente, custoNaoRecorrente: formatarEnquantoDigita(e.target.value) })} placeholder="0,00" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-lime-400/50" />
                        <AvisoCustoNaoRecorrente className="mt-2" custos={novoCliente.costs} naoRecorrente={novoCliente.custoNaoRecorrente} />
                      </div>
                      <div>
                        <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">Custo da Dívida (%)</label>
                        <input type="text" value={novoCliente.custoDivida} onChange={(e) => setNovoCliente({ ...novoCliente, custoDivida: e.target.value })} placeholder="0,00" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-lime-400/50" />
                      </div>
                      <div>
                        <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">Dívida de Curto Prazo</label>
                        <input type="text" value={novoCliente.dividaCurtoPrazo} onChange={(e) => setNovoCliente({ ...novoCliente, dividaCurtoPrazo: formatarEnquantoDigita(e.target.value) })} placeholder="0,00" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-lime-400/50" />
                      </div>
                      <div>
                        <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">Pró-labore</label>
                        <input type="text" value={novoCliente.proLabore} onChange={(e) => setNovoCliente({ ...novoCliente, proLabore: formatarEnquantoDigita(e.target.value) })} placeholder="0,00" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-lime-400/50" />
                      </div>
                      <div>
                        <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">Prazo de Recebimento (dias)</label>
                        <input type="number" value={novoCliente.prazoRecebimento} onChange={(e) => setNovoCliente({ ...novoCliente, prazoRecebimento: e.target.value })} placeholder="30" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-lime-400/50" />
                      </div>
                      <div>
                        <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">Prazo de Pagamento (dias)</label>
                        <input type="number" value={novoCliente.prazoPagamento} onChange={(e) => setNovoCliente({ ...novoCliente, prazoPagamento: e.target.value })} placeholder="30" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-lime-400/50" />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-8">
                    <button onClick={() => { setModalNovoCliente(false); setClienteEditandoId(null); }} className="flex-1 bg-white/5 text-slate-300 py-3.5 rounded-full font-black uppercase text-xs tracking-widest hover:bg-white/10 transition-all">
                      Cancelar
                    </button>
                    <button onClick={salvarNovoCliente} disabled={salvandoNovoCliente || (clienteEditandoId ? !novoCliente.companyName.trim() : ['companyName', 'niche', 'revenue', 'costs', 'debt', 'custoNaoRecorrente', 'custoDivida', 'prazoRecebimento', 'prazoPagamento', 'dividaCurtoPrazo', 'proLabore'].some(campo => !String(novoCliente[campo]).trim()))} className="flex-1 bg-lime-400 text-black py-3.5 rounded-full font-black uppercase text-xs tracking-widest hover:bg-lime-500 active:scale-95 transition-all disabled:opacity-50">
                      {clienteEditandoId ? 'Salvar Alterações' : 'Cadastrar Cliente'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* MOTOR INVISÍVEL DO BLOCO DE NOTAS — Tarefas + Anotações do cliente
            selecionado, no mesmo estilo visual do relatório oficial. */}
        {selectedClient && (
          <div style={{ position: 'fixed', top: 0, left: '-9999px', width: '800px', zIndex: -1 }}>
            <div ref={blocoNotasRef}>
              <div style={{
                width: '800px', minHeight: '1131px', backgroundColor: '#ffffff', boxSizing: 'border-box',
                padding: '60px 80px', fontFamily: 'Arial, sans-serif', color: '#0f172a',
                display: 'flex', flexDirection: 'column'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                  <div style={{ width: '34px', height: '34px', backgroundColor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: '16px', height: '16px', border: '2px solid #ffffff' }}></div>
                  </div>
                  <span style={{ fontSize: '18px', fontWeight: '900', letterSpacing: '-0.5px' }}>
                    PHOENIX<span style={{ color: '#65a30d' }}>OS</span>
                  </span>
                </div>
                <p style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 40px 0' }}>
                  Bloco de Notas de Trabalho
                </p>

                <div style={{ borderBottom: '2px solid #0f172a', paddingBottom: '14px', marginBottom: '24px' }}>
                  <h1 style={{ fontSize: '28px', fontWeight: '900', margin: '0 0 4px 0', textTransform: 'uppercase' }}>{selectedClient.name}</h1>
                  <p style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>
                    Gerado em {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>

                <h3 style={{ fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', margin: '0 0 14px 0', color: '#0f172a' }}>Tarefas</h3>
                {tarefas.length === 0 ? (
                  <p style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '30px' }}>Nenhuma tarefa registrada.</p>
                ) : (
                  <div style={{ marginBottom: '30px' }}>
                    {tarefas.map((t, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '9px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{
                          width: '14px', height: '14px', border: `2px solid ${t.concluida ? '#65a30d' : '#cbd5e1'}`,
                          borderRadius: '3px', backgroundColor: t.concluida ? '#65a30d' : 'transparent', flexShrink: 0, marginTop: '1px'
                        }}></div>
                        <p style={{ fontSize: '12px', margin: 0, flex: 1, color: t.concluida ? '#94a3b8' : '#0f172a', textDecoration: t.concluida ? 'line-through' : 'none' }}>
                          {t.texto}
                        </p>
                        {t.prazo && (
                          <span style={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{new Date(t.prazo).toLocaleDateString('pt-BR')}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <h3 style={{ fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', margin: '0 0 14px 0', color: '#0f172a' }}>Anotações</h3>
                {notas.length === 0 ? (
                  <p style={{ fontSize: '11px', color: '#94a3b8' }}>Nenhuma anotação registrada.</p>
                ) : (
                  <div>
                    {notas.map((n, i) => (
                      <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <p style={{ fontSize: '12px', margin: '0 0 3px 0', color: '#334155', lineHeight: '1.5' }}>{n.texto}</p>
                        {n.data && <p style={{ fontSize: '9px', color: '#94a3b8', margin: 0 }}>{new Date(n.data).toLocaleString('pt-BR')}</p>}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ marginTop: 'auto', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                  <p style={{ fontSize: '8px', color: '#94a3b8', margin: 0 }}>Phoenix OS® — Bloco de Notas (documento de trabalho, não faz parte do dossiê financeiro oficial)</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MOTOR INVISÍVEL DOS PDFs — mesmo ReportEngine.jsx do app mobile.  */}
        {/* Gera o Relatório Master e o Super Relatório 360º (com white-label) */}
        {/* usando a empresa selecionada (ou a primeira da carteira, pra a    */}
        {/* amostra do White-Label funcionar mesmo sem uma empresa aberta).   */}
        {/* ============================================================ */}
        {(() => {
          const clientePdf = selectedClient || clienteParaAmostra || clients[0];
          if (!clientePdf) return null;
          const metricsPdf = calculateClientMetrics(clientePdf);
          const dadosPdf = montarDadosParaPDF(clientePdf, metricsPdf);
          if (!dadosPdf) return null;

          // 🔥 A seção "Parecer Técnico Inicial" (e as outras 3 páginas que também
          // dependem de 'insights') usam gerarInsights — que gera os 6 blocos
          // completos usados em TODAS as páginas do relatório, não só 3. Antes eu
          // usava gerarParecer (só 3 blocos), por isso as páginas seguintes do
          // Super Relatório ficavam em branco.
          const saudePdf = calculateHealthStatus({ ebitda: metricsPdf.ebitdaAjustado, alavancagem: metricsPdf.alavancagem, deb: clientePdf.debt, rev: clientePdf.revenue, sector: clientePdf.sector });
          // 🔥 Nicho é opcional no cadastro — se não tiver, cai no setor (nunca
          // deixa o texto sair com "nicho de -", que é o placeholder cru do banco).
          const nichoValido = (clientePdf.niche && clientePdf.niche !== '-') ? clientePdf.niche : clientePdf.sector;

          const insightsPdf = parecerIAParaBlocosPdf(clientePdf.parecerIA) || gerarInsights(
            {
              status: { label: saudePdf.label },
              ebitda: metricsPdf.ebitdaAjustado,
              currentMargin: metricsPdf.currentMargin,
              alavancagem: metricsPdf.alavancagem
            },
            { niche: nichoValido, segment: clientePdf.sector, companyName: clientePdf.name, expertise: 'Avançado' },
            true // tom Consultor
          );

          // 🔥 NOVO: mesmos dados que já aparecem na tela (Diagnóstico
          // Executivo), agora também alimentando o PDF.
          const projecaoEstocasticaPdf = calculateStochasticProjection(metricsPdf, clientePdf.revenue);
          const horizontesLiberadosPdf = HORIZONTES_POR_PLANO_CONSULTOR[String(planoConsultorReal || '').toLowerCase()] || [];

          return (
            <>
              {/* Relatório Master + Super 360º COM white-label (usado só pelo botão da aba White-Label) */}
              <ReportEngine
                data={dadosPdf}
                userPlan={String(planoConsultorReal || 'advisor').toUpperCase()}
                basicRef={basicPdfRef}
                superRef={superPdfRef}
                insights={insightsPdf}
                status={saudePdf}
                wlName={brand.name}
                wlColor={brand.color}
                wlLogo={brand.logo}
                isConsultor={true}
                isRealConsultor={planoConsultorReal === 'enterprise'}
                auditId={clientePdf.id}
                projecaoEstocastica={projecaoEstocasticaPdf}
                leituraCurto={clientePdf.leituraProjecaoCurto}
                leituraMedio={clientePdf.leituraProjecaoMedio}
                leituraLongo={clientePdf.leituraProjecaoLongo}
                horizontesLiberados={horizontesLiberadosPdf}
                oportunidadesIA={clientePdf.oportunidadesIA}
              />
              {/* Information Memorandum NEUTRO (logo Phoenix, sem white-label) — usado
                pelo botão "Gerar IM" do Boardroom. Mesmo template do Super 360º, só
                que com isRealConsultor=false, o que faz o ReportEngine cair no
                branding neutro "Phoenix OS Executive" em vez da marca do Consultor. */}
              <ReportEngine
                data={dadosPdf}
                userPlan={String(planoConsultorReal || 'advisor').toUpperCase()}
                basicRef={imBasicRefNaoUsado}
                superRef={imRef}
                insights={insightsPdf}
                status={saudePdf}
                isConsultor={true}
                isRealConsultor={false}
                auditId={clientePdf.id}
                projecaoEstocastica={projecaoEstocasticaPdf}
                leituraCurto={clientePdf.leituraProjecaoCurto}
                leituraMedio={clientePdf.leituraProjecaoMedio}
                leituraLongo={clientePdf.leituraProjecaoLongo}
                horizontesLiberados={horizontesLiberadosPdf}
                oportunidadesIA={clientePdf.oportunidadesIA}
              />
            </>
          );
        })()}
      </div>
    </div>
  );
}